import type { TicketPriority } from '@/types';
import type { HomeworkSurfaceSpec } from '@/surfaces/types';

/**
 * Homework v2 · новый формат домашек · slot_homework_rework · 11 JUN 2026.
 * Пилот: S01-S02 (6 HW). Решения Genie 11 JUN: вкладка спринта + экран «Портфолио»;
 * сдача = чеклист самопроверки (гейтит кнопку) + поле артефакта (persist) + разбор
 * открывается ТОЛЬКО после сдачи.
 *
 * UI-контракт — мокап 04_DESIGN/mockup_homework_rework.html (ack Genie 11 JUN).
 * Старый homework.ts НЕ тронут (no_content_loss): UI предпочитает v2-запись по id,
 * fallback — старый prompt-рендер. Меток эмодзи в v2 нет (канон).
 * Все строки — гендерно-нейтральные (feedback_qa_studio_gender_neutral_style).
 * HTML в полях — без бэктиков; инлайн-код через <code>, кавычки «».
 */

export interface HomeworkHintV2 {
  /** «Подсказка N» */
  label: string;
  /** короткий курсивный ярлык состояния: «не вижу проблем в требовании» */
  sub: string;
  /** html-тело подсказки · НЕ выдаёт ответ, направляет */
  body: string;
}

export interface HomeworkQAV2 {
  q: string;
  a: string;
}

export interface HomeworkRazborV2 {
  /** html · эталон / опора сильного решения */
  etalon: string;
  /** типовые ошибки · по одной строке */
  mistakes: string[];
  /** бывшие self-check вопросы · теперь с разбором */
  qa: HomeworkQAV2[];
}

/**
 * Встроенная песочница Ательера в карточке домашки (Опция 1 / Вариант A · 21 JUN).
 * homework-v2 = оболочка + захват (surface), Ательер-песочница = «делание».
 * Рендерится iframe `/atelier.html?screen=&scenario=&sandbox-mode=on` внутри домашки —
 * студент находит зашитый баг прямо тут, не уходя со страницы.
 */
export interface HomeworkAtelierEmbed {
  /** ключ экрана Ательера (Screen union): cart · place-order · sign-up · profile · marketplace */
  screen: string;
  /** id сценария для баннера-подсказки (ScenarioOverlay) */
  scenarioId?: string;
  /** включить sandbox-режим (зашитые баги) — флаг `?sandbox-mode=on` */
  sandbox?: boolean;
  /** строка-подводка над панелью */
  intro?: string;
}

/**
 * Ось навыка для радара хаба «Твоя практика» (9 осей на весь курс · решение Genie 24 JUN).
 * Опциональный ОВЕРРАЙД: если не задан — ось выводится по surface/atelier/sprint/title
 * в `lib/practiceStats.ts` (deriveSkillAxis). Спек: COORDINATION/specs/practice_hub_stats_logic.md.
 * Покрытие: S02 requirements · S03-S06 test-design · S07 bug-report · S08/S11 data-sql ·
 * S09/S10 api · S12 non-functional · S14-S17 automation · S18 ai · сквозь курс exploratory.
 */
export type SkillAxis =
  | 'requirements'
  | 'test-design'
  | 'bug-report'
  | 'data-sql'
  | 'api'
  | 'non-functional'
  | 'automation'
  | 'ai'
  | 'exploratory';

export interface HomeworkV2 {
  /** совпадает с Homework.id старого файла */
  id: string;
  sprintId: string;
  code: string;
  /** канон-тайтл · при рендере заменяет старый */
  title: string;
  estimateMin: number;
  priority: TicketPriority;
  /** метка «Портфолио»: что за артефакт + что он показывает работодателю */
  portfolio?: { artefact: string; shows: string };
  /**
   * Рабочая поверхность сдачи (slot_homework_rework Ф2 · структурный инструмент).
   * Студент заполняет форму под тип задания (Story / критерии / MoSCoW / баг-репорт)
   * прямо в инструменте — те же кубики, что в Ательере. Нет поля → свободный текст.
   */
  surface?: HomeworkSurfaceSpec;
  /** встроенная песочница Ательера (Опция 1 · пилот HW-S04-02) */
  atelier?: HomeworkAtelierEmbed;
  /** ось навыка для радара хаба · оверрайд деривации (см. SkillAxis) */
  skillAxis?: SkillAxis;
  /** html · «зачем и что получится» · 1-3 предложения */
  brief: string;
  buddyAdvice: string;
  /** рамка «Дано» */
  given?: { label: string; html: string };
  /** нумерованные шаги задания · html на шаг */
  steps: string[];
  /** примечание под шагами: где делать, формат вывода */
  formatNote?: string;
  /** образец (для артефактов — билингва: en + ru) */
  example?: { head: string; html: string };
  hints: HomeworkHintV2[];
  /** чеклист самопроверки · гейтит «Сдать» */
  selfCheck: string[];
  /** подпись под полем сдачи */
  submitNote?: string;
  razbor: HomeworkRazborV2;
}

export const HOMEWORK_V2: ReadonlyArray<HomeworkV2> = [
  // ─── S01 ──────────────────────────────────────────────────────────────────
  {
    id: 'hw_s01_01',
    sprintId: 'S01',
    code: 'HW-S01-01',
    title: 'Расскажи, кто ты и зачем ты здесь',
    estimateMin: 10,
    priority: 'P3',
    brief:
      '<p>Это задание-журнал, не контрольная. Цель — зафиксировать стартовую точку: через 17 спринтов будет с чем сравнить. Никто не оценивает и не проверяет.</p>',
    buddyAdvice:
      'Пиши как есть, без красивостей. Эта запись — для тебя из будущего, не для проверки.',
    steps: [
      'Ответь в 3-5 предложениях: кто ты сейчас — занятие, бэкграунд?',
      'Что привело тебя в QA?',
      'Чего больше всего опасаешься на этом пути?',
      'Какой результат через 17 спринтов будет «успехом»? Конкретно: что изменится.',
    ],
    formatNote: 'Свободный текст прямо в поле сдачи — это первая запись твоего дневника тестировщика.',
    example: {
      head: 'Образец · как может выглядеть запись',
      html:
        '<p>«Сейчас — администратор в клинике, с техникой на «ты», кода не знаю. В QA привлекает, что можно войти без программирования и расти дальше. Опасаюсь, что не хватит усидчивости на 17 спринтов. Успех — пройти первое собеседование и не растеряться на вопросе про тест-кейсы».</p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'не пишется',
        body: 'Представь, что рассказываешь близкому человеку: что было до курса и чего хочется после. Запиши почти дословно — этого достаточно.',
      },
    ],
    selfCheck: [
      'Есть ответ на все 4 вопроса — хотя бы по предложению.',
      'Про «успех» написано конкретно: что именно изменится, а не «всё получится».',
      'Запись честная: опасения названы своими словами, не «их нет».',
    ],
    submitNote: 'Сохраняется в профиле. В конце курса вернёшься к этой записи — и увидишь пройденный путь.',
    razbor: {
      etalon:
        '<p>Сильную запись отличает конкретика: не «хочу в IT», а «хочу первую работу в тестировании и не бояться технических вопросов». Чем конкретнее стартовая точка, тем заметнее будет прогресс при перечитывании.</p>',
      mistakes: [
        '«Успех — стать тестировщиком» — слишком общо, сравнивать будет не с чем. Конкретнее: какая работа, какой первый шаг.',
        'Пропущен вопрос про опасения. Названный страх перестаёт быть фоном — с ним можно работать.',
        'Полотно на страницу. 3-5 предложений достаточно: запись должно быть легко перечитать.',
      ],
      qa: [
        {
          q: 'Зачем вообще эта запись?',
          a: 'В середине курса почти у всех бывает момент «прогресса нет». Эта запись — точка отсчёта: открой её и сравни с тем, что умеешь к тому моменту.',
        },
        {
          q: 'Можно потом переписать?',
          a: 'Не переписывай — дописывай. Старые записи ценны именно тем, что показывают путь.',
        },
      ],
    },
  },
  {
    id: 'hw_s01_02',
    sprintId: 'S01',
    code: 'HW-S01-02',
    title: 'SDLC твоего любимого приложения',
    estimateMin: 20,
    priority: 'P2',
    brief:
      '<p>Теория SDLC оживает, когда приложить её к приложению из твоего телефона. Заодно потренируешься говорить о процессах словами, которые ждут на собеседовании.</p>',
    buddyAdvice:
      'Выбирай приложение, которое правда открываешь каждый день, — про него рассуждать легче, чем про «идеальный проект» из учебника.',
    steps: [
      'Выбери приложение, которым пользуешься каждый день: мессенджер, банк, музыка — любое.',
      'Определи, какая модель разработки там, похоже, работает: Waterfall / Agile (Scrum, Kanban). По каким признакам видно: как часто выходят обновления, как быстро чинятся ошибки, появляются ли фичи маленькими порциями?',
      'Предположи роль QA в этой команде: где видны «следы» тестирования — качество новых фич, скорость исправлений?',
      'Реши: на какую командную встречу попросить доступ в первый месяц, если прийти туда junior-QA? Почему именно на неё?',
    ],
    formatNote: 'Объём — один абзац, 5-8 предложений.',
    example: {
      head: 'Образец · начало разбора (Telegram)',
      html:
        '<p>«Telegram: обновления раз в 2-4 недели, фичи появляются порциями и сначала в бета-версии — похоже на Agile с короткими циклами. Ошибки в новых фичах чинятся за день-два — значит, проверка после изменений идёт постоянно, а не раз в квартал…»</p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'не получается определить модель',
        body: 'История версий в магазине приложений — открытая летопись процесса. Большие редкие релизы — один стиль работы, частые маленькие — другой.',
      },
      {
        label: 'Подсказка 2',
        sub: 'не вижу «следов» QA',
        body: 'Следы — это не люди, а результат: что происходит с ошибками после релиза? Как быстро выходит исправление? Есть ли бета-канал, где фичи обкатываются до всех?',
      },
    ],
    selfCheck: [
      'Модель названа и подкреплена 2-3 наблюдаемыми признаками, не «мне кажется».',
      'Про роль QA — предположение со «следами», которые видны обычному пользователю.',
      'Выбрана конкретная командная встреча и объяснено, что она даст в первый месяц.',
    ],
    razbor: {
      etalon:
        '<p>Сильный ответ опирается на наблюдаемое: историю версий, скорость исправлений, бета-каналы. Слабый — на угадывание «наверное Scrum, все так делают». Точного ответа снаружи не узнать, и это нормально: ценится обоснование.</p>',
      mistakes: [
        '«У них Agile, потому что приложение хорошее» — модель определяется по процессным признакам, не по качеству продукта.',
        'Смешение модели и инструмента: Jira — не модель; Kanban-доска может жить и в Waterfall-проекте.',
        'Встреча «все сразу» — в первый месяц важнее всего одна: та, где требования встречаются с реальностью.',
      ],
      qa: [
        {
          q: 'Можно ли по открытым данным точно узнать модель?',
          a: 'Нет — и это часть задания. QA часто приходится восстанавливать процесс по косвенным признакам; тренируется именно обоснованное предположение.',
        },
        {
          q: 'А если релизы и частые, и большие?',
          a: 'Гибриды встречаются чаще чистых моделей. Назови, что от какой модели видно, — это сильнее, чем втиснуть всё в один ярлык.',
        },
      ],
    },
  },
  {
    id: 'hw_s01_03',
    sprintId: 'S01',
    code: 'HW-S01-03',
    title: 'Открой «Артель» и опиши, что это за продукт',
    estimateMin: 15,
    priority: 'P3',
    atelier: {
      screen: 'marketplace',
      sandbox: false,
      intro: 'Чистая «Артель» — просто осмотрись, тут ничего не сломается.',
    },
    skillAxis: 'exploratory',
    brief:
      '<p>Первый спринт — знакомство с продуктом, который будешь тестировать весь курс. Ошибки пока не ищем: сначала пойми, что это за магазин и кто им пользуется.</p>',
    buddyAdvice:
      'Ходи по экрану как обычный покупатель, а не как тестировщик. Понимание «что это и для кого» — фундамент всех будущих проверок.',
    steps: [
      'Открой встроенную «Артель» и осмотри витрину: что здесь продают и кто авторы работ?',
      'Открой одну карточку товара. Что на ней есть: цена, мастер, рейтинг, отзывы?',
      'Опиши в 4-6 предложениях: что это за продукт, для кого он, и какие 3-4 главные вещи тут можно сделать.',
      'Одним предложением — на какой знакомый тебе сайт это похоже.',
    ],
    formatNote: 'Свободный текст. Без терминов — своими словами, как рассказываешь другу.',
    example: {
      head: 'Образец · начало описания',
      html:
        '<p>«Артель» — маркетплейс изделий ручной работы: керамика, украшения, текстиль от разных мастеров. Покупатель ищет товар в каталоге, открывает карточку с ценой и отзывами, кладёт в корзину и оформляет заказ. Похоже на небольшой маркетплейс хендмейда.»</p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'не знаю, с чего начать',
        body: 'Пройди путь обычного покупателя: витрина → карточка товара → корзина. Что встретилось по дороге — то и опиши.',
      },
    ],
    selfCheck: [
      'Названо, что это за продукт и для кого — своими словами, без терминов.',
      'Перечислены 3-4 действия, которые тут реально можно сделать.',
      'Есть сравнение с одним знакомым сайтом.',
    ],
    submitNote: 'Это карта территории, по которой дальше будешь писать проверки. Сохраняется в профиле.',
    razbor: {
      etalon:
        '<p>Сильное описание отвечает на «что / для кого / что можно сделать» без терминов. Чем лучше знаешь продукт, тем меньше слепых зон в будущих тестах — это не формальность, а рабочая карта.</p>',
      mistakes: [
        'Пересказ каждой кнопки вместо сути. Нужна картина целиком, не инвентаризация экрана.',
        '«Обычный интернет-магазин» без деталей — а что именно продают, чем отличается от других?',
        'Взгляд сразу «как тестировщик», списком проблем. Рано: в этом задании — просто понять продукт.',
      ],
      qa: [
        {
          q: 'Зачем тестировщику описывать продукт словами?',
          a: 'Нельзя проверить то, чего не понимаешь. Описание вслух вскрывает места, где сам не разобрался, — их и стоит изучить первыми.',
        },
        {
          q: 'А если чего-то не понял на экране?',
          a: 'Запиши это как вопрос. Непонятное место для новичка часто оказывается непонятным и для пользователя, а это уже находка.',
        },
      ],
    },
  },
  {
    id: 'hw_s01_04',
    sprintId: 'S01',
    code: 'HW-S01-04',
    title: 'Первый взгляд: три вещи, которые зацепили',
    estimateMin: 15,
    priority: 'P3',
    atelier: {
      screen: 'marketplace',
      sandbox: false,
      intro: 'Осмотрись спокойно. Это не охота за багами — просто наблюдения.',
    },
    skillAxis: 'exploratory',
    brief:
      '<p>У тестировщика включён «второй взгляд»: он замечает, что удобно, а что нет. Потренируем его мягко — без формального баг-репорта, просто наблюдения.</p>',
    buddyAdvice:
      'Не ищи «поломки». Замечай, где сам на секунду задумался или засомневался, — это и есть материал.',
    steps: [
      'Пройди путь покупателя в «Артели»: найди товар, открой карточку, положи в корзину.',
      'Выпиши 3 вещи, которые зацепили внимание: удобное, непонятное или неожиданное. По одному предложению.',
      'Для каждой пометь тип: «удобно», «непонятно» или «мешает».',
      'Одно наблюдение из «непонятно/мешает» разверни: что ожидалось и что произошло вместо этого.',
    ],
    formatNote: 'Список из 3 наблюдений. Пока без строгой формы баг-репорта — она придёт в спринте про дефекты.',
    example: {
      head: 'Образец · одно наблюдение',
      html:
        '<p>«Непонятно: на карточке товара есть сердечко, но с первого взгляда неясно, что оно делает и куда попадает отмеченное. Ожидание — подсказка или раздел „избранное“; на деле переход неочевиден.»</p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'всё кажется нормальным',
        body: 'Замедлись на переходах: что происходит после клика по сердечку? Понятно ли, где растёт счётчик корзины? Мелкие «а что это?» и есть наблюдения.',
      },
    ],
    selfCheck: [
      'Три наблюдения записаны, каждое одним предложением.',
      'У каждого помечен тип: удобно / непонятно / мешает.',
      'Одно наблюдение развёрнуто через «ожидание против того, что произошло».',
    ],
    submitNote: 'Привычка замечать — главный навык тестировщика. Эти заметки — его первая тренировка.',
    razbor: {
      etalon:
        '<p>Формула «ожидалось одно — получилось другое» — зародыш будущего баг-репорта (полноценный придёт в спринте про дефекты). Сейчас цель проще: заметить и назвать. Тестировщика отличает не знание инструментов, а эта привычка замечать.</p>',
      mistakes: [
        '«Всё отлично, придраться не к чему» — приглядись: идеальных продуктов нет, а наблюдение это не обвинение.',
        'Оценка «плохо/хорошо» без факта. Что конкретно произошло на экране?',
        'Сразу вердикт «это баг». Рано называть; сейчас только наблюдаем.',
      ],
      qa: [
        {
          q: 'Чем наблюдение отличается от бага?',
          a: 'Баг — нарушение конкретного требования, оформленное по шаблону. Наблюдение — сырое «мне показалось странным». Из вторых рождаются первые, но не каждое наблюдение станет багом.',
        },
        {
          q: 'А если помеченное оказалось «так задумано» — это ошибка?',
          a: 'Нет. Даже «так задумано» бывает неудобно. Такие заметки позже превращаются в предложения по улучшению.',
        },
      ],
    },
  },
  // ─── S02 ──────────────────────────────────────────────────────────────────
  {
    id: 'hw_s02_01',
    sprintId: 'S02',
    code: 'HW-S02-01',
    surface: { kind: 'storyForm', config: { minAc: 3 } },
    title: 'Перепиши плохое требование в нормальные Story',
    estimateMin: 25,
    priority: 'P1',
    portfolio: {
      artefact: 'Документ «разбор плохого требования + 2-3 Story»',
      shows: 'умеешь читать требования глазами тестировщика',
    },
    brief:
      '<p>На груминге «Артели» PM приносит требование вроде «сделайте каталог быстрым и удобным». Умение разложить такое на проверяемые Story экономит команде дни переделок — и это навык, который проверяют на собеседовании.</p>',
    buddyAdvice:
      'Не пытайся написать идеально с первого раза. Сначала выпиши всё, что кажется мутным в требовании, — а Story переписывай уже потом.',
    given: {
      label: 'Дано · требование от PM по каталогу «Артели»',
      html: '«Каталог „Артели“ должен быстро находить нужные работы и быть удобным для покупателей».',
    },
    steps: [
      'Найди в требовании минимум <strong>4 проблемы</strong>. Каждую назови термином из тикетов спринта: двусмысленность, нет измеримости, смешаны разные требования, нет критериев приёмки.',
      'Перепиши его в <strong>2-3 User Story</strong> формата «As a [роль], I want [действие], so that [цель]». Контекст — каталог «Артели» (поиск, фильтр, сортировка работ ручной работы).',
      'Каждую Story проверь по 6 буквам <strong>INVEST</strong>: галочка + одна фраза-обоснование на букву.',
    ],
    formatNote:
      'Где делать: любой текстовый документ — Google Docs, Notion, обычный файл. Структура: «Что не так» + «Story с проверкой INVEST».',
    example: {
      head: 'Образец · как выглядит одна готовая Story',
      html:
        '<p><code>As a returning customer, I want to filter the catalog by price range, so that I can find items within my budget.</code></p>' +
        '<p><em>Как постоянный покупатель, я хочу фильтровать каталог по диапазону цены, чтобы находить товары в рамках бюджета.</em></p>' +
        '<p>+ чеклист INVEST: I — не зависит от других Story · N — детали фильтра обсуждаемы · V — покупатель быстрее находит товар · E — команда оценит за один груминг · S — одна фича, один экран · T — есть что проверить: границы диапазона, пустой результат.</p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'не вижу проблем в требовании',
        body: 'Пройди по каждому слову с вопросом «как я это проверю?». «Быстро» — это сколько? «Удобно» — для кого и в чём? Если на слово нельзя написать тест — это находка.',
      },
      {
        label: 'Подсказка 2',
        sub: 'Story получаются одинаковые',
        body: 'Разные Story — это разные ценности, не разные слова. Какие конкретно действия пользователя прячутся за «удобно»? Поиск — одна ценность, оформление заказа — другая. Каждая — своя Story.',
      },
      {
        label: 'Подсказка 3',
        sub: 'буква T не сходится',
        body: 'Если не придумывается тест — Story слишком абстрактна. Дроби её, пока не появится конкретное проверяемое поведение: что вводит пользователь, что видит в ответ.',
      },
    ],
    selfCheck: [
      'Минимум 4 проблемы найдены, каждая названа конкретным термином — не «тут что-то мутно».',
      '2-3 Story написаны строго по шаблону As a / I want / so that.',
      'Каждая Story проверена по всем 6 буквам INVEST — с обоснованием, не просто галочками.',
      'Хотя бы в одной Story есть измеримый критерий — число, время или процент.',
    ],
    submitNote:
      'Сохраняется в профиле и попадает в портфолио: на собеседовании покажешь, как раскладываешь требования.',
    razbor: {
      etalon:
        '<p>4 проблемы исходного требования: 1 — «быстро»: двусмысленность, нет измеримой границы. 2 — «удобной»: смешана бизнес-цель с UX без критериев. 3 — в одной фразе склеены два разных требования (скорость + удобство). 4 — нет критериев приёмки: невозможно сказать, когда требование выполнено.</p>',
      mistakes: [
        '«As a user, I want to use the system…» — шаблон соблюдён, смысла нет. Story всегда про конкретное действие конкретной роли.',
        'Галочки INVEST без обоснования — «I: да, N: да». Ценность упражнения именно во фразе-обосновании.',
        'Все Story зависят друг от друга («сначала поиск, потом фильтр, потом…») — проверь букву I честно.',
      ],
      qa: [
        {
          q: 'Какая из твоих Story не Independent?',
          a: 'Чаще всего — та, что начинается с «после того как». Лекарство: переформулировать так, чтобы её можно было сделать и проверить отдельно, даже если в продакшен она попадёт позже.',
        },
        {
          q: 'PM говорит «нужно вчера» — что с буквой E?',
          a: 'Оценка не исчезает от срочности. Junior-ход: предложить разрезать Story на тонкие срезы и оценить хотя бы первый — это уже Estimable.',
        },
      ],
    },
  },
  {
    id: 'hw_s02_02',
    sprintId: 'S02',
    code: 'HW-S02-02',
    surface: { kind: 'acList', config: { minItems: 6, itemLabel: 'критерий' } },
    title: 'Шесть критериев приёмки для отзыва на товар',
    estimateMin: 30,
    priority: 'P1',
    portfolio: {
      artefact: 'Таблица из 6+ AC в формате Given / When / Then со связкой «что станет тестом»',
      shows: 'владеешь общим языком команды о том, что считается готовым',
    },
    brief:
      '<p>Given / When / Then — общий язык, на котором PM, разработчики и QA одинаково понимают, что считается готовым. После шести критериев этот формат станет автоматическим.</p>',
    buddyAdvice:
      'Перед каждым AC спрашивай: из какого состояния стартуем (Given), что делает пользователь (When), что система обязана ответить (Then).',
    given: {
      label: 'Дано · User Story «Артели» · отзыв на товар (REV-01 / PDP-09)',
      html:
        '<code>As a registered customer, I want to leave a review on a product, so that I can help other buyers make a better choice.</code><br/>' +
        '<em>Как зарегистрированный покупатель, я хочу оставить отзыв на товар в «Артели», чтобы помочь другим с выбором.</em>',
    },
    steps: [
      'Напиши минимум <strong>6 AC</strong> в формате Given / When / Then.',
      'Покрой 4 типа: happy path (всё хорошо), negative (что-то пошло не так), граничное значение, защита от вредных данных. Тип укажи в скобках: AC-1 (Happy), AC-2 (Negative)…',
      'Для каждого AC — одна строка «что станет тестом»: какой тест-кейс из него вырастет.',
    ],
    formatNote: 'Формат — таблица: № · AC · Тип · Что станет тестом.',
    example: {
      head: 'Образец · один готовый AC',
      html:
        '<p><code>AC-2 (Negative): Given a guest user is on the product page, When they tap «Leave a review», Then a sign-in prompt is shown and no review is created.</code></p>' +
        '<p><em>Дано: на странице товара гость без входа. Когда нажимает «Оставить отзыв» — видит предложение войти, отзыв не создаётся.</em></p>' +
        '<p>Что станет тестом: проверка, что без входа отзыв невозможен — ни с кнопки, ни повторным нажатием.</p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'AC выходят однообразные',
        body: 'Меняй три ручки: кто (вошёл в аккаунт / гость), какие данные (обычные / пустые / на границе), что в ответ (успех / ошибка / предупреждение). Шесть одинаковых Given — признак слабого покрытия.',
      },
      {
        label: 'Подсказка 2',
        sub: 'не придумывается граничный случай',
        body: 'У любого поля есть края: длина текста отзыва, оценка в звёздах. Что происходит ровно на краю и на шаг за ним?',
      },
      {
        label: 'Подсказка 3',
        sub: 'negative и граница путаются',
        body: 'Negative: действие неправильное — система отказывает. Граница: действие правильное, но в крайней точке допустимого. Это разные проверки.',
      },
    ],
    selfCheck: [
      'Минимум 6 AC, в каждом есть все три части: Given, When, Then.',
      'Покрыты все 4 типа: happy / negative / граница / защита от вредных данных.',
      'У каждого AC заполнено «что станет тестом» — конкретный сценарий, не «проверим это».',
      'Хотя бы один AC построен на граничном числе: длина текста, оценка 0 или максимум.',
    ],
    submitNote:
      'Сохраняется в профиле и попадает в портфолио — таблица AC отлично смотрится в ответ на «а как вы работали с требованиями?».',
    razbor: {
      etalon:
        '<p>Сильный набор: 1 happy + 2 negative (гость; пустой отзыв) + 2 границы (максимальная длина текста; оценка на краю шкалы) + 1 защита (HTML-теги в тексте отзыва показываются как текст, не выполняются). Каждый AC порождает явный тест.</p>',
      mistakes: [
        'Шесть вариаций happy path разными словами — формат соблюдён, покрытие нулевое.',
        'Then без проверяемого результата: «работает корректно». Что именно видно на экране и что меняется в данных?',
        'Граница без пары: проверена точка на краю, но не шаг за краем. У края всегда две стороны.',
      ],
      qa: [
        {
          q: 'Какой AC самый опасный, если его не проверить?',
          a: 'Обычно защита данных: текст отзыва видят все покупатели, и выполняемый в нём скрипт — инцидент безопасности, а не косметика.',
        },
        {
          q: 'Какие AC легко автоматизируются, какие нет?',
          a: 'Где чёткие данные и чёткий ответ (границы, negative) — легко. Субъективное («отзыв читабельно отображается») — останется ручной проверкой, и это нормально.',
        },
      ],
    },
  },
  {
    id: 'hw_s02_03',
    sprintId: 'S02',
    code: 'HW-S02-03',
    surface: {
      kind: 'moscowBoard',
      config: {
        pool: [
          'Восстановление пароля «Забыли пароль?»',
          'Правка своего листинга продавцом',
          'История заказов в профиле',
          'Тёмная тема магазина',
          'Синхронизация избранного между устройствами',
          'Письмо продавцу о новом заказе',
          'Экспорт аналитики менеджера в CSV',
          'Выплаты продавцам через платёжку',
        ],
      },
    },
    title: 'MoSCoW: что не делать в этом спринте',
    estimateMin: 20,
    priority: 'P2',
    brief:
      '<p>Главная сложность приоритизации — не «что важно», а «что НЕ делать в этом спринте». MoSCoW даёт чёткий язык для этого разговора с PM «Артели», когда команда упирается в реальность: 4 человека, 2 недели.</p>',
    buddyAdvice:
      'Если хочется всё положить в Must — представь, что команда успеет ровно три задачи. Какие?',
    given: {
      label: 'Дано · бэклог «Артели» · команда 4 человека · спринт 2 недели',
      html:
        '<ol>' +
        '<li>Восстановление пароля: кнопка «Забыли пароль?» с письмом для сброса</li>' +
        '<li>Правка своего листинга продавцом (сейчас можно только создать)</li>' +
        '<li>История заказов покупателя в профиле (сейчас данные-заглушки)</li>' +
        '<li>Тёмная тема магазина</li>' +
        '<li>Синхронизация избранного между устройствами (сейчас список живёт на одном)</li>' +
        '<li>Письмо продавцу о новом заказе на его работу</li>' +
        '<li>Экспорт аналитики менеджера в CSV</li>' +
        '<li>Выплаты продавцам: интеграция с внешней платёжной системой</li>' +
        '</ol>',
    },
    steps: [
      'Разложи все 8 задач по категориям <strong>Must / Should / Could / Won’t</strong> (this sprint).',
      'Для каждой — одна строка обоснования: почему именно эта категория.',
      'Добавь колонку «кому больнее всего, если не сделать»: конкретная группа — клиенты, команда безопасности, маркетинг, «никто значимый».',
    ],
    formatNote: 'Формат — таблица: № · Задача · MoSCoW · Обоснование · Кому больнее.',
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'всё кажется Must',
        body: 'Прогони каждую задачу через вопрос: что случится, если этого не будет ещё месяц? Потеря денег или доверия — одна категория, «было бы приятно» — совсем другая.',
      },
      {
        label: 'Подсказка 2',
        sub: 'Should и Could не различаются',
        body: 'Should больно отложить, но есть обходной путь. Could — улучшение, без которого основной сценарий не страдает вовсе.',
      },
      {
        label: 'Подсказка 3',
        sub: 'Won’t пустует',
        body: 'Won’t — не мусор, а явное решение «не сейчас». Ищи задачу, которая тянет на отдельный проект: внешние партнёры, железо, большие интеграции.',
      },
    ],
    selfCheck: [
      'Все 8 задач разложены, без «не уверен».',
      'Распределение неравномерное — не по 2 в каждой категории.',
      'В Won’t есть хотя бы одна задача с обоснованием «почему не сейчас».',
      'Колонка «кому больнее» заполнена конкретными группами, не «всем».',
    ],
    razbor: {
      etalon:
        '<p>Опора — базовый путь покупателя и продавца. Восстановление пароля закрывает частую боль входа (кандидат в Must/Should); правка листинга и история заказов достраивают уже начатые сценарии (Should); выплаты через внешнюю платёжку — крупная интеграция, кандидат в Won’t this sprint; тёмная тема — приятная мелочь без влияния на основной сценарий (Could). Единственно правильного ответа нет — есть обоснованный.</p>',
      mistakes: [
        'Шесть Must из восьми — команда из 4 человек за 2 недели столько не закроет; это не приоритизация, а список желаний.',
        'Симметричное 2/2/2/2 — реальный бэклог так не делится; симметрия означает подгонку.',
        'Won’t прочитан как «никогда» — это «не в этом спринте», решение можно пересмотреть в следующем.',
      ],
      qa: [
        {
          q: 'Что изменится, если PM говорит «на следующей неделе показываем «Артель» инвесторам»?',
          a: 'Витринные вещи (тёмная тема, гладкая история заказов) поднимаются в приоритете, а тяжёлая интеграция выплат остаётся в Won’t. Приоритеты живут в контексте — умение их пересобрать ценится не меньше первой раскладки.',
        },
        {
          q: 'Что страдает в MoSCoW у команды без опытных людей?',
          a: 'Оценка скрытой сложности: задачи с внешними интеграциями (выплаты, письма) недооцениваются — и Must раздувается сверх реальной ёмкости спринта.',
        },
      ],
    },
  },
  {
    id: 'hw_s02_04',
    sprintId: 'S02',
    code: 'HW-S02-04',
    surface: {
      kind: 'acList',
      config: {
        minItems: 5,
        itemLabel: 'пробел',
        requirement:
          '«Авторизованный пользователь должен иметь возможность сохранять товары в избранное. При нажатии на иконку сердечка товар добавляется в список. Список доступен из профиля. Пользователь может удалить товар из списка. Список синхронизируется между устройствами».',
      },
    },
    title: 'Ревью требования: найди дыры до кода',
    estimateMin: 25,
    priority: 'P2',
    portfolio: {
      artefact: 'Документ ревью: пробелы + вопросы PM + риски, каждая находка с severity',
      shows: 'умеешь находить проблемы до того, как они стали багами',
    },
    brief:
      '<p>Тестирование начинается до запуска кода — на ревью требований. Дыра, найденная здесь, — это баг, который не дожил до production.</p>',
    buddyAdvice:
      'Читай требование как въедливый человек читает договор: каждое гладкое слово прячет вопрос.',
    given: {
      label: 'Дано · требование на ревью · избранное «Артели» (ср. с FAV-01)',
      html:
        '«Авторизованный пользователь должен иметь возможность сохранять товары в избранное. При нажатии на иконку сердечка товар добавляется в список. Список доступен из профиля. Пользователь может удалить товар из списка. Список синхронизируется между устройствами».',
    },
    steps: [
      'Найди минимум <strong>5 пробелов или двусмысленностей</strong>. Каждую — с цитатой из требования и пояснением, почему это пробел.',
      'Сформулируй минимум <strong>5 вопросов к PM</strong> — законченными вопросами («Сохраняется ли список при выходе из аккаунта?»), не «уточнить про…».',
      'Назови минимум <strong>3 риска</strong> — ситуации, которые требование не покрывает вовсе.',
      'Каждой находке поставь severity: low / medium / high. Не всё high — калибровка тоже тренируется.',
    ],
    formatNote: 'Формат — документ из 3 секций: Пробелы · Вопросы · Риски.',
    example: {
      head: 'Образец · одна готовая находка',
      html:
        '<p>«Пробел №1 (medium): „Список синхронизируется между устройствами“ — не сказано, когда: мгновенно, при следующем входе, раз в сутки? От ответа зависят и дизайн теста, и ожидания пользователя».</p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'не вижу пробелов',
        body: 'Задавай каждому предложению вопросы: когда? сколько? что, если недоступно? что увидит пользователь в этот момент? Требование молчит — значит, находка.',
      },
      {
        label: 'Подсказка 2',
        sub: 'вопросы получаются общие',
        body: 'Хороший вопрос содержит конкретную ситуацию: не «а что с лимитом?», а «что увидит пользователь при попытке добавить 1001-й товар, если лимит — 1000?».',
      },
      {
        label: 'Подсказка 3',
        sub: 'риски путаются с пробелами',
        body: 'Пробел — мутное место в написанном. Риск — сценарий, о котором текст вообще молчит: товар удалён из каталога, аккаунт заблокирован, список открыт на двух устройствах сразу.',
      },
    ],
    selfCheck: [
      'Минимум 5 пробелов, каждый с цитатой и пояснением.',
      'Минимум 5 вопросов к PM — законченные вопросы с конкретной ситуацией.',
      'Минимум 3 риска — не ошибки формулировки, а непокрытые сценарии.',
      'У каждой находки severity, и они не все одинаковые.',
    ],
    submitNote:
      'Сохраняется в профиле и попадает в портфолио: документ ревью — редкий и сильный артефакт для junior-уровня.',
    razbor: {
      etalon:
        '<p>Классика этого требования: что именно сохраняется — ID товара или снимок с ценой; где конкретно «в профиле»; как и когда работает синхронизация; есть ли лимит списка; что с товаром, удалённым из каталога; приватен ли список. На static review находки документируются — правит автор требования.</p>',
      mistakes: [
        'Переписывание требования вместо ревью — на ревью документируются дефекты документа, финальную правку делает автор.',
        '«Всё high» — калибровка severity и есть навык: опечатка и блокирующая дыра не равны.',
        'Вопросы-обвинения («почему не написали про…») — ревью работает, когда вопросы нейтральны и конкретны.',
      ],
      qa: [
        {
          q: 'Какой вопрос первым превратится в критерий приёмки?',
          a: 'Тот, на который у PM есть готовый ответ про поведение системы — обычно про сохранение или синхронизацию: ответ почти дословно ложится в Given / When / Then.',
        },
        {
          q: 'Какой риск стрельнёт через 2-3 спринта?',
          a: 'Рассинхронизация устройств: на демо она не видна и почти всегда всплывает у реальных пользователей с телефоном и планшетом.',
        },
      ],
    },
  },
  {
    id: 'hw_s02_05',
    sprintId: 'S02',
    code: 'HW-S02-05',
    surface: { kind: 'acList', config: { minItems: 5, itemLabel: 'дефект' } },
    title: 'Static testing: ревью куска спеки',
    estimateMin: 35,
    priority: 'P1',
    portfolio: {
      artefact: 'Лог дефектов ревью спеки: тип + почему проблема + как починить',
      shows: 'находишь дефекты в требованиях до строчки кода — самый дешёвый момент',
    },
    brief:
      '<p>Static testing — это поиск дефектов БЕЗ запуска: читаем требование, а не гоняем систему. Дефект, пойманный на ревью документа, в десятки раз дешевле того же дефекта в проде. Это и есть «сдвиг влево».</p>',
    buddyAdvice:
      'Не правь требование сразу. Сначала собери все находки списком — обсуждение и решения потом, на ревью.',
    given: {
      label: 'Дано · кусок спеки «Артели» «Промокод» на ревью (ср. с CART-09 / CHK-03)',
      html:
        '<p>R1. Пользователь вводит промокод и получает скидку.<br/>' +
        'R2. Скидка применяется быстро.<br/>' +
        'R3. Промокод действует до конца акции.<br/>' +
        'R4. Если промокод неверный — система реагирует.<br/>' +
        'R5. Один промокод можно применить много раз; повторное применение запрещено.</p>',
    },
    steps: [
      'Прочитай 5 требований. К каждому вопрос: однозначно? измеримо? полно? нет ли противоречия? напишется ли тест?',
      'Запиши минимум 5 дефектов. Каждый: номер требования + тип (двусмысленность / неполнота / противоречие / непроверяемость) + почему это проблема.',
      'Для каждого дефекта добавь «как починить» одной фразой.',
    ],
    hints: [
      { label: 'Подсказка 1', sub: 'выглядит нормально', body: 'Тест на проверяемость: попробуй написать проверку на требование. На «реагирует» (R4) и «быстро» (R2) теста не напишешь — это дефект.' },
      { label: 'Подсказка 2', sub: 'не вижу противоречия', body: 'Сравнивай требования между собой, не только по одному. R5 сам себе противоречит: «много раз» и «повторное запрещено» одновременно.' },
      { label: 'Подсказка 3', sub: 'тип дефекта путается', body: 'Двусмысленность — можно понять по-разному. Неполнота — чего-то не хватает (даты, числа). Противоречие — два места спорят. Непроверяемость — нельзя измерить.' },
    ],
    selfCheck: [
      'Минимум 5 дефектов, у каждого указан номер требования.',
      'У каждого дефекта назван тип, не «тут плохо».',
      'Найдено противоречие между требованиями, а не только внутри одного.',
      'К каждому дефекту есть «как починить».',
    ],
    submitNote: 'Лог ревью — классический артефакт в портфолио и частый вопрос на собесе про static testing.',
    razbor: {
      etalon:
        '<p>Сильный лог: R2 «быстро» — двусмысленность (нет порога); R3 — неполнота (даты акции не заданы); R4 «реагирует» — непроверяемость (что именно показывает?); R5 — противоречие («много раз» против «запрещено»). Часто упускают: R1 не покрывает негативный сценарий (промокод с опечаткой).</p>',
      mistakes: [
        'Найдены только «плохие слова», но не противоречие между требованиями — а оно самое дорогое в проде.',
        'Дефект без типа и без «как починить» — это жалоба, а не находка ревью.',
        'Переписали требование вместо того, чтобы зафиксировать дефект — на ревью сначала находки, решения потом.',
      ],
      qa: [
        { q: 'Почему дефект в требовании дешевле, чем в коде?', a: 'Исправить строчку в документе — минуты. Тот же дефект, доехавший до прода, тянет переделку кода, тестов и фикс у пользователей. Это сдвиг влево.' },
        { q: 'Чем static testing отличается от обычного?', a: 'Static — проверяем артефакт НЕ запуская (читаем требование, код, дизайн). Dynamic — запускаем систему. Ревью требований — static.' },
      ],
    },
  },
  {
    id: 'hw_s02_06',
    sprintId: 'S02',
    code: 'HW-S02-06',
    surface: {
      kind: 'acList',
      config: {
        minItems: 6,
        itemLabel: 'вопрос',
        requirement: 'Требование от аналитика «Артели»: «Добавить экспорт сводки в разделе Аналитика менеджера. Кнопка где-то на странице. Поддержать основные форматы.»',
      },
    },
    title: 'Вопросы аналитику к мутному требованию',
    estimateMin: 25,
    priority: 'P2',
    portfolio: {
      artefact: 'Список 6+ уточняющих вопросов к требованию, по одной дыре на вопрос',
      shows: 'не тестируешь вслепую — снимаешь неоднозначность до начала работы',
    },
    brief:
      '<p>Junior часто молча додумывает мутное требование и тестирует не то. Один хороший вопрос аналитику экономит целый спринт переделок. Хороший вопрос — закрытый и конкретный, на него отвечают за секунду.</p>',
    buddyAdvice:
      'Не «а как должно работать?», а «экспорт доступен гостю или только вошедшему?». Сведи каждый вопрос к выбору из вариантов.',
    given: {
      label: 'Дано · требование от аналитика «Артели»',
      html: '<p>«Добавить экспорт сводки в разделе Аналитика менеджера. Кнопка где-то на странице. Поддержать основные форматы.»</p>',
    },
    steps: [
      'Найди в требовании всё недосказанное: что, где, для кого, какие данные, какие края, что при ошибке.',
      'Сформулируй минимум 6 закрытых уточняющих вопросов — каждый про одну конкретную дыру.',
      'Помечай у каждого вопроса, какую проверку он разблокирует.',
    ],
    hints: [
      { label: 'Подсказка 1', sub: 'вопросы слишком общие', body: '«Как это работает?» — не вопрос. Сведи к выбору: «формат CSV, XLSX или оба?». На такой аналитик ответит сразу.' },
      { label: 'Подсказка 2', sub: 'не хватает тем', body: 'Пройди оси: доступ (кто), объём (лимит строк), формат, край (пустой отчёт), ошибка (экспорт упал), нефункц. (большой отчёт — сколько ждать).' },
      { label: 'Подсказка 3', sub: 'вопросы дублируются', body: 'Один вопрос — одна дыра. Если два закрываются одним ответом, объедини и освободи слот под новую тему.' },
    ],
    selfCheck: [
      'Минимум 6 вопросов, каждый закрытый и конкретный.',
      'Покрыто минимум 4 темы: доступ, формат, край, ошибка.',
      'Есть вопрос про пустой или очень большой отчёт.',
      'Нет двух вопросов, закрывающихся одним ответом.',
    ],
    submitNote: 'Умение задавать вопросы к требованию отличает junior от стажёра. Список идёт в портфолио.',
    razbor: {
      etalon:
        '<p>Сильный набор бьёт по осям: доступ (гость или вошедший?), формат (какие именно «основные»?), расположение кнопки (влияет на UI-проверку), объём (лимит строк?), край (экспорт пустого отчёта?), ошибка (что при сбое?), нефункц. (большой отчёт — приемлемое время?).</p>',
      mistakes: [
        'Открытые вопросы «а что если…» без вариантов — аналитик отвечает так же расплывчато.',
        'Все вопросы про happy path — а тестирование ломается как раз на краях и ошибках.',
        'Шесть вопросов про формат — нет покрытия других дыр.',
      ],
      qa: [
        { q: 'Аналитик недоступен — что делаешь?', a: 'Фиксируешь предположения письменно («тестирую исходя из: экспорт только для вошедших») и помечаешь риск. Это лучше, чем молча додумать.' },
        { q: 'Зачем помечать, какую проверку разблокирует вопрос?', a: 'Это приоритизирует вопросы: тот, что блокирует половину тестов, важнее косметического. Спрашиваешь сначала о главном.' },
      ],
    },
  },
  {
    id: 'hw_s02_07',
    sprintId: 'S02',
    code: 'HW-S02-07',
    surface: {
      kind: 'table',
      config: { tableColumns: ['Требование', 'Идея проверки', 'Приоритет'], minRows: 3 },
    },
    title: 'Мини-RTM: требование → проверки',
    estimateMin: 30,
    priority: 'P2',
    portfolio: {
      artefact: 'Мини-матрица трассировки: 3 требования → идеи проверок → приоритет',
      shows: 'связываешь каждое требование с проверкой — в покрытии не остаётся дыр',
    },
    brief:
      '<p>RTM (матрица трассировки) — карта «каждое требование чем-то проверено». Её польза для junior: сразу видно, где дыра (требование без теста) и где лишнее (тест без требования). Строится от требований, не от тестов.</p>',
    buddyAdvice:
      'Сначала все требования в столбик, потом к каждому идеи проверок. Пустая строка напротив требования — это дыра в покрытии.',
    given: {
      label: 'Дано · 3 требования «Артели» (по FSD)',
      html:
        '<p>T1. Регистрация: пароль — минимум 8 символов и хотя бы одна цифра; email по маске name@domain (AUTH-02).<br/>' +
        'T2. Сердечко-избранное на карточке: гость видит приглашение войти, покупатель переключает избранное (CAT-06 + гейт гостя).<br/>' +
        'T3. Написать отзыв на товар может только купивший покупатель; гость и не купивший — не могут (PDP-09).</p>',
    },
    steps: [
      'Выпиши 3 требования в столбик.',
      'К каждому — минимум одну идею проверки (для T2 и T3 по две: happy + край/негатив).',
      'Проставь приоритет каждой проверки (высокий/средний/низкий) и обоснуй один высокий.',
    ],
    hints: [
      { label: 'Подсказка 1', sub: 'что писать в идею проверки', body: 'Не весь тест-кейс — одна строка сути: «пароль „abc1234“ (7 симв.) → отказ», «покупатель жмёт сердце → товар в избранном».' },
      { label: 'Подсказка 2', sub: 'одной идеи на требование мало', body: 'У T2 минимум две: гость жмёт сердце → приглашение войти И покупатель жмёт → избранное меняется. Одно требование часто = несколько проверок.' },
      { label: 'Подсказка 3', sub: 'все приоритеты одинаковые', body: 'Приоритет = риск × частота. Валидация регистрации — ворота в продукт, высокий. Тип избранного — важно, но не блокирует. Разнеси.' },
    ],
    selfCheck: [
      'Все 3 требования в матрице, ни одного без проверки.',
      'У T2 и T3 минимум по 2 идеи проверки (happy + край/негатив).',
      'У каждой проверки проставлен приоритет.',
      'Один высокий приоритет обоснован риском.',
    ],
    submitNote: 'RTM показывают на собесе как доказательство системного покрытия. Идёт в портфолио.',
    razbor: {
      etalon:
        '<p>Сильная матрица: T1 → пароль верный/короткий/без цифры, email по маске/без @ (высокий: ворота в продукт). T2 → гость жмёт сердце и получает приглашение войти, покупатель переключает избранное туда-обратно (средний). T3 → купивший видит кнопку отзыва, гость и не купивший — заблокированы (высокий: данные видят все покупатели). Видно: ни одно требование не осталось без проверки.</p>',
      mistakes: [
        'Требование без строки проверки — это и есть дыра в покрытии, ради которой RTM нужна.',
        'Проверки есть, но приоритеты не проставлены — матрица не помогает решить, с чего начать.',
        'Только happy path у T2 — гейт гостя без проверки роняет половину смысла требования.',
      ],
      qa: [
        { q: 'Зачем RTM, если есть просто список тест-кейсов?', a: 'Список не показывает, что осталось НЕ покрытым. RTM связывает требования и тесты в обе стороны: видно дыры (требование без теста) и лишнее (тест без требования).' },
        { q: 'Требование поменялось — что с матрицей?', a: 'Идёшь по строке этого требования и обновляешь связанные проверки. В этом ценность трассировки: видно, какие тесты задеть.' },
      ],
    },
  },
  {
    id: 'hw_s02_08',
    sprintId: 'S02',
    code: 'HW-S02-08',
    surface: { kind: 'acList', config: { minItems: 5, itemLabel: 'расхождение' } },
    atelier: {
      screen: 'sign-up',
      sandbox: true,
      intro: 'Живая форма регистрации «Артели» в режиме тренажёра. Сверь её поведение со спекой ниже (мини-выжимка по FSD AUTH) — где реальность расходится с требованиями.',
    },
    title: 'Сверь спеку с поведением живой формы',
    estimateMin: 35,
    priority: 'P2',
    portfolio: {
      artefact: 'Лог расхождений «спека vs поведение» по форме регистрации',
      shows: 'проверяешь, что построили то, что требовали (verification), а не веришь на слово',
    },
    brief:
      '<p>Требования живут, только если поведение им соответствует. Здесь проверяешь форму против спеки руками — это verification, мост между «требование» и «тест». Junior часто читает спеку и верит, что код её исполняет; ты — проверяешь.</p>',
    buddyAdvice:
      'Не «работает / не работает», а «требование R3 сказало X, форма делает Y». Каждое расхождение — со ссылкой на пункт спеки.',
    given: {
      label: 'Дано · мини-спека формы регистрации «Артели» (по FSD AUTH-01…AUTH-05)',
      html:
        '<p>R1. Пароль — минимум 8 символов.<br/>' +
        'R2. Email — формат name@domain.tld.<br/>' +
        'R3. Пустые обязательные поля отклоняются с понятной ошибкой.<br/>' +
        'R4. Ошибка валидации называет конкретную причину (не «что-то пошло не так»).<br/>' +
        'R5. Модалку можно закрыть с клавиатуры (Esc).</p>',
    },
    steps: [
      'Прочитай мини-спеку (5 требований к форме).',
      'Прогони форму в песочнице руками по каждому требованию: ведёт ли она себя так, как написано?',
      'Зафиксируй минимум 5 расхождений: требование · что должно быть · что реально происходит.',
      'Для каждого помечай: это <strong>баг</strong> (код нарушает спеку) или <strong>дыра в спеке</strong> (поведение странное, но спека молчит)?',
    ],
    hints: [
      { label: 'Подсказка 1', sub: 'где искать', body: 'Пробуй крайние и невалидные данные: короткий пароль («a»), email без @ («abc»), пустые поля. Спека говорит «отклонить» — а форма?' },
      { label: 'Подсказка 2', sub: 'баг или дыра спеки', body: 'Спека явно сказала, а форма нарушает → баг (на разработчика). Форма делает странное, но спека про это молчит → дыра в требованиях (на аналитика).' },
      { label: 'Подсказка 3', sub: 'не выдумывай', body: 'Только реально воспроизведённые расхождения. По требованию всё сошлось — так и отметь «соответствует», не натягивай.' },
    ],
    selfCheck: [
      'Каждое требование спеки проверено на живой форме.',
      'Минимум 5 расхождений: требование · ожидание · реальность.',
      'Для каждого помечено: баг или дыра в спеке.',
      'Расхождения воспроизводимы, не выдуманы.',
    ],
    submitNote: 'Лог «спека vs поведение» — наглядный артефакт verification и частый рабочий навык (приёмка фичи).',
    razbor: {
      etalon:
        '<p>Сильный лог опирается на пункты спеки: «R1: пароль ≥8 → форма приняла "a" (1 символ) = баг валидации»; «R2: email формата → "abc" прошло = баг»; «R4: ошибка вида "что-то пошло не так" вместо конкретной = баг UX». Плюс находит дыры спеки (про спецсимволы в имени спека молчит — вопрос аналитику). Verification = «то ли построили, что требовали».</p>',
      mistakes: [
        '«Форма глючит» без привязки к требованию — непонятно, что именно нарушено.',
        'Не отличил баг от дыры спеки — это разные адресаты (разработчик vs аналитик).',
        'Проверил только happy-path — расхождения живут на невалидных данных.',
      ],
      qa: [
        { q: 'Чем verification отличается от validation тут?', a: 'Verification — «форма соответствует спеке» (то, что ты делаешь). Validation — «спека вообще решает задачу пользователя». Можно идеально соответствовать плохой спеке.' },
        { q: 'Нашёл расхождение, но спека молчит — чья проблема?', a: 'Аналитика: дыра в требованиях. Заводишь не баг на разработчика, а вопрос на уточнение — иначе «починят» наугад.' },
      ],
    },
  },
  {
    id: 'hw_s02_09',
    sprintId: 'S02',
    code: 'HW-S02-09',
    surface: {
      kind: 'table',
      config: { tableColumns: ['Требование', 'Ф / НФ', 'Обоснование', 'Вид тестирования'], minRows: 8 },
    },
    skillAxis: 'requirements',
    title: 'Функциональные и нефункциональные: разбери требования «Артели»',
    estimateMin: 25,
    priority: 'P2',
    portfolio: {
      artefact: 'Классификация 8 требований «Артели»: функц./нефункц. + вид проверки',
      shows: 'различаешь, ЧТО система делает и НАСКОЛЬКО хорошо она это делает',
    },
    brief:
      '<p>Требования бывают функциональные (что система делает) и нефункциональные (насколько хорошо: скорость, удобство, доступность). Путать их — классическая ошибка новичка: проверяют только кнопки и забывают про скорость и вёрстку на телефоне.</p>',
    buddyAdvice:
      'Функциональное проверяется ответом «да/нет: сделала или нет». Нефункциональное — вопросом «насколько»: быстро, удобно, на всех ли устройствах.',
    given: {
      label: 'Дано · 8 требований «Артели» вперемешку (по FSD + NFR)',
      html:
        '<ol>' +
        '<li>Гость не может оставить отзыв без входа (PDP-09).</li>' +
        '<li>Каталог открывается за 2 секунды даже на 500 работах.</li>' +
        '<li>Кнопка «+ в корзину» добавляет товар и увеличивает счётчик (CAT-05).</li>' +
        '<li>Магазин корректно выглядит на телефоне 360px и планшете 768px.</li>' +
        '<li>Менеджер видит очередь модерации, покупатель — нет (MOD-01 / ACL-01).</li>' +
        '<li>Модалку регистрации можно закрыть с клавиатуры (Esc).</li>' +
        '<li>Поиск фильтрует работы по названию и мастеру (CAT-08).</li>' +
        '<li>Сердечко-избранное реагирует на клик мгновенно, без задержки.</li>' +
        '</ol>',
    },
    steps: [
      'Для каждого требования определи: <strong>функциональное</strong> (что система делает) или <strong>нефункциональное</strong> (насколько хорошо она это делает).',
      'Одной фразой обоснуй выбор.',
      'Назови вид тестирования, который его проверяет: функциональное · производительность · юзабилити · совместимость · доступность.',
    ],
    formatNote: 'Формат — таблица: № · Требование · Ф/НФ · Обоснование · Вид тестирования.',
    example: {
      head: 'Образец · один разбор',
      html:
        '<p>«№2 „Каталог открывается за 2 секунды на 500 работах“ — нефункциональное: описывает не что система делает, а насколько быстро. Проверяет тестирование производительности».</p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'всё кажется функциональным',
        body: 'Спроси: это про действие или про качество действия? «Добавляет в корзину» — действие (Ф). «Добавляет быстро» — качество (НФ).',
      },
      {
        label: 'Подсказка 2',
        sub: 'не знаю вид тестирования',
        body: 'Скорость → производительность. Удобство → юзабилити. Разные экраны → совместимость. Клавиатура и скринридер → доступность.',
      },
    ],
    selfCheck: [
      'Все 8 требований разобраны, у каждого метка Ф или НФ.',
      'У каждого одно обоснование, не просто метка.',
      'Каждому назван вид тестирования.',
      'Среди нефункциональных названо минимум 3 разных вида.',
    ],
    submitNote:
      'Классификация требований идёт в портфолио: показывает, что видишь не только кнопки, но и качество продукта.',
    razbor: {
      etalon:
        '<p>Функциональные (что делает): 1, 3, 5, 7. Нефункциональные (насколько хорошо): 2 — производительность, 4 — совместимость, 6 — доступность, 8 — отзывчивость (производительность интерфейса). Ошибка новичка — проверить только функциональные и упустить, что каталог тормозит или магазин ломается на телефоне.</p>',
      mistakes: [
        '«Всё функциональное, ведь это фичи» — нефункциональное тоже про фичу, но про её качество, не про факт наличия.',
        'Метка без обоснования — навык именно в том, чтобы объяснить, почему.',
        'Один вид тестирования на все нефункциональные — скорость, вёрстка и клавиатура проверяются по-разному.',
      ],
      qa: [
        {
          q: 'Почему новичок чаще пропускает нефункциональные требования?',
          a: 'Их не видно в happy-path клике: кнопка сработала — галочка стоит. А что каталог грузится 8 секунд или ломается на телефоне, замечаешь, только если специально проверяешь.',
        },
        {
          q: 'Требование «удобное избранное» — это Ф или НФ?',
          a: 'Нефункциональное (юзабилити): «удобное» про качество. Но за ним прячется функциональное «избранное добавляет и убирает», которое стоит выделить отдельным требованием.',
        },
      ],
    },
  },
  // ─── S03 · виды тестирования · риск-приоритизация · exploratory ─────────────
  {
    id: 'hw_s03_01',
    sprintId: 'S03',
    code: 'HW-S03-01',
    surface: { kind: 'freeText', config: { placeholder: 'Ссылка на карту (Miro / Figma / FigJam) или фото + пара слов, что где.' } },
    title: 'Карта типов тестирования',
    estimateMin: 45,
    priority: 'P1',
    portfolio: {
      artefact: 'Mind map типов тестирования: Functional / Non-functional (ISO 25010) / Change-related с примерами',
      shows: 'держишь всю область тестирования в голове целиком, не теряешься в типах',
    },
    brief:
      '<p>На собесе junior часто получает пустую коробку «Test Types» и просьбу разложить. Эта карта — навигатор по всей области, к которому будешь возвращаться весь первый год работы.</p>',
    buddyAdvice:
      'Не зубри список — рисуй ветками. Что увидел глазами один раз, на собесе вспомнишь быстрее, чем прочитанное десять раз.',
    given: {
      label: 'Дано · три главные ветки',
      html:
        '<p><strong>Functional</strong> · <strong>Non-functional</strong> · <strong>Change-related</strong>. Под Non-functional — 8 категорий ISO/IEC 25010: Performance Efficiency, Compatibility, Usability, Reliability, Security, Maintainability, Portability, Functional Suitability.</p>',
    },
    steps: [
      'Возьми любой инструмент: Miro / Figma / FigJam / бумага + фото. Корневой узел — «Test Types».',
      'Разверни три ветки: <strong>Functional</strong>, <strong>Non-functional</strong>, <strong>Change-related</strong> (Confirmation / Regression / Maintenance).',
      'Под Non-functional разложи минимум 6 из 8 категорий ISO 25010. К каждому узлу — один пример из «Артели» (можно добрать из другого приложения, но хотя бы половина — про «Артель»).',
      'Risk-Based вынеси отдельной плашкой в углу — это подход, а не тип.',
    ],
    formatNote: 'Сдай ссылку на карту или фото. В поле ниже — ссылка плюс пара слов, что где.',
    example: {
      head: 'Образец · один узел (по «Артели»)',
      html: '<p>Non-functional → Security → «покупатель видит только свои заказы, чужой id заказ не открывает» (ORD-05 «Артели»).</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'Functional ветка вышла крупнее', body: 'Это нормально — функциональных проверок объективно больше. Не подгоняй ветки под симметрию.' },
      { label: 'Подсказка 2', sub: 'путаю типы и уровни', body: 'Уровни (Component / System / Acceptance) — отдельная ось, в эту карту не кладём. Здесь только типы: что проверяем, а не на каком слое.' },
      { label: 'Подсказка 3', sub: 'Usability или Accessibility — куда', body: 'Оба в Non-functional. Usability — удобно ли; Accessibility — доступно ли людям с ограничениями. Разные стандарты: Nielsen heuristics против WCAG.' },
    ],
    selfCheck: [
      'Покрыто минимум 10 типов и категорий.',
      'У каждой ветки есть хотя бы один пример приложения или теста.',
      'Risk-Based вынесен отдельно как подход, не как тип.',
      'Ссылка или фото карты приложены.',
    ],
    submitNote: 'Карта ляжет в портфолио — на собесе откроешь её как «вот как я вижу область».',
    razbor: {
      etalon:
        '<p>Сильная карта читается за 10 секунд: три ветки, под Non-functional — 6+ категорий ISO с примерами, Risk-Based отдельной плашкой. Главное не полнота списка, а структура: видно, что ты держишь область целиком.</p>',
      mistakes: [
        'Свалены типы и уровни в одну кучу — System / Acceptance это не типы.',
        'Non-functional ветка из двух узлов — значит, ISO 25010 не раскрыта.',
        'Risk-Based положен как тип рядом с Functional — это подход к приоритизации, у него другая природа.',
      ],
      qa: [
        { q: 'Где Usability и Accessibility — functional или non-functional?', a: 'Обе non-functional. Functional отвечает «делает ли система то, что должна»; Usability и Accessibility — «насколько удобно и доступно». Это про качество, не про функцию.' },
        { q: 'Какой тип критичнее всего для мобильного банка — top-3?', a: 'Security (деньги), Reliability (не падать в момент перевода), Performance (отклик). Functional подразумевается, но доверие к банку решают именно non-functional.' },
      ],
    },
  },
  {
    id: 'hw_s03_02',
    sprintId: 'S03',
    code: 'HW-S03-02',
    surface: {
      kind: 'moscowBoard',
      config: {
        columns: [
          { key: 'critical', label: 'CRITICAL' },
          { key: 'important', label: 'IMPORTANT' },
          { key: 'moderate', label: 'MODERATE' },
          { key: 'low', label: 'LOW' },
        ],
        pool: [
          'Оплата картой при оформлении (CHK-02)',
          'Роле-гейт разделов менеджера (ACL-01)',
          'Гейт отзыва: пишет только купивший (PDP-09)',
          'Сортировка каталога по цене (CAT-08)',
          'Сердечко-избранное на карточке (CAT-06)',
          'Плитки категорий на главной (CAT-11)',
          'Текст в футере магазина',
          'Пагинация каталога 10/25/50 (CAT-10)',
        ],
      },
    },
    atelier: {
      screen: 'marketplace',
      sandbox: true,
      intro: '«Артель» в режиме тренажёра (зашиты баги). Сначала пройди фичи из списка руками и отметь, где реально странно (медленно, валидация дырявая, поведение неожиданное) — это калибрует Likelihood, а не «мне кажется».',
    },
    title: 'Risk-Based приоритизация бэклога',
    estimateMin: 30,
    priority: 'P2',
    portfolio: {
      artefact: 'Risk-раскладка бэклога: 8 фич по категориям CRITICAL / IMPORTANT / MODERATE / LOW',
      shows: 'обосновываешь приоритеты тестирования цифрами, а не интуицией',
    },
    brief:
      '<p>Никто не успевает протестировать всё. Risk-Based превращает «не успеть всё» в «успеть главное». После этого ты обосновываешь приоритеты цифрами, а не «мне кажется».</p>',
    buddyAdvice:
      'Если всё уехало в CRITICAL — риски переоценены. Если всё в LOW — недооценены. Реальный бэклог всегда неравномерный.',
    given: {
      label: 'Дано · бэклог «Артели» на спринт · 8 фич',
      html:
        '<p>Распредели восемь фич «Артели» справа по категориям риска. Категория = <strong>Likelihood</strong> (насколько вероятен баг: новизна, сложность, частота изменений) × <strong>Impact</strong> (насколько больно: деньги, число юзеров, доступ к чужим данным).</p>',
    },
    steps: [
      'Пройди фичи из списка в песочнице руками — где поведение странное, там Likelihood выше.',
      'Для каждой фичи прикинь Likelihood (1-5) и Impact (1-5) — вслух или на бумаге.',
      'Разложи 8 фич по категориям: <strong>CRITICAL / IMPORTANT / MODERATE / LOW</strong> (нажми на букву у задачи).',
      'Для каждой CRITICAL-фичи держи в голове стратегию: минимум 3 типа теста (functional + non-functional + ещё один).',
    ],
    example: {
      head: 'Образец · одна фича с обоснованием',
      html: '<p>«Оплата картой при оформлении (CHK-02) — Likelihood 4 (валидация карты, легко ошибиться), Impact 5 (деньги). Итог: CRITICAL».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'оплата картой — куда', body: 'Валидация номера/срока карты плюс касается денег → высокий Likelihood и высокий Impact. Это CRITICAL.' },
      { label: 'Подсказка 2', sub: 'футер и плитки категорий', body: 'Статичный контент, меняется редко, ничего не ломает → низкий Likelihood и Impact. LOW, хватит smoke.' },
      { label: 'Подсказка 3', sub: 'роле-гейт недооценён', body: 'Если гейт разделов менеджера дырявый — покупатель откроет чужой раздел. Impact высокий (доступ к чужому), даже если Likelihood средний. Не клади в LOW.' },
    ],
    selfCheck: [
      'Каждую фичу прикинул по Likelihood (1-5) и Impact (1-5).',
      'Распределение неравномерное — не по две в каждой категории.',
      'У CRITICAL-фич в голове есть стратегия: минимум 3 типа теста.',
      'Понятно, что повышает Likelihood и что повышает Impact для каждой.',
    ],
    submitNote: 'Раскладка сохранится в портфолио как пример риск-мышления.',
    razbor: {
      etalon:
        '<p>Опора — деньги, доступ к чужим данным, частота изменений. Оплата картой и роле-гейт менеджера уходят в CRITICAL; гейт отзыва и сортировка/пагинация каталога — IMPORTANT / MODERATE; футер и плитки категорий — LOW. Единственно правильного ответа нет — есть обоснованный.</p>',
      mistakes: [
        'Шесть фич в CRITICAL — за спринт столько глубоко не покрыть; это не приоритизация, а список желаний.',
        'Футер положен в IMPORTANT — статичный текст не несёт такого риска.',
        'Likelihood и Impact перепутаны: «редкий, но катастрофичный» баг — это низкий Likelihood и высокий Impact, а не наоборот.',
      ],
      qa: [
        { q: 'У какой фичи самый высокий риск для доверия к «Артели»?', a: 'Роле-гейт разделов: если покупатель откроет модерацию или чужие заказы — это утечка доступа, а не косметика. Деньги и доступ решают доверие к маркетплейсу.' },
        { q: 'Есть 3 дня на спринт — в каком порядке закроешь?', a: 'Сначала CRITICAL (оплата, роле-гейт), затем IMPORTANT по убыванию Impact. LOW (футер, плитки) — smoke в последний день или в общий регрессионный прогон.' },
      ],
    },
  },
  {
    id: 'hw_s03_03',
    sprintId: 'S03',
    code: 'HW-S03-03',
    surface: { kind: 'freeText', config: { placeholder: 'Charter + Session Report: можно текстом или ссылкой на документ.' } },
    atelier: {
      screen: 'marketplace',
      sandbox: true,
      intro: 'Площадка для сессии — «Артель» в режиме тренажёра (зашиты баги). Ходи каталог → корзина → профиль по своему charter.',
    },
    title: 'Exploratory-сессия по charter',
    estimateMin: 30,
    priority: 'P1',
    portfolio: {
      artefact: 'Charter (SBTM) + Session Report: coverage, находки, открытые вопросы, тайминг',
      shows: 'ведёшь структурированную исследовательскую сессию, а не кликаешь наугад',
    },
    brief:
      '<p>Exploratory без charter — полчаса случайных кликов. С charter — структурный поиск багов, до которых автотесты не дойдут. Расширяем миссию из спринта на несколько страниц.</p>',
    buddyAdvice:
      'Charter пишется ДО сессии, не после. Полстраницы плана экономят час метаний по сайту.',
    given: {
      label: 'Дано · площадка (встроенный магазин)',
      html:
        '<p>Встроенный магазин ниже (sandbox-режим). Сессия на нескольких связанных страницах: каталог → корзина → профиль, 45-60 минут.</p>',
    },
    steps: [
      'Запиши Charter в формате SBTM: Mission (1 предложение) · Areas (минимум 3 связанные страницы) · Duration (45-60 мин) · Focus (например cross-page state) · Out of scope.',
      'Проведи сессию по charter, засеки таймер.',
      'Найди минимум 3 наблюдения, которые видны ТОЛЬКО при переходах между страницами (на одной странице их бы не нашёл).',
      'Примени хотя бы одну эвристику SFDIPOT (Time / State / Operations).',
      'Собери Session Report: Coverage · Находки · Открытые вопросы · Разбивка по времени.',
    ],
    formatNote: 'В поле ниже — весь документ: charter плюс report. Можно ссылкой.',
    example: {
      head: 'Образец · строка charter + одна находка',
      html:
        '<p><strong>Charter:</strong> Mission — проверить перенос состояния между каталогом, корзиной и профилем «Артели». Areas — каталог, корзина, профиль. Duration — 45 мин. Focus — cross-page state. Out of scope — оплата.</p>' +
        '<p><strong>Находка:</strong> добавил товар в корзину гостем → зарегистрировался → корзина опустела (ожидание: позиции переносятся). Cross-page, на одной странице не видно.</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'где искать cross-page', body: 'Что в URL при переходах? localStorage переживает logout/login? Корзина анонима переносится при регистрации? Фильтры каталога сохраняются при возврате с товара?' },
      { label: 'Подсказка 2', sub: 'сессия раздувается', body: '45-60 минут, не 6 часов. Нашёл много — фиксируй и иди по charter, не сворачивай в боковую ветку.' },
      { label: 'Подсказка 3', sub: 'не понимаю SFDIPOT-State', body: 'State — что произойдёт, если состояние меняется во время перехода: добавил в корзину → разлогинился → вернулся. Это и есть «состояние во времени».' },
    ],
    selfCheck: [
      'Charter записан ДО старта (минимум 5 строк).',
      'Охвачено минимум 3 страницы в одной сессии.',
      'Минимум 3 cross-page наблюдения (баги, подтверждённое поведение или вопросы PM — выдумывать баги не надо).',
      'Применена минимум одна эвристика SFDIPOT.',
      'Session Report оформлен: Coverage / Находки / Вопросы / Время.',
    ],
    razbor: {
      etalon:
        '<p>Сильная сессия отличается от слабой именно charter: видно, ЧТО покрывал и зачем. Cross-page находки — главная ценность: «корзина анонима потерялась при регистрации» автотест на одной странице не поймает. Не «придумай баги», а честный отчёт о том, что увидел.</p>',
      mistakes: [
        'Charter дописан после сессии — тогда это не charter, а пересказ.',
        'Все находки на одной странице; cross-page так и не проверен.',
        'Bug / Session-баланс не отмечен — непонятно, насколько сессия была результативной.',
      ],
      qa: [
        { q: 'Какое из наблюдений ты бы не нашёл, тестируя одну страницу?', a: 'Любое про перенос состояния между страницами: корзина при логине, фильтры при возврате, URL-параметры. Изолированный тест страницы их не видит — нужен переход.' },
        { q: 'Что поменял бы в Charter после сессии?', a: 'Обычно сузил бы Focus: «cross-page state» широко, к концу понимаешь, что интереснее всего корзина плюс аккаунт. Charter живой, его уточняют после первого прогона.' },
      ],
    },
  },
  {
    id: 'hw_s03_04',
    sprintId: 'S03',
    code: 'HW-S03-04',
    surface: { kind: 'freeText', config: { placeholder: 'Сравнительная таблица или короткий документ — текстом или ссылкой.' } },
    title: 'Сравни два non-functional типа',
    estimateMin: 45,
    priority: 'P3',
    portfolio: {
      artefact: 'Сравнение двух non-functional типов по 4 осям: что / инструменты / критерий / когда',
      shows: 'выходишь за functional — видишь, как проверяют скорость, безопасность, удобство',
    },
    brief:
      '<p>Большинство junior застревает на functional. Это мост к «работает быстро / безопасно / удобно». После него сможешь обсудить с senior, какой non-functional тип критичен для релиза.</p>',
    buddyAdvice:
      'Бери два РАЗНЫХ типа из разных категорий ISO (Performance против Security), не два подтипа одного и того же.',
    given: {
      label: 'Дано · 4 оси сравнения',
      html:
        '<p>1) что именно проверяем · 2) какими инструментами (минимум 2 на тип) · 3) критерий «прошёл / провалил» (конкретная метрика) · 4) когда в SDLC и на каком уровне.</p>',
    },
    steps: [
      'Выбери 2 типа non-functional из ISO 25010 (например Performance против Security, или Usability против Accessibility).',
      'Заполни 4 оси для каждого: что проверяем · инструменты (минимум 2) · pass/fail-критерий с цифрой · когда и на каком уровне.',
      'Сформулируй вывод: какой из двух junior чаще пропускает и почему.',
    ],
    formatNote: 'Формат — таблица или короткий документ. В поле ниже — текст или ссылка.',
    example: {
      head: 'Образец · одна ось для Performance (по «Артели»)',
      html: '<p>«Performance → что: время открытия каталога «Артели» на 500 работах · инструменты: Lighthouse, k6 · критерий: LCP < 2.5с на 4G · когда: в CI на каждом релизе, уровень System».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'Performance против Load — это одно?', body: 'Load — подтип Performance Efficiency, не отдельная категория. Возьми Performance против Security — они из разных категорий ISO, сравнение богаче.' },
      { label: 'Подсказка 2', sub: 'Usability и Accessibility смешиваются', body: 'Разные стандарты: Usability — Nielsen heuristics, Accessibility — WCAG 2.1. Разные инструменты: UserTesting против axe DevTools.' },
      { label: 'Подсказка 3', sub: 'не знаю инструментов', body: 'Performance: Lighthouse, k6 / JMeter. Security: OWASP ZAP, Burp. Accessibility: axe, WAVE. Подсмотри в материалах Sprint 12 для интуиции.' },
    ],
    selfCheck: [
      'Выбраны два non-functional типа (не functional плюс non-functional).',
      'На каждый тип названо минимум 2 конкретных инструмента.',
      'Pass/fail-критерий измеримый (цифра или булева проверка), не «нормально работает».',
      'Указан уровень тестирования для каждого типа.',
    ],
    razbor: {
      etalon:
        '<p>Сильное сравнение конкретно: не «Performance про скорость», а «LCP меньше 2.5с на 4G, проверяем Lighthouse в CI на каждом релизе». Измеримый критерий отличает инженера от «вроде работает быстро».</p>',
      mistakes: [
        'Сравнили functional с non-functional — задача про ДВА non-functional.',
        'Критерий «работает нормально» — это не критерий; нужна цифра или чёткое да/нет.',
        'Инструменты названы общо («какой-нибудь профайлер») — нужны конкретные, которые гуглятся.',
      ],
      qa: [
        { q: 'Какой из двух типов сложнее автоматизировать?', a: 'Обычно Usability / Accessibility-часть, где нужен живой человек или экспертная оценка; Performance и часть Security автоматизируются метриками и сканерами проще.' },
        { q: 'Какой junior чаще пропускает на собесе и в работе?', a: 'Security и Accessibility: про Performance хотя бы слышали, а «проверь, что чужой токен не открывает чужой профиль» или «работает ли с клавиатуры без мыши» вспоминают редко.' },
      ],
    },
  },
  {
    id: 'hw_s03_05',
    sprintId: 'S03',
    code: 'HW-S03-05',
    surface: {
      kind: 'acList',
      config: {
        minItems: 5,
        itemLabel: 'проверка',
        requirement:
          'Фикс в «Артели»: «при оплате картой с истёкшим сроком заказ всё равно создавался». Теперь форма оплаты (CHK-02) отклоняет истёкшие карты. Фикс затронул валидацию формы оплаты.',
      },
    },
    atelier: {
      screen: 'place-order',
      sandbox: false,
      intro: 'Оформление заказа «Артели» (шаг «Оплата», CHK-02). Посмотри форму вживую, чтобы понять, что рядом мог задеть фикс валидации карты.',
    },
    title: 'Change-related: спланируй регрессию после фикса',
    estimateMin: 35,
    priority: 'P2',
    portfolio: {
      artefact: 'План проверок после фикса: confirmation + scope регрессии с обоснованием',
      shows: 'оцениваешь, что мог задеть фикс, а не проверяешь только сам баг',
    },
    brief:
      '<p>Change-related тестирование — про «что ещё мог сломать этот фикс». Confirmation проверяет, что баг ушёл; регрессия — что рядом ничего не отвалилось. Junior часто проверяет только сам фикс и пропускает побочку.</p>',
    buddyAdvice:
      'Фикс редко живёт в вакууме. Спроси: что использует тот же код, ту же таблицу, тот же экран?',
    given: {
      label: 'Дано · фикс на ревью (оформление «Артели», CHK-02)',
      html:
        '<p>В «Артели» починили баг: «при оплате картой с истёкшим сроком заказ всё равно создавался». Теперь форма оплаты отклоняет истёкшие карты. Фикс затронул валидацию шага «Оплата» (CHK-02).</p>',
    },
    steps: [
      '<strong>Confirmation</strong>: запиши проверку, что исходный баг ушёл (истёкшая карта отклоняется).',
      '<strong>Регрессия</strong>: добавь минимум 5 проверок рядом — что мог задеть фикс валидации оплаты (соседние поля CHK-02: номер карты, CVV, промокод CHK-03; переход к шагу «Проверка»).',
      'Для каждой проверки — почему она в scope: что у неё общего с фиксом (код / экран / данные).',
    ],
    example: {
      head: 'Образец · одна регрессионная проверка',
      html: '<p>«Валидная карта (срок в будущем) всё ещё проходит и создаёт заказ — scope: тот же валидатор шага „Оплата“, фикс легко ломает валидные карты (happy-path)».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'регрессия = весь сайт?', body: 'Нет. Фокус на том, что делит код / экран / данные с фиксом: форма оплаты, другие карты, валидация других полей.' },
      { label: 'Подсказка 2', sub: 'забыл happy-path', body: 'Валидную карту фикс ломать не должен. Проверка «обычная карта всё ещё проходит» обязательна — её чаще всего забывают.' },
      { label: 'Подсказка 3', sub: 'что ещё трогает оплату', body: 'Промокод (CHK-03), повтор оплаты после ошибки, возврат назад к шагу «Адрес» (CHK-06), переход к «Проверке». Любое могло зацепиться за валидацию.' },
    ],
    selfCheck: [
      'Есть проверка confirmation: исходный баг ушёл.',
      'Минимум 5 регрессионных проверок рядом с фиксом.',
      'Каждая проверка обоснована: что общего с фиксом.',
      'Включена проверка happy-path (валидная карта всё ещё проходит).',
    ],
    submitNote: 'Scope регрессии — частый вопрос на собесе. Этот список — готовый пример в портфолио.',
    razbor: {
      etalon:
        '<p>Сильный scope узкий и обоснованный: валидная карта проходит, истёкшая отклоняется (confirmation), плюс соседи — другие способы оплаты, сохранённые карты, повтор после ошибки. Не «перепроверь весь сайт», а «что делит код с фиксом».</p>',
      mistakes: [
        'Регрессия = весь сайт — это не приоритизация, на это нет времени.',
        'Проверили только сам баг, happy-path забыт — а фикс валидации легко ломает валидные карты.',
        'Проверки без обоснования — непонятно, почему именно они в scope.',
      ],
      qa: [
        { q: 'Почему happy-path важнее части «соседей»?', a: 'Фикс валидации напрямую меняет логику приёма карт — выше всего шанс сломать именно валидную оплату, а это деньги. Соседи вторичны.' },
        { q: 'Где грань между confirmation и регрессией?', a: 'Confirmation — что ИСХОДНЫЙ баг ушёл. Регрессия — что РЯДОМ ничего не отвалилось. Разные цели, обе нужны.' },
      ],
    },
  },
  {
    id: 'hw_s03_06',
    sprintId: 'S03',
    code: 'HW-S03-06',
    surface: {
      kind: 'moscowBoard',
      config: {
        columns: [
          { key: 'component', label: 'Component' },
          { key: 'system', label: 'System' },
          { key: 'acceptance', label: 'Acceptance' },
        ],
        pool: [
          'Функция расчёта скидки промокода возвращает верное число (CHK-03)',
          'Оформление заказа «Артели» целиком: корзина → оплата → подтверждение',
          'Бизнес-приёмка: «избранное закрывает потребность вернуться к работе»',
          'Модуль валидации email формы регистрации отдельно (AUTH-02)',
          'Создание заказа пишет запись в стор и очищает корзину (ORD-01)',
          'Демо заказчику: сценарий покупки из User Story проходит',
        ],
      },
    },
    title: 'Уровни тестирования: разложи проверки',
    estimateMin: 30,
    priority: 'P3',
    portfolio: {
      artefact: 'Раскладка 6 проверок по уровням Component / System / Acceptance',
      shows: 'различаешь уровни тестирования и не путаешь их с типами',
    },
    brief:
      '<p>Уровни — это «на каком слое проверяем», отдельная ось от типов. Component — кусок изолированно; System — собранная система целиком; Acceptance — глазами заказчика, «то ли вообще построили». Junior часто путает уровни с типами.</p>',
    buddyAdvice:
      'Спроси про каждую проверку: «один кусок в изоляции, вся система, или взгляд заказчика?» — это и есть уровень.',
    given: {
      label: 'Дано · 6 проверок',
      html: '<p>Разложи шесть проверок справа по трём уровням. Для спорных решай по вопросу «изоляция / вся система / взгляд заказчика».</p>',
    },
    steps: [
      'Разложи 6 проверок по уровням: <strong>Component / System / Acceptance</strong> (нажми букву у проверки).',
      'Для спорных опирайся на вопрос: изолированный кусок, вся система целиком, или взгляд заказчика?',
    ],
    example: {
      head: 'Образец · одна проверка с уровнем',
      html: '<p>«Функция расчёта скидки промокода возвращает верное число (CHK-03) — Component: один кусок логики в изоляции, без остального оформления заказа».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'Component или System', body: 'Component — один кусок БЕЗ остальной системы (функция, модуль). System — всё собрано вместе. «Расчёт скидки в изоляции» — Component; «оформление заказа целиком» — System.' },
      { label: 'Подсказка 2', sub: 'System или Acceptance', body: 'System — «работает технически». Acceptance — «решает задачу заказчика». Демо по User Story и бизнес-приёмка — Acceptance.' },
      { label: 'Подсказка 3', sub: 'интеграция — это уровень?', body: 'Integration — между Component и System (как куски стыкуются). Здесь упрощаем: «заказ пишет в БД и шлёт письмо» ближе к System (несколько частей вместе).' },
    ],
    selfCheck: [
      'Все 6 проверок разложены.',
      'Component — только изолированные куски (функция, модуль).',
      'Acceptance — взгляд заказчика, не «технически работает».',
    ],
    razbor: {
      etalon:
        '<p>Component: расчёт скидки, валидация email. System: оформление заказа целиком, заказ → БД → письмо. Acceptance: бизнес-приёмка, демо по User Story. Ключ — изоляция (Component) против собранности (System) против взгляда заказчика (Acceptance).</p>',
      mistakes: [
        'Уровни перепутаны с типами — «функциональный» это тип, а не уровень.',
        'Всё свалено в System — тогда теряется смысл уровней.',
        'Acceptance прочитан как «тщательно протестировали» — нет, это «то ли вообще построили», взгляд заказчика.',
      ],
      qa: [
        { q: 'Чем уровень отличается от типа тестирования?', a: 'Тип — ЧТО проверяем (функция, скорость, безопасность). Уровень — НА КАКОМ слое (изолированный кусок / вся система / приёмка). Две независимые оси.' },
        { q: 'Где обычно дешевле всего найти баг?', a: 'На Component — чем раньше и изолированнее, тем дешевле фикс (принцип Early Testing). На Acceptance баг самый дорогой.' },
      ],
    },
  },
  // ─── S04 · тестовая документация · TC · Bug Report · Use Case · Test Plan ────
  {
    id: 'hw_s04_01',
    sprintId: 'S04',
    code: 'HW-S04-01',
    surface: { kind: 'testCase' },
    atelier: {
      screen: 'cart',
      sandbox: true,
      intro: 'Корзина с промокодом в sandbox-режиме. Сначала прогони код руками (валидный/невалидный/пустой) и зафиксируй фактическое поведение — кейсы пиши по факту и отметь, где оно расходится со спекой (там баг).',
    },
    title: 'Напиши тест-кейс по структуре IEEE 829',
    estimateMin: 45,
    priority: 'P1',
    portfolio: {
      artefact: 'Тест-кейс по структуре IEEE 829: предусловие, атомарные шаги, ожидаемый результат',
      shows: 'пишешь тест-кейс, который выполнит другой человек без вопросов',
    },
    brief:
      '<p>Тест-кейс — самый частый артефакт junior QA. Хороший кейс выполняется кем угодно без догадок: чёткое предусловие, атомарные шаги, проверяемый ожидаемый результат. Этот навык проверяют почти на каждом собесе.</p>',
    buddyAdvice:
      'Пиши так, будто кейс выполнит человек, который видит фичу впервые. Каждый шаг — одно действие. «Проверить корзину» — не шаг; «Нажать "Добавить в корзину"» — шаг.',
    given: {
      label: 'Дано · фича «Артели» · промокод в корзине (CART-09)',
      html: '<p>Промокод в корзине «Артели»: пользователь вводит код в поле «Промокод» и нажимает «Применить». Валидный код <code>ARTEL10</code> даёт скидку 10%, невалидный — показывает ошибку. Прогони это в песочнице ниже.</p>',
    },
    steps: [
      'Напиши <strong>один</strong> тест-кейс на позитивный сценарий (валидный промокод <code>ARTEL10</code>). Поля: ID · Заголовок · Предусловие · Шаги · Ожидаемый результат · Постусловие.',
      'Шаги сделай <strong>атомарными</strong> — одно действие на шаг, с конкретными данными (какой код вводим).',
      'Ожидаемый результат — проверяемый: не «скидка работает», а «итог уменьшился на 10%, видна строка "Скидка −10%"».',
    ],
    formatNote:
      'Любой документ или таблица. Если знаешь Gherkin (Given/When/Then) — можешь показать тот же кейс вторым стилем, но пошаговый обязателен.',
    example: {
      head: 'Образец · готовый тест-кейс',
      html:
        '<p><strong>TC-01 · Промокод ARTEL10 даёт скидку 10%</strong><br/>' +
        'Предусловие: в корзине «Артели» товар на 1000 ₽, покупатель вошёл.<br/>' +
        'Шаги: 1. Открыть корзину. 2. В поле «Промокод» ввести <code>ARTEL10</code>. 3. Нажать «Применить».<br/>' +
        'Ожидаемый результат: итог = 900 ₽, видна строка «Скидка −10%».<br/>' +
        'Постусловие: код применён, повторно тот же код не применяется.</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'что писать в предусловие', body: 'Предусловие — состояние ДО первого шага: «в корзине товар на 1000 ₽, пользователь вошёл, есть валидный код ARTEL10». Без него кейс начинается из ниоткуда.' },
      { label: 'Подсказка 2', sub: 'шаги слишком крупные', body: 'Если в шаге есть «и» — это два шага. «Открыть корзину и ввести код» → «1. Открыть корзину. 2. Ввести код ARTEL10 в поле "Промокод".»' },
      { label: 'Подсказка 3', sub: 'ожидаемый результат размытый', body: 'Спроси: как я докажу, что прошло? Конкретное число, текст на экране, изменение суммы. «Корректно отображается» — не результат.' },
    ],
    selfCheck: [
      'Есть все поля: ID, заголовок, предусловие, шаги, ожидаемый результат, постусловие.',
      'Каждый шаг атомарный — одно действие, с конкретными данными.',
      'Ожидаемый результат проверяемый (число / текст / изменение состояния).',
      'Кейс сможет выполнить человек, не видевший фичу.',
    ],
    submitNote: 'Тест-кейс — базовый артефакт портфолио. На собесе часто просят написать один прямо на месте.',
    razbor: {
      etalon:
        '<p>Сильный кейс самодостаточен: по предусловию понятно стартовое состояние, шаги атомарны и с данными (код ARTEL10, товар на 1000 ₽), ожидаемый результат измерим (итог 900 ₽, строка «Скидка −10%»). Постусловие фиксирует, что осталось после (код применён, повторно не применяется).</p>',
      mistakes: [
        'Шаги-«главы»: «Проверить промокод» — внутри спрятано пять действий, исполнитель догадывается сам.',
        'Нет конкретных данных: «ввести код» — какой именно? Кейс невоспроизводим.',
        'Ожидаемый результат = пересказ шага («код применился»), а не наблюдаемое следствие (сумма уменьшилась).',
      ],
      qa: [
        { q: 'Зачем постусловие, если есть ожидаемый результат?', a: 'Ожидаемый результат — про момент проверки. Постусловие — про состояние системы после (данные сохранились, сессия осталась). Важно для следующих кейсов и очистки.' },
        { q: 'Пошагово или Gherkin — что выбрать?', a: 'Пошагово — для ручных кейсов и новичков в команде, нагляднее. Gherkin (Given/When/Then) — когда кейсы ведут к автоматизации. S04 учит обоим; начинай с пошагового.' },
      ],
    },
  },
  {
    id: 'hw_s04_02',
    sprintId: 'S04',
    code: 'HW-S04-02',
    surface: { kind: 'bugReport' },
    atelier: {
      screen: 'cart',
      scenarioId: 'B1-FOUND-03',
      sandbox: true,
      intro: 'Песочница магазина в sandbox-режиме: в корзину зашиты баги. Найди хотя бы один и оформи на него баг-репорт в форме сдачи ниже.',
    },
    title: 'Оформи баг-репорт по находке в песочнице',
    estimateMin: 35,
    priority: 'P1',
    portfolio: {
      artefact: 'Баг-репорт: заголовок, severity, окружение, шаги, ожидаемо/фактически',
      shows: 'оформляешь баг так, что разработчик воспроизводит его без переписки',
    },
    brief:
      '<p>Баг-репорт — лицо тестировщика. Плохой репорт («не работает корзина») возвращается с вопросами и теряет дни. Хороший воспроизводится с первого раза: точные шаги, ожидаемо vs фактически, верная severity. Это задание про второе.</p>',
    buddyAdvice:
      'Severity — это про влияние на систему, не про твоё раздражение. Упал платёж — Blocker. Опечатка в футере — Trivial. Калибруй по последствию для пользователя.',
    given: {
      label: 'Дано · корзина «Артели» в режиме тренажёра',
      html: '<p>В песочнице «Артели» (ниже) — корзина с зашитыми багами. Поиграй: поменяй количество (попробуй отрицательное), очисти корзину, удали товар крестиком, сверь подытог и итог. Найди минимум один баг.</p>',
    },
    steps: [
      'Поработай в песочнице: меняй количество, очищай корзину, удаляй товары — ищи, где поведение неверное.',
      'Выбери один баг и оформи на него баг-репорт в форме сдачи ниже: заголовок, severity, окружение, шаги, ожидаемо, фактически.',
      'Заголовок — формула «что + где + при каком условии». Шаги — пронумерованы и атомарны, чтобы баг повторили точь-в-точь.',
    ],
    example: {
      head: 'Образец · баг-репорт по находке',
      html:
        '<p><strong>Заголовок:</strong> Корзина «Артели» принимает отрицательное количество — подытог уходит в минус.<br/>' +
        '<strong>Окружение:</strong> «Артель», режим тренажёра, корзина.<br/>' +
        '<strong>Шаги:</strong> 1. Открыть корзину. 2. В поле количества ввести −5. 3. Уйти из поля.<br/>' +
        '<strong>Ожидаемо:</strong> значение меньше 1 отклоняется, количество откатывается к 1.<br/>' +
        '<strong>Фактически:</strong> количество = −5, подытог отрицательный.<br/>' +
        '<strong>Severity:</strong> Major (битые данные доходят до оформления, система жива).</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'не вижу бага', body: 'Считай руками и проверяй края. Введи в количество «-2» — что стало с подытогом и итогом? Очисти корзину — нормальный ли пустой экран? Удали товар крестиком — список обновился?' },
      { label: 'Подсказка 2', sub: 'какая severity', body: 'Severity по последствию. Отрицательный итог или списание не той суммы — Major/Critical (деньги). Кривой пустой экран — Major (UX-тупик). Косметика — Minor/Trivial.' },
      { label: 'Подсказка 3', sub: 'ожидаемо/фактически сливаются', body: 'Ожидаемо — как по логике должно быть («количество меньше 1 не принимается, итог не уходит в минус»). Фактически — что видишь. Они должны явно противоречить.' },
    ],
    selfCheck: [
      'Баг найден в песочнице и воспроизводится по шагам.',
      'Заголовок узнаваем: что + где + при каком условии.',
      'Шаги пронумерованы, атомарны, воспроизводимы.',
      'Ожидаемо и фактически явно противоречат; severity по последствию, а не по эмоции.',
    ],
    submitNote: 'Баг-репорт — обязательный артефакт портфолио. Severity-калибровку часто спрашивают на собесе.',
    razbor: {
      etalon:
        '<p>Сильный репорт воспроизводится с первого раза. Пример по зашитому багу: «Корзина принимает отрицательное количество — подытог уходит в минус». Шаги: 1. Открыть корзину. 2. В поле количества ввести −5. 3. Уйти из поля. Ожидаемо: значение меньше 1 отклоняется, количество откатывается к 1. Фактически: количество = −5, подытог отрицательный. Severity: Major (битые данные доходят до оформления, но система жива). Окружение указано.</p>',
      mistakes: [
        'Заголовок-эмоция «всё сломано» — в трекере из ста таких ни один не найти; нужен «что + где + при каком условии».',
        'Severity по раздражению, а не по влиянию: отрицательный итог (деньги) метят Minor, а косметику — Critical.',
        'Шаги без данных и нумерации — баг не повторят и вернут «не воспроизводится».',
      ],
      qa: [
        { q: 'Severity и Priority — это одно и то же?', a: 'Нет. Severity — насколько серьёзно для системы (техническая тяжесть). Priority — насколько срочно чинить (бизнес). Тихая скидка −19% в итоге: severity Critical (деньги), priority высокий.' },
        { q: 'Нашёл несколько багов — что делать?', a: 'Каждый баг — отдельный репорт: одна находка = один тикет. Не сваливай «корзина глючит» в один — разработчик чинит и закрывает по одному.' },
      ],
    },
  },
  {
    id: 'hw_s04_03',
    sprintId: 'S04',
    code: 'HW-S04-03',
    surface: { kind: 'acList', config: { minItems: 6, itemLabel: 'сценарий' } },
    atelier: {
      screen: 'sign-up',
      sandbox: true,
      intro: 'Форма регистрации/входа в sandbox-режиме. Сначала поломай её руками (пустой пароль, неверный, спецсимволы) — сценарии пиши по фактическим реакциям и отметь, где валидация дырявая.',
    },
    title: 'Шесть тест-сценариев для фичи',
    estimateMin: 30,
    priority: 'P2',
    portfolio: {
      artefact: 'Набор из 6+ высокоуровневых тест-сценариев с покрытием happy / negative / край',
      shows: 'видишь фичу сценариями целиком, ещё до детальных кейсов',
    },
    brief:
      '<p>Тест-сценарий — высокоуровневое «что проверяем», без пошаговых деталей. Из одного сценария потом вырастает один-несколько тест-кейсов. Умение быстро накидать сценарии показывает охват мышления — первый шаг тест-дизайна.</p>',
    buddyAdvice:
      'Сценарий — одна строка про ЧТО, не про КАК. «Оформление заказа с валидным промокодом» — сценарий. Пошаговые действия оставь для тест-кейса.',
    given: {
      label: 'Дано · фича «Артели» · регистрация и вход (AUTH-01…AUTH-05)',
      html: '<p>Форма «Артели» ниже: регистрация (email, username, пароль, согласие с условиями), валидация (email по маске, пароль ≥8 и цифра), тогл «Уже есть аккаунт? Войти», вход существующего аккаунта. Прогони её руками.</p>',
    },
    steps: [
      'Напиши минимум <strong>6 тест-сценариев</strong> — по одной строке, высокоуровнево (что проверяем, не как).',
      'Покрой типы: happy path, негативные, граничные, безопасность. Тип помечай в скобках.',
      'Сценарии не должны дублироваться — каждый про отдельную проверяемую ситуацию.',
    ],
    example: {
      head: 'Образец · один сценарий',
      html: '<p>«Регистрация с паролем без цифры (7 символов) отклоняется с понятной ошибкой (negative)».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'сценарий или кейс?', body: 'Если в строке появились конкретные шаги и данные — это уже кейс. Сценарий: «Вход с неверным паролем». Кейс: «1. Ввести email. 2. Ввести неверный пароль…».' },
      { label: 'Подсказка 2', sub: 'мало сценариев', body: 'Пройди фичу по частям: регистрация, валидация каждого поля, согласие с условиями, тогл на вход, вход существующего. У каждого свой happy и свой провал.' },
      { label: 'Подсказка 3', sub: 'все про happy path', body: 'На каждый «как надо» придумай «как ломают»: пароль без цифры, email без @, пустое обязательное поле, вход с неверным паролем, спецсимволы или HTML в username.' },
    ],
    selfCheck: [
      'Минимум 6 сценариев, каждый — высокоуровневая строка (что, не как).',
      'Покрыты happy, негативные, граничные, безопасность.',
      'Нет двух сценариев про одну и ту же ситуацию.',
      'Ни один сценарий не скатился в пошаговый тест-кейс.',
    ],
    submitNote: 'Набор сценариев показывает охват мышления — сильный артефакт в портфолио и на собесе.',
    razbor: {
      etalon:
        '<p>Сильный набор покрывает фичу по осям: регистрация с валидными данными (happy), пароль без цифры или короче 8 (negative), email без @ (negative), пустое обязательное поле (граница), тогл «Войти» и вход существующего аккаунта (happy), HTML/спецсимволы в username (безопасность). Каждый — отдельная ситуация, ни одного дубля.</p>',
      mistakes: [
        'Сценарии скатились в пошаговые кейсы — теряется смысл уровня «что, не как».',
        'Шесть вариантов happy path — покрытие узкое, негатив и края не тронуты.',
        'Дубли разными словами: «вход с ошибкой» и «неверный логин» — одна ситуация.',
      ],
      qa: [
        { q: 'Сколько кейсов рождает один сценарий?', a: 'От одного до нескольких. Сценарий «вход с неверным паролем» даёт кейсы: пустой пароль, неверный пароль, пароль с пробелами. Сценарий — зонтик над кейсами.' },
        { q: 'Зачем сценарии, если можно сразу кейсы?', a: 'Сценарии быстрые — за 10 минут виден весь охват, легко заметить дыру. Детальные кейсы дороги; сначала договариваются о сценариях, потом расписывают.' },
      ],
    },
  },
  {
    id: 'hw_s04_04',
    sprintId: 'S04',
    code: 'HW-S04-04',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Актор · Цель · Предусловие · Основной флоу · Альтернативные · Исключения.' },
    },
    atelier: {
      screen: 'place-order',
      sandbox: true,
      intro: 'Оформление заказа в sandbox-режиме. Сначала пройди флоу руками, поймай альтернативы, исключения и баги — Use Case описывай по фактическому поведению, а не по догадке.',
    },
    title: 'Опиши Use Case с альтернативными флоу',
    estimateMin: 40,
    priority: 'P2',
    portfolio: {
      artefact: 'Use Case: актор, основной флоу, альтернативы и исключения',
      shows: 'описываешь поведение системы со всеми ветками, не только happy path',
    },
    brief:
      '<p>Use Case описывает взаимодействие актора с системой ради цели — с основным флоу и всеми ответвлениями. В отличие от User Story («что и зачем»), Use Case показывает «как именно проходит сценарий», включая что идёт не так. Альтернативы и исключения — там, где живут баги.</p>',
    buddyAdvice:
      'Сначала пропиши основной флоу как идеальную прямую. Потом на каждом шаге спроси «а что, если нет?» — так находятся альтернативы и исключения.',
    given: {
      label: 'Дано · цель актора (оформление «Артели», CHK-01…CHK-05)',
      html: '<p>Покупатель «Артели» оформляет заказ: адрес → оплата картой → проверка → подтверждение. Опиши это как Use Case. Пройди флоу в песочнице ниже, чтобы поймать реальные ветки.</p>',
    },
    steps: [
      'Заполни шапку: актор, цель, предусловие, постусловие успеха.',
      'Опиши <strong>основной флоу</strong> — пронумерованные шаги «актор → система» до достижения цели.',
      'Добавь минимум <strong>2 альтернативных флоу</strong> (другой валидный путь: вернуться на шаг «Адрес», применить промокод) и <strong>2 исключения</strong> (карта с истёкшим сроком, пустые обязательные поля).',
    ],
    example: {
      head: 'Образец · шапка + ветка',
      html:
        '<p><strong>Актор:</strong> покупатель. <strong>Цель:</strong> оплатить и оформить заказ. <strong>Предусловие:</strong> в корзине есть товар, покупатель вошёл.</p>' +
        '<p><strong>Исключение 2a:</strong> на шаге «Оплата» введена карта с истёкшим сроком → система отклоняет, заказ не создаётся (ведёт к неуспеху).</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'путаю альтернативу и исключение', body: 'Альтернатива — другой УСПЕШНЫЙ путь (оплата сохранённой картой). Исключение — путь к НЕуспеху (карта отклонена). Оба ответвляются от шага основного флоу.' },
      { label: 'Подсказка 2', sub: 'основной флоу разбухает', body: 'В основной флоу — только идеальный путь, без «если». Все ветвления выноси в альтернативы и исключения, ссылаясь на номер шага: «3a. Если карта отклонена…».' },
      { label: 'Подсказка 3', sub: 'мало исключений', body: 'Пройди по шагам и сломай каждый: нет связи, карта без денег, неверный CVV, таймаут банка, пользователь закрыл вкладку. Любое — кандидат в исключение.' },
    ],
    selfCheck: [
      'Есть шапка: актор, цель, предусловие, постусловие.',
      'Основной флоу — только успешный путь, пронумерован.',
      'Минимум 2 альтернативы и 2 исключения, привязанные к шагам.',
      'Исключения ведут к неуспеху, альтернативы — к успеху другим путём.',
    ],
    submitNote: 'Use Case с проработанными ветками — сильный артефакт: показывает, что ты думаешь не только про happy path.',
    razbor: {
      etalon:
        '<p>Сильный Use Case: основной флоу — прямая «ввёл адрес → ввёл данные карты → проверил сводку → подтвердил → заказ ORD-XXXXXX создан, корзина очищена». Альтернативы: вернуться на шаг «Адрес» и поправить данные (CHK-06); применить промокод на шаге оплаты (CHK-03). Исключения: карта с истёкшим сроком отклонена (2a), пустой индекс не проходит шаг «Адрес» (1a). Каждая ветка привязана к номеру шага и ведёт к понятному исходу.</p>',
      mistakes: [
        'Основной флоу с «если» внутри — нечитаемо; ветвления место в альтернативах и исключениях.',
        'Только happy path: ни одного исключения — а именно там самые дорогие баги (потеря денег).',
        'Альтернативы и исключения перепутаны: «карта отклонена» помечено как альтернатива (это путь к неуспеху → исключение).',
      ],
      qa: [
        { q: 'Чем Use Case отличается от User Story?', a: 'User Story — короткая «кто / что / зачем» для планирования. Use Case — детальный сценарий взаимодействия со всеми ветками. Story отвечает «что строим», Use Case — «как это проходит».' },
        { q: 'Use Case или тест-сценарии — что раньше?', a: 'Часто Use Case раньше: из его флоу и веток напрямую вырастают тест-сценарии (каждое исключение — минимум один негативный сценарий). Удобная цепочка.' },
      ],
    },
  },
  {
    id: 'hw_s04_05',
    sprintId: 'S04',
    code: 'HW-S04-05',
    surface: {
      kind: 'freeText',
      config: { minChars: 300, placeholder: 'Объект · Scope · Out of scope · Подход · Риски · Критерии входа/выхода.' },
    },
    atelier: {
      screen: 'marketplace',
      sandbox: false,
      intro: '«Артель» с живым избранным (сердечко на карточке, счётчик в шапке, раздел «Избранное»). Погоняй фичу, чтобы план опирался на реальное поведение, а не на догадку.',
    },
    title: 'Мини Test Plan на фичу',
    estimateMin: 45,
    priority: 'P2',
    portfolio: {
      artefact: 'Облегчённый Test Plan: scope, подход, риски, критерии входа/выхода',
      shows: 'планируешь тестирование фичи, а не просто гоняешь кейсы наугад',
    },
    brief:
      '<p>Test Plan отвечает на «что, как и до каких пор мы тестируем». Полный план по IEEE 829 — большой документ, но junior должен уметь набросать облегчённую версию: что в scope, чего НЕ трогаем, подход, риски, когда останавливаемся. Это превращает хаотичный тык в план.</p>',
    buddyAdvice:
      'Самая ценная секция — out of scope. Явно написать, чего ты НЕ тестируешь, важнее списка того, что тестируешь: снимает споры потом.',
    given: {
      label: 'Дано · фича «Артели» · избранное (FAV-01 / CAT-06)',
      html: '<p>В «Артели» есть избранное: сердечко на карточке товара, счётчик в шапке, раздел «Избранное» со списком; гость не может добавлять (приглашение войти). Составь мини Test Plan на эту фичу.</p>',
    },
    steps: [
      'Напиши облегчённый план из секций: <strong>объект · scope · out of scope · подход · риски · критерии входа и выхода</strong>.',
      'В scope и out of scope — конкретику: например, «корзина — out of scope, тестировалась в S03».',
      'Минимум <strong>3 риска</strong> и по одному критерию входа и выхода (когда начинаем / когда считаем готовым).',
    ],
    example: {
      head: 'Образец · две секции плана',
      html:
        '<p><strong>Scope:</strong> сердечко на карточке (добавить/убрать), счётчик в шапке, раздел «Избранное», гейт гостя. <strong>Out of scope:</strong> корзина и оплата (проверялись в S03-S04).</p>' +
        '<p><strong>Риск:</strong> избранное завязано на роль (гость против покупателя) — без входа поведение отличается, нужен тестовый аккаунт.</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'что писать в подход', body: 'Подход — КАК тестируем: ручное или авто, какие техники (границы, негатив), на каких браузерах, с какими данными. Пара предложений, не диссертация.' },
      { label: 'Подсказка 2', sub: 'не вижу рисков', body: 'Риск — что может сорвать тестирование или где вероятны баги: «избранное завязано на роль — у гостя и покупателя поведение разное», «нет тестовых аккаунтов», «сроки сжаты, успеем только happy path».' },
      { label: 'Подсказка 3', sub: 'критерии входа/выхода размыты', body: 'Вход — что должно быть готово, чтобы начать («фича на тест-стенде, есть доступ»). Выход — когда стоп («все P1-кейсы прошли, открытых Blocker/Critical нет»).' },
    ],
    selfCheck: [
      'Есть все секции: объект, scope, out of scope, подход, риски, критерии входа/выхода.',
      'Out of scope заполнен конкретно (что именно не трогаем и почему).',
      'Минимум 3 риска, привязанных к этой фиче.',
      'Критерий выхода измерим, а не «когда всё хорошо».',
    ],
    submitNote: 'Даже облегчённый Test Plan в портфолио выделяет: показывает, что ты мыслишь планированием, а не только кейсами.',
    razbor: {
      etalon:
        '<p>Сильный мини-план конкретен: scope — сердечко на карточке, счётчик в шапке, раздел «Избранное», гейт гостя; out of scope — корзина (S03) и оплата; подход — ручное + границы (пустое избранное, много товаров), гость против покупателя, 2 браузера; риски — зависимость от роли, нет тестовых аккаунтов, сжатый срок; вход — фича на стенде; выход — P1-кейсы зелёные, нет открытых Blocker/Critical.</p>',
      mistakes: [
        'Out of scope пустой — потом спор «а это вы тестировали?»; явная граница экономит нервы.',
        'Риски — общие места («могут быть баги») вместо конкретных для фичи.',
        'Критерий выхода «когда оттестируем» — непроверяемо; нужен измеримый порог.',
      ],
      qa: [
        { q: 'Зачем junior план, если кейсы и так есть?', a: 'Кейсы отвечают «что проверить», план — «что в принципе берём, чего не берём, когда стоп». Без него легко утонуть в деталях и пропустить, что важная область вообще вне охвата.' },
        { q: 'Чем критерий входа отличается от предусловия кейса?', a: 'Критерий входа — про старт ВСЕГО тестирования фичи (среда, доступы, готовность сборки). Предусловие — про старт одного кейса. Разные масштабы.' },
      ],
    },
  },
  // ─── S05 · тест-дизайн · EP/BVA · Decision Table · State Transition · Pairwise · метрики ──
  {
    id: 'hw_s05_01',
    sprintId: 'S05',
    code: 'HW-S05-01',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'По таблице на поле: классы EP (valid/invalid) + BVA 3-value на границы + минимальный набор тест-данных.' },
    },
    atelier: {
      screen: 'sign-up',
      sandbox: true,
      intro: 'Форма регистрации в sandbox-режиме. Сначала проверь свои граничные значения прямо на ней — какие реально отвергаются, а где валидация пропускает (баг)? Классы дели по фактическому поведению.',
    },
    title: 'EP + BVA для формы регистрации',
    estimateMin: 45,
    priority: 'P1',
    portfolio: {
      artefact: 'EP+BVA по 4 полям формы: классы эквивалентности + 3-value границы + минимальный набор тестов',
      shows: 'разбиваешь любое поле на классы и проверяешь границы — база тест-дизайна',
    },
    brief:
      '<p>EP + BVA — пара техник, на которой держится тестирование любой формы, и любимый билет на CTFL. EP даёт классы (что эквивалентно), BVA проверяет конкретную границу класса. Junior часто берёт только EP — и пропускает баги ровно на краях.</p>',
    buddyAdvice:
      'EP без BVA — типичная ошибка. EP говорит «эти значения эквивалентны», BVA — «а вот ровно на границе сломается». Они дополняют, не заменяют.',
    given: {
      label: 'Дано · форма регистрации «Артели» (AUTH-01 / AUTH-02)',
      html: '<p>Поля формы (проверь на песочнице ниже): <strong>Email</strong> (формат name@domain), <strong>Username</strong> (минимум 3 символа), <strong>Password</strong> (минимум 8 символов и хотя бы 1 цифра), <strong>Согласие с условиями</strong> (обязательный чекбокс).</p>',
    },
    steps: [
      'Для каждого поля выпиши <strong>классы эквивалентности</strong> (EP): valid + invalid, минимум 3 класса на поле.',
      '<strong>BVA 3-value</strong> на каждую границу длины: username вокруг 3 (2·3·4), password вокруг 8 (7·8·9) — три значения вокруг границы, не одно.',
      'В конце — <strong>минимальный набор тест-данных</strong>, покрывающий все EP+BVA полей.',
      'Включи хотя бы один Unicode-класс (username кириллицей — valid; username с эмодзи — по спецификации?).',
    ],
    formatNote: 'Формат — таблица на поле: класс · тип (valid/invalid) · пример значения.',
    hints: [
      { label: 'Подсказка 1', sub: 'сколько границ у поля', body: 'У «username ≥3» одна граница: 2·3·4 (меньше·ровно·больше). У «password ≥8» — 7·8·9. По 3 значения на границу.' },
      { label: 'Подсказка 2', sub: 'на границе, но невалидный', body: '«abcdefgh» — 8 символов (по длине ок), но без цифры → invalid по составу (AUTH-02 требует ≥1 цифру). Так EP и BVA пересекаются: на границе длины, но провал класса.' },
      { label: 'Подсказка 3', sub: 'email-регулярка засасывает', body: 'Не уходи в RFC целиком. Для junior упрощай: есть @, есть точка после @, не пустое. Спорные кейсы пометь «уточнить у аналитика».' },
    ],
    selfCheck: [
      'Минимум 3 EP-класса на каждое поле формы.',
      'BVA 3-value на границах длины (username вокруг 3, password вокруг 8), по 3 значения на границу.',
      'В конце есть минимальный набор тест-данных.',
      'Есть хотя бы один Unicode-класс.',
    ],
    submitNote: 'Таблицы EP+BVA — частый практический билет на собесе и в портфолио.',
    example: {
      head: 'Образец · EP+BVA для поля Password',
      html: '<p>EP: {<8 символов invalid}, {≥8 без цифры invalid}, {≥8 с цифрой valid}. BVA границы длины 8: «abc1234» (7, invalid) · «abcd1234» (8, valid) · «abcde1234» (9, valid).</p>',
    },
    razbor: {
      etalon:
        '<p>Сильная работа разделяет EP (классы) и BVA (границы) явно. Пример по password: EP — {<8 invalid}, {≥8 без цифры invalid}, {≥8 с цифрой valid}; BVA границы 8 — 7·8·9 символов. Минимальный набор — представитель каждого valid-класса + все boundary-значения + по одному из invalid-классов.</p>',
      mistakes: [
        'Взяли только EP, границы не тронули — баги живут именно на краях.',
        '2-value вместо 3-value — CTFL 4.0 требует три значения вокруг границы.',
        'Классы не покрывают invalid — «только хорошие данные» это happy-path, а не EP.',
      ],
      qa: [
        { q: 'Какое поле дало больше всего тест-данных и почему?', a: 'Обычно пароль: несколько независимых правил (длина + цифра + заглавная + спецсимвол) перемножают классы. Каждое правило — своя ось EP.' },
        { q: 'Какой класс не стал бы тестировать при time-boxed регрессии?', a: 'Глубокие редкие invalid-подклассы (экзотический Unicode) — низкий риск и частота. Это risk-based из S03: режем по последствию.' },
      ],
    },
  },
  {
    id: 'hw_s05_02',
    sprintId: 'S05',
    code: 'HW-S05-02',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Полная таблица 16 строк (условия → Expected · Reachability · TC) + сокращённая до минимального покрытия.' },
    },
    title: 'Decision Table для права на отзыв в «Артели»',
    estimateMin: 40,
    priority: 'P1',
    portfolio: {
      artefact: 'Decision Table «право написать отзыв»: 16 комбинаций + сокращение до минимального покрытия + пометки доступа',
      shows: 'проверяешь логику «если X и Y и Z» без пропуска комбинаций — то, что губит контроль доступа',
    },
    brief:
      '<p>Decision Table ловит логику «если X, и Y, и Z, то…». В гейтах доступа пропуск одной комбинации = дыра (кто-то оставит отзыв, не имея права). Техника даёт полную таблицу, а потом честно сокращает её до минимума — это и спрашивают на собесе.</p>',
    buddyAdvice:
      'Если у тебя 16 разных Expected — почти наверняка ошибка. В реальной логике многие комбинации схлопываются в один исход (гость — остальное уже неважно, всё равно «войди и купи»).',
    given: {
      label: 'Дано · 4 условия права на отзыв (PDP-09)',
      html: '<p>Булевы условия для кнопки «Написать отзыв» на карточке товара «Артели»: <strong>пользователь вошёл (не гость)</strong> · <strong>купил этот товар (заказ доставлен)</strong> · <strong>не продавец</strong> · <strong>ещё не оставлял отзыв на этот товар</strong>. Исходы: показать «Написать отзыв» · «войди и купи» + Войти · «после покупки» · «продавцы не оставляют отзывы» · «уже оставил отзыв».</p>',
    },
    steps: [
      'Построй полную Decision Table: 4 условия → 2⁴ = <strong>16 комбинаций</strong>. Колонки: # · усл.1 · 2 · 3 · 4 · Expected · Reachability · TC.',
      'Для каждой строки — Expected Result и пометка достижимости (возможна ли комбинация на практике).',
      'Явно помечай строки-доступа (кнопка «Написать отзыв» показана тому, кто не имеет права — это дефект доступа, не «обычная ошибка»).',
      'Сократи таблицу до <strong>минимального покрытия</strong>: оставь только правила с разными Expected при тех же релевантных условиях (обычно 5-6).',
    ],
    example: {
      head: 'Образец · схлопнутое правило',
      html: '<p>«Гость (вошёл=нет): купил/продавец/оставлял — неважно, все 8 строк схлопываются в один исход „войди и купи“ + кнопка Войти».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'как схлопывать', body: 'Если пользователь не вошёл — система покажет «войди и купи» независимо от покупки/роли/истории. 8 строк с «вошёл=нет» свернутся в одно правило.' },
      { label: 'Подсказка 2', sub: 'минимизация не интуиция', body: 'Выкидывай правило, только если другое даёт тот же Expected при тех же значимых условиях. Это формально проверяемо, а не «кажется, лишнее».' },
      { label: 'Подсказка 3', sub: 'достижимость', body: 'Некоторые комбинации не собрать (не купил, но «уже оставил отзыв» — противоречие). Помечай «не достижимо» с обоснованием; если строка достижима — тоже фиксируй.' },
    ],
    selfCheck: [
      'Все 16 комбинаций в полной таблице, у каждой Expected и Reachability.',
      'Строки-доступа помечены отдельно (кнопка показана без права).',
      'Сокращённое покрытие выделено (обычно 5-6), каждое правило — уникальный Expected.',
      'Для «не достижимых» строк есть обоснование (или явно сказано, что все достижимы).',
    ],
    submitNote: 'Полная + минимальная Decision Table — сильный артефакт собеса, особенно на гейтах доступа.',
    razbor: {
      etalon:
        '<p>Сильная таблица показывает схлопывание: 16 строк → ~5-6 правил. «Вошёл=нет» поглощает всё в «войди и купи»; «продавец» — в «продавцы не оставляют»; среди купивших ветвится «уже оставлял». Минимизация формальна: правило остаётся, только если даёт новый Expected. Строки-доступа (кнопка показана гостю/не купившему) помечены — их пропуск = дыра доступа.</p>',
      mistakes: [
        '16 разных Expected — не схлопнули комбинации, таблица не отражает реальную логику.',
        'Сократили «на глаз», без правила «тот же Expected при тех же условиях» — теряется доказуемость покрытия.',
        'Строки-доступа в общей куче — «кнопка показана без права» не «обычная ошибка», а отдельный риск.',
      ],
      qa: [
        { q: 'Какое правило опаснее всего пропустить?', a: 'Кнопка «Написать отзыв» показана тому, кто не купил (или гостю): отзыв оставит человек без покупки — накрутка и подрыв доверия к отзывам.' },
        { q: 'Добавили 5-е условие — что с таблицей?', a: '2⁵ = 32 строки. Поэтому Decision Table не масштабируется бесконечно: при многих условиях переходят к pairwise или cause-effect.' },
      ],
    },
  },
  {
    id: 'hw_s05_03',
    sprintId: 'S05',
    code: 'HW-S05-03',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Часть А: состояния+переходы+триггеры+невалидные. Часть Б: pairwise-набор + число тестов + расчёт сокращения.' },
    },
    atelier: {
      screen: 'marketplace',
      sandbox: true,
      intro: 'Каталог с фильтром в sandbox-режиме. Прогони несколько pairwise-комбинаций руками — отметь, что реально ломается на сочетаниях.',
    },
    title: 'State Transition аккаунта + Pairwise фильтра',
    estimateMin: 45,
    priority: 'P2',
    portfolio: {
      artefact: 'State-Transition диаграмма аккаунта (валидные + невалидные переходы) + Pairwise-набор для фильтра с расчётом сокращения',
      shows: 'превращаешь «144 комбинации» в управляемый набор и обосновываешь покрытие',
    },
    brief:
      '<p>Две техники против комбинаторного взрыва. State Transition — для систем с состояниями (аккаунт, заказ): тестируем переходы, включая запрещённые. Pairwise — когда параметров много: 90% багов от взаимодействия пар, поэтому 12 тестов покрывают 144 комбинации.</p>',
    buddyAdvice:
      'В State Transition самое ценное — НЕвалидные переходы (Deleted → Active). Их забывают, а это security-территория.',
    given: {
      label: 'Дано · листинг «Артели» и фильтр каталога',
      html: '<p><strong>А.</strong> Состояния листинга (SELL / MOD): Draft · Pending (на модерации) · Active (в каталоге) · Rejected. <strong>Б.</strong> Фильтр каталога «Артели»: Категория (3) × Материал (4) × Наличие (3: все/в наличии/готово к отправке) × Сортировка (4) = 144 комбинации.</p>',
    },
    steps: [
      '<strong>Часть А.</strong> Диаграмма состояний (Mermaid/рисунок): все 4 состояния листинга + переходы с триггерами («продавец создал» → Pending, «менеджер одобрил» → Active, «менеджер отклонил» → Rejected).',
      'Найди <strong>невалидные переходы</strong> (Rejected → Active без повторной модерации, Active → Pending напрямую) и пометь «должны отвергаться» — обычно 3-5.',
      '<strong>Часть Б.</strong> Сгенерируй pairwise-набор для фильтра каталога (PICT / pairwise.yuuniworks) — зафиксируй число тестов (обычно ~12-16).',
      'Посчитай сокращение: «полный перебор 144 → pairwise M → в 144/M раз».',
    ],
    hints: [
      { label: 'Подсказка 1', sub: 'ключевой переход модерации', body: 'Pending особое: из него либо в Active (менеджер одобрил, MOD-02), либо в Rejected (отклонил). Два исходящих перехода с разными триггерами — сердце модерации.' },
      { label: 'Подсказка 2', sub: 'что значит невалидный переход', body: 'Переход, который система должна запрещать (отклонённый листинг сам не всплывает в каталоге). Прямой вызов в обход модерации — проверка контроля доступа поверх state-transition.' },
      { label: 'Подсказка 3', sub: 'почему pairwise работает', body: 'Большинство багов вызывают ПАРЫ параметров, не тройки. Покрыв все пары (12 из 144), ловишь почти всё — это и есть размен техники.' },
    ],
    example: {
      head: 'Образец · один невалидный переход',
      html: '<p>«Rejected → Active напрямую (отклонённый листинг сам появляется в каталоге) — невалидный, должен отвергаться: путь в каталог только через повторную модерацию (Pending → Active)».</p>',
    },
    selfCheck: [
      'Диаграмма покрывает все 4 состояния листинга; переходы с конкретными триггерами.',
      'Невалидные переходы помечены «должны отвергаться» (3-5 для 4 состояний).',
      'Pairwise-набор сгенерирован, число тестов зафиксировано (+ вывод инструмента).',
      'Расчёт сокращения 144 → M указан.',
    ],
    submitNote: 'Диаграмма + pairwise-расчёт наглядно показывают, что ты управляешь комбинаторикой, а не тонешь в ней.',
    razbor: {
      etalon:
        '<p>Сильная часть А делит валидные (Draft→Pending, Pending→Active, Pending→Rejected) и невалидные (Rejected→Active без повторной модерации, Active→Pending напрямую) переходы; у каждого конкретный триггер. Часть Б показывает сокращение в ~9-12 раз (144→12-16) и понимает цену: pairwise не ловит баги тройных взаимодействий — осознанный размен.</p>',
      mistakes: [
        'Только валидные переходы — пропущена территория контроля (отклонённый листинг всплыл в каталоге).',
        'Триггеры абстрактны («событие 1») — тест по такому не напишешь.',
        'Pairwise как «магия», без числа и расчёта — теряется смысл обоснования покрытия.',
      ],
      qa: [
        { q: 'Самый опасный невалидный переход, если случайно работает?', a: 'Rejected → Active: отклонённый модератором листинг сам появляется в каталоге — обход модерации, в продажу уходит то, что забраковали.' },
        { q: 'Какие баги pairwise точно не найдёт?', a: 'Те, что срабатывают только на тройном сочетании параметров. Это закладываемый размен: меньше тестов ценой пропуска редких тройных взаимодействий.' },
      ],
    },
  },
  {
    id: 'hw_s05_04',
    sprintId: 'S05',
    code: 'HW-S05-04',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Метрики (формула+цифра+интерпретация) + Test Summary Report: Executive Summary, таблица, Open Risks, GO/NO-GO.' },
    },
    title: 'Метрики цикла + Test Summary Report',
    estimateMin: 35,
    priority: 'P3',
    portfolio: {
      artefact: 'Test Summary Report: метрики прогона + Open Risks + рекомендация GO/NO-GO с обоснованием',
      shows: 'переводишь цифры прогона в решение «релизим или ждём» — язык, понятный PM',
    },
    brief:
      '<p>Test Summary Report — документ, по которому PM решает «релизим или ждём». Тут не «потыкал и норм», а посчитанные метрики, честные риски и рекомендация GO/NO-GO. Это разговор тестировщика с бизнесом на языке цифр.</p>',
    buddyAdvice:
      'Executive Summary — для человека, у которого 30 секунд: 3-5 строк (что проверено, ключевые цифры, GO/NO-GO). Детали ниже.',
    given: {
      label: 'Дано · итоги цикла «Артели» (условные)',
      html: '<p>Фича «Избранное «Артели»». Test Cases: 80 запланировано, 75 выполнено (5 блокированы BUG-101). Passed 62 / Failed 13. Багов 24 (Critical 2 · Major 6 · Minor 11 · Trivial 5), закрыто 18, open 6 (1 Critical · 2 Major · 3 Minor). Требований 12, покрыто 11 (не покрыта синхронизация между устройствами). Reopen: 3 из 18.</p>',
    },
    example: {
      head: 'Образец · одна метрика',
      html: '<p>«Pass Rate = passed / выполнено = 62 / 75 ≈ 82,7%. Интерпретация: есть стабильность, но не „ready“ — бенчмарк релиза обычно 90%+».</p>',
    },
    steps: [
      'Посчитай метрики (формула + цифра + интерпретация одной строкой): <strong>Pass Rate</strong>, <strong>Coverage по требованиям</strong>, <strong>Defect Density по фиче</strong>, <strong>Reopen Rate</strong>.',
      '(опц.) Defect Leakage: если 1 баг ушёл в прод через неделю — как изменится картина.',
      'Напиши Test Summary Report (1 страница): Executive Summary (3-5 строк) · таблица метрик · Open Risks · Recommendation.',
      'Recommendation: <strong>GO / NO-GO / GO с условием</strong> — с обоснованием, а не просто словом.',
    ],
    hints: [
      { label: 'Подсказка 1', sub: 'Pass Rate от чего', body: 'Pass Rate = passed / выполнено = 62/75 ≈ 82,7% (не от 80 — блокированные не выполнялись). 82% — есть стабильность, но не «ready» (бенчмарк релиза ~90%+).' },
      { label: 'Подсказка 2', sub: 'непокрытое требование', body: 'Coverage = 11/12 ≈ 91,7%. Одно требование («синхронизация избранного между устройствами») не покрыто — это GAP, его место в Open Risks, а не в сноске.' },
      { label: 'Подсказка 3', sub: 'как решать GO/NO-GO', body: 'Открытые 1 Critical + 2 Major по ключевой фиче — это потеря данных и подрыв доверия пользователя. Базовая школа: NO-GO, либо GO с жёстким условием (Critical чинится до релиза).' },
    ],
    selfCheck: [
      'Все 4 метрики: формула + цифра + интерпретация.',
      'Executive Summary 3-5 строк, не больше.',
      'Recommendation обоснована (не просто «GO»), с условием при необходимости.',
      'Open Risks заполнены конкретикой (непокрытое требование, открытые Critical/Major).',
    ],
    submitNote: 'TSR с рекомендацией GO/NO-GO отличает тестировщика от «кликера»: ты влияешь на релиз.',
    razbor: {
      etalon:
        '<p>Сильный отчёт честен: Pass Rate 82,7% и Coverage 91,7% поданы с интерпретацией, открытые Critical/Major вынесены в Open Risks, рекомендация — «NO-GO» или «GO с условием: BUG-101 (Critical) чинится до релиза, для Major — воркэраунд». Reopen 16,7% упомянут как флаг нестабильности фиксов.</p>',
      mistakes: [
        '«GO» без условий при открытом Critical — цифры есть, но решение игнорирует риск.',
        'Метрика без интерпретации (просто «82%») — PM не поймёт, хорошо это или плохо.',
        'Непокрытое требование спрятано — частый способ «случайно» отгрузить дыру.',
      ],
      qa: [
        { q: 'Какая метрика самая обманчивая?', a: 'Pass Rate: высокий процент при низком Coverage выглядит хорошо, но значит «прошло то, что вообще проверяли». Без Coverage Pass Rate льстит.' },
        { q: 'PM: «релиз через 2 дня, выкатываем?»', a: '«С текущими цифрами NO-GO: открыт 1 Critical и 2 Major по ключевой фиче. Реалистично — GO с условием: Critical чиним за 2 дня, по Major — воркэраунд + план фикса».' },
      ],
    },
  },
  {
    id: 'hw_s05_05',
    sprintId: 'S05',
    code: 'HW-S05-05',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Выбери 2 техники на оформление заказа: классы/границы/таблица/переходы → мини-набор тест-кейсов + обоснование выбора.' },
    },
    atelier: {
      screen: 'place-order',
      sandbox: true,
      scenarioId: 'B1-FOUND-04',
      intro: 'Оформление заказа во встроенном магазине (sandbox). Примени техники тест-дизайна к реальному флоу.',
    },
    title: 'Тест-дизайн-пак на оформление заказа',
    estimateMin: 35,
    priority: 'P2',
    portfolio: {
      artefact: 'Мини тест-дизайн-пак на оформление заказа: 2 техники → набор тест-кейсов с обоснованием выбора',
      shows: 'выбираешь подходящую технику под конкретную фичу, а не одну на всё',
    },
    brief:
      '<p>Техники не живут в вакууме — их выбирают под фичу. Здесь применишь две из пройденных (EP/BVA, Decision Table, State Transition, Pairwise) к живому оформлению заказа в песочнице и обоснуешь, почему именно эти.</p>',
    buddyAdvice:
      'Сначала пройди флоу руками: где поля (EP/BVA), где ветвления условий (Decision Table), где шаги-состояния (State Transition). Техника идёт от структуры фичи.',
    given: {
      label: 'Дано · оформление заказа (песочница)',
      html: '<p>Флоу в панели: адрес → оплата → подтверждение. Есть поля (индекс, карта), условия (адрес валиден × карта валидна × согласие), шаги-состояния (степпер).</p>',
    },
    steps: [
      'Пройди флоу оформления в песочнице, отметь: где поля, где условия, где переходы-шаги.',
      'Выбери <strong>2 техники</strong> под структуру (например BVA на индекс/карту + Decision Table на «адрес × карта × согласие → можно ли оформить»).',
      'Для каждой техники — мини-набор тест-кейсов (3-4), коротко: вход → ожидание.',
      'Обоснуй выбор: почему эти 2 техники подходят этой фиче, а не другие.',
    ],
    example: {
      head: 'Образец · выбор техники + один кейс',
      html: '<p>«BVA на индекс (6 цифр): 5 цифр (invalid) · 6 цифр (valid) · 7 цифр (invalid). Выбор: индекс — поле с точной длиной, границы и есть источник багов».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'какая техника к чему', body: 'Поле с диапазоном (индекс 6 цифр) → EP/BVA. Несколько булевых условий на один исход (оформить/нет) → Decision Table. Многошаговый степпер с «назад» → State Transition.' },
      { label: 'Подсказка 2', sub: 'не размазывай', body: 'Не пытайся применить все четыре. Две техники, но осмысленно: покрытие + обоснование важнее количества.' },
      { label: 'Подсказка 3', sub: 'sandbox подсказывает', body: 'В sandbox-режиме в чекауте зашиты баги (индекс принимает не-цифры, пустой купон проходит). Это подскажет, какие границы и условия точно стоит покрыть.' },
    ],
    selfCheck: [
      'Флоу пройден в песочнице, структура размечена (поля/условия/переходы).',
      'Выбраны 2 техники, к каждой 3-4 тест-кейса (вход → ожидание).',
      'Есть обоснование выбора техник под эту фичу.',
      'Хотя бы один кейс покрывает границу или запрещённую комбинацию.',
    ],
    submitNote: 'Пак «техника под фичу» показывает зрелость: ты не лупишь одной техникой по всему.',
    razbor: {
      etalon:
        '<p>Сильный пак подбирает технику под структуру: BVA на числовые поля (индекс 5·6·7 цифр), Decision Table на «адрес валиден × карта валидна × согласие → оформить/блок». Обоснование честное: степпер можно было бы взять State Transition, но при 3 линейных шагах выгода мала. Зрелость — в отказе от лишней техники.</p>',
      mistakes: [
        'Применили технику не по адресу (Decision Table к одному полю вместо EP/BVA).',
        'Все четыре техники «на показ» — без обоснования и с пустыми наборами.',
        'Кейсы только happy-path — границы и запрещённые комбинации не тронуты.',
      ],
      qa: [
        { q: 'Когда НЕ стоит брать Decision Table?', a: 'Когда условий мало или они независимы (одно → один исход). Таблица 2¹ бессмысленна; бери EP. DT окупается на 3-4 взаимозависимых условиях.' },
        { q: 'Как техники связаны с риском (S03)?', a: 'Техника даёт ПОКРЫТИЕ, риск — ПРИОРИТЕТ. Сначала risk-based решаешь, что покрывать глубоко, потом техникой — как именно. Вместе: глубоко тестируешь важное.' },
      ],
    },
  },
  // ─── S06 · CTFL Summit · подготовка к экзамену (mock-разбор · колода · шпаргалка) ──
  {
    id: 'hw_s06_01',
    sprintId: 'S06',
    code: 'HW-S06-01',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Анализ ошибок (главы + K-уровень) · топ 3-5 тем с действиями · расписание по дням (≤90 мин/день).' },
    },
    title: 'План адресного повтора после Mock #1',
    estimateMin: 45,
    priority: 'P1',
    portfolio: {
      artefact: 'Targeted re-study план: анализ ошибок по главам и K-уровню + 3-5 тем + расписание',
      shows: 'готовишься к экзамену адресно по слабым местам, а не «повторю всё»',
    },
    brief:
      '<p>После Mock-экзамена ценно не «повторить всё», а закрыть конкретные слабые места. Адресный план экономит дни до Mock #2 и снимает риск выгорания перед реальным CTFL.</p>',
    buddyAdvice:
      'Разбирай ошибки по K-уровню. Если 80% провалов на K3 (применение) — проблема не в памяти, а в тренировке применения: добавляй Lab-практику, не зубрёжку.',
    given: {
      label: 'Дано · результаты Mock #1',
      html: '<p>На руках — 40 ответов с PASS/FAIL и привязкой к главам CTFL 4.0 (Ch.1 Fundamentals · Ch.2 SDLC · Ch.3 Static · Ch.4 Techniques · Ch.5 Management · Ch.6 Tools). Если до Mock #1 ещё не дошёл — работай в режиме «допустим, у меня было…».</p>',
    },
    steps: [
      '<strong>Анализ.</strong> Какие 2 главы дали больше всего ошибок? Какой K-уровень подвёл (K1 recall / K2 understand / K3 apply)?',
      '<strong>Топ 3-5 тем.</strong> Внутри слабых глав — конкретные темы (не «Ch.4», а «Decision Table — построение и минимизация»); к каждой 1-2 действия (перечитать секцию, прорешать N вопросов, посмотреть Lab).',
      '<strong>Расписание.</strong> День 1/2/3 (до 5): что делаешь, сколько времени, ожидаемый эффект. Порог ≤90 мин/день.',
    ],
    formatNote: 'Формат — таблица или один документ: тема · действие · день · время.',
    example: {
      head: 'Образец · одна строка плана',
      html: '<p>«Тема: минимизация Decision Table (Ch.4, K3) · Действие: прорешать 8 вопросов на схлопывание правил · День 2 · 60 мин · Эффект: перестать путать „убрать дубль“ и „убрать правило“».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'почему именно Ch.4', body: 'У джуниоров чаще всего слабая Ch.4 (техники): K3-вопросы требуют применения, не recall. Если ошибки там — это нормально, лечится Lab-практикой.' },
      { label: 'Подсказка 2', sub: 'что такое K-уровень', body: 'K1 — «определи термин», K2 — «объясни разницу», K3 — «выбери технику под ситуацию». Если все ошибки на K3 — теорию понял, применение не натренировал.' },
      { label: 'Подсказка 3', sub: '90 минут — порог', body: 'Больше 90 мин/день — копится усталость, retention падает. Лучше 4 раза по 60, чем один раз по 4 часа.' },
    ],
    selfCheck: [
      'Анализ ошибок по главам И по K-уровню сделан (можно в режиме «допустим»).',
      'Темы конкретные (не «Ch.4», а узкая тема), 3-5 штук.',
      'У каждой темы конкретное действие (не «повторить»).',
      'Расписание реалистичное, ≤90 мин/день.',
    ],
    submitNote: 'Адресный план — артефакт самоорганизации; пригодится и в работе при подготовке к любой аттестации.',
    razbor: {
      etalon:
        '<p>Сильный план начинается с честного анализа: «12 ошибок, 8 в Ch.4 на K3 → проблема в применении техник». Темы узкие («BVA 3-value», «минимизация Decision Table»), к каждой действие и день. Расписание щадящее (≤90 мин), с ожидаемым эффектом, а не «учить всё подряд».</p>',
      mistakes: [
        '«Повторю весь Ch.4» — это не адресный план, на него нет времени и он размазан.',
        'Ошибки разобраны по главам, но не по K-уровню — непонятно, память подвела или применение.',
        'Расписание на 4 часа в день — выгорание до Mock #2, retention падает.',
      ],
      qa: [
        { q: 'Что сложнее — анализ ошибок или выбор тем?', a: 'Обычно выбор тем: на анализе видно, ГДЕ слабо, а сузить до 3-5 конкретных тем и отсечь лишнее — это и есть приоритизация.' },
        { q: 'Остался 1 день до Mock #2 — на что потратишь?', a: 'На самую слабую K3-тему с наибольшим весом в экзамене (обычно техники Ch.4): прорешать вопросы на применение, а не перечитывать определения.' },
      ],
    },
  },
  {
    id: 'hw_s06_02',
    sprintId: 'S06',
    code: 'HW-S06-02',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: '30 карточек (Front: термин+контекст / Back: определение своими словами + pitfall) + журнал прогонов.' },
    },
    title: 'Колода spaced repetition · топ-30 терминов',
    estimateMin: 40,
    priority: 'P2',
    portfolio: {
      artefact: 'Колода 30 flashcards по сложным терминам CTFL + журнал прогонов retention',
      shows: 'запоминаешь терминологию в долгую через интервальные повторения, а не зубрёжкой',
    },
    brief:
      '<p>Spaced repetition — самый эффективный способ удержать терминологию надолго. Не 100 карточек: 30 хорошо проработанных запоминаются лучше и не съедают день на прогон.</p>',
    buddyAdvice:
      'Pitfall важнее определения. «X — это…» без «часто путают с Y» = половина знания. CTFL обожает вопросы «чем X отличается от Y».',
    given: {
      label: 'Дано · глоссарий 155 терминов',
      html: '<p>Из глоссария CTFL выбери <strong>30 самых сложных для тебя</strong> терминов: где путаешься между похожими (severity/priority, smoke/sanity, validation/verification, EP/BVA), не уверен в формальном определении, или ошибся на mock из-за термина.</p>',
    },
    steps: [
      'Создай 30 карточек (Anki / Quizlet / бумага / Notion). <strong>Front:</strong> термин + 1 фраза контекста. <strong>Back:</strong> определение своими словами (1-2 предложения, без дословных цитат) + pitfall («часто путают с X, потому что…»).',
      'Минимум 5 карточек — классические путаницы (severity/priority, validation/verification, EP/BVA, smoke/sanity, retesting/regression).',
      'Прогон 3-7 дней, 5-10 мин/день. Веди журнал retention (сколько из 30 правильно на каждый день).',
    ],
    formatNote: 'Сдай экспорт колоды (или скриншот) + журнал прогонов.',
    example: {
      head: 'Образец · одна карточка (на примере «Артели»)',
      html: '<p><strong>Front:</strong> Severity vs Priority (баг в «Артели»). <strong>Back:</strong> severity — тяжесть для системы (отрицательный итог корзины = высокая); priority — срочность для бизнеса. <strong>Pitfall:</strong> путают, потому что оба «про важность» — но severity техническая, priority бизнесовая.</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'почему 30, не 100', body: 'Оставшиеся 125 терминов ты уже знаешь по S01-S05. 30 трудных, проработанных глубоко, дают больше, чем 100 поверхностных, и не выжигают день.' },
      { label: 'Подсказка 2', sub: 'бумажные карточки', body: 'Без Anki — нумеруй и перемешивай по дням: день 1 все 30, день 2 незапомненные, день 4 ещё раз, день 7 финал. Это ручной spaced repetition.' },
      { label: 'Подсказка 3', sub: 'как писать back', body: 'Своими словами, не цитата из syllabus — так проверяешь понимание, а не копипаст. Плюс строка pitfall: с чем и почему путают.' },
    ],
    selfCheck: [
      'Колода ≥30 карточек, у каждой непустые Front и Back.',
      'Минимум 5 карточек — классические путаницы.',
      'Back содержит определение И pitfall (с чем путают).',
      'Журнал прогонов ≥3 дней (число правильных из 30 по дням).',
    ],
    submitNote: 'Колода — переиспользуемый артефакт: пригодится перед реальным CTFL и при онбординге на работе.',
    razbor: {
      etalon:
        '<p>Сильная колода бьёт по путаницам: severity (тяжесть для системы) vs priority (срочность для бизнеса), validation («то ли построили») vs verification («правильно ли построили»). Back — своими словами + строка «путают с…, потому что…». Журнал показывает рост retention день ото дня.</p>',
      mistakes: [
        '100 поверхностных карточек — прогон превращается в работу на день, retention не растёт.',
        'Back — дословная цитата из глоссария: проверяешь копипаст, а не понимание.',
        'Нет pitfall — знаешь определение, но на вопросе «чем X отличается от Y» всё равно ошибёшься.',
      ],
      qa: [
        { q: 'После 3 дней какие 5 карточек не запоминаются и что общего?', a: 'Обычно это пары-путаницы (verification/validation): мозг держит «оба про проверку» и не различает. Лечится мнемоникой различия, а не повтором определения.' },
        { q: 'Делал бы колоду для друга — что изменил?', a: 'Набор сложных тем: у каждого свои слабые места. Колода персональна — копировать чужую почти бесполезно, ценность в выборе своих трудных терминов.' },
      ],
    },
  },
  {
    id: 'hw_s06_03',
    sprintId: 'S06',
    code: 'HW-S06-03',
    surface: {
      kind: 'freeText',
      config: { minChars: 300, placeholder: 'По вопросу: формулировка · твой выбор · верный · почему верный — верный · почему каждый дистрактор неверен · K-уровень.' },
    },
    title: 'Разбор 10 каверзных вопросов',
    estimateMin: 55,
    priority: 'P1',
    portfolio: {
      artefact: 'Разбор 10 экзаменационных вопросов: почему верный — верный и почему каждый дистрактор неверен',
      shows: 'разбираешь логику дистракторов — навык, который реально поднимает балл на CTFL',
    },
    brief:
      '<p>На CTFL балл поднимает не зубрёжка, а умение объяснить, почему неверные варианты неверны. Дистракторы пишут под типичные заблуждения — разобрав их, ты перестаёшь на них попадаться.</p>',
    buddyAdvice:
      'На каждый вопрос отвечай не только «верный — B», а «A неверен потому-то, C — потому-то». Не можешь объяснить, почему дистрактор неверен, — значит угадал, а не знаешь.',
    given: {
      label: 'Дано · 10 вопросов',
      html: '<p>Возьми 10 вопросов, на которых ты ошибся или сомневался (mock, Quiz Banks курса, открытые CTFL sample exams). Лучше — те, что задели техники Ch.4 и термины-путаницы.</p>',
    },
    steps: [
      'Для каждого вопроса выпиши: формулировка (кратко) · твой выбор · верный ответ.',
      'Объясни, <strong>почему верный — верный</strong> (1-2 предложения, со ссылкой на правило/определение).',
      'Объясни, <strong>почему каждый дистрактор неверен</strong> — какое заблуждение он эксплуатирует.',
      'Помечай K-уровень вопроса (K1/K2/K3) — увидишь, на каком уровне чаще спотыкаешься.',
    ],
    example: {
      head: 'Образец · разбор одного вопроса',
      html: '<p>Вопрос (K3): «Для поля „индекс — 6 цифр“ на оформлении заказа какая техника?». Верный: BVA (есть числовая граница длины). Дистрактор «Decision Table» неверен — одно поле, нет взаимозависимых условий (ловит тех, кто берёт DT на всё). Дистрактор «только happy-path» неверен — пропускает края.</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'дистрактор не случаен', body: 'Каждый неверный вариант пишут под конкретную ошибку мышления (перепутать severity/priority, взять 2-value вместо 3-value). Назови эту ошибку — и вопрос больше не поймает.' },
      { label: 'Подсказка 2', sub: 'где брать вопросы', body: 'Mock #1, Quiz Banks курса, открытые ISTQB sample exams (istqb.org). Бери те, что задели, а не лёгкие.' },
      { label: 'Подсказка 3', sub: 'K-уровень подскажет', body: 'Если 8 из 10 трудных — K3 (применение), твой следующий шаг не определения, а больше задач на выбор техники под ситуацию.' },
    ],
    selfCheck: [
      '10 вопросов разобраны: формулировка · твой выбор · верный.',
      'Для каждого объяснено, почему верный — верный.',
      'Для каждого объяснено, почему дистракторы неверны (какое заблуждение).',
      'У каждого помечен K-уровень.',
    ],
    submitNote: 'Разбор дистракторов реально двигает балл; техника пригодится на любой сертификации.',
    razbor: {
      etalon:
        '<p>Сильный разбор не останавливается на «верный — B». По каждому дистрактору назван механизм: «A путает retesting и regression», «C берёт happy-path вместо граничного». Видна картина по K-уровням: спотыкаешься на K3 — добираешь практику применения, а не recall.</p>',
      mistakes: [
        'Отметил только верный ответ — не понял, почему остальные неверны, значит снова попадёшься на тот же дистрактор.',
        'Объяснение «потому что так правильно» — без правила/определения это не разбор.',
        'Брал лёгкие вопросы — на них и так не ошибаешься, толку ноль.',
      ],
      qa: [
        { q: 'Почему разбор дистракторов поднимает балл сильнее, чем повтор определений?', a: 'Определения дают K1, а экзамен валит на K3 (применение) через дистракторы под типичные ошибки. Разбирая, почему вариант неверен, тренируешь именно тот навык, что проверяют.' },
        { q: 'Что делать с вопросом, где даже верный ответ непонятен?', a: 'Это пробел в теме: вернись к секции syllabus и проработай её (карточка в колоду), а не заучивай ответ — на экзамене формулировка будет другой.' },
      ],
    },
  },
  {
    id: 'hw_s06_04',
    sprintId: 'S06',
    code: 'HW-S06-04',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Одна страница: техники Ch.4 (когда применять + мини-пример) + 5 путаниц с однострочной разницей.' },
    },
    title: 'Одностраничная шпаргалка Ch.4 + путаницы',
    estimateMin: 40,
    priority: 'P2',
    portfolio: {
      artefact: 'Одностраничная шпаргалка: техники Ch.4 (когда применять) + 5 классических путаниц с разницей',
      shows: 'умеешь сжать тему до рабочей шпаргалки — и для экзамена, и для работы',
    },
    brief:
      '<p>Умение сжать главу до одной рабочей страницы — это и подготовка к экзамену, и навык на работе (онбординг, памятка команде). Делаем шпаргалку по самой «экзаменуемой» Ch.4 + классические путаницы.</p>',
    buddyAdvice:
      'Шпаргалка — не пересказ syllabus, а «когда что применять». Рядом с каждой техникой — одна строка «берём, когда…».',
    given: {
      label: 'Дано · Ch.4 + термины',
      html: '<p>Техники Ch.4: Equivalence Partitioning · Boundary Value Analysis · Decision Table · State Transition (+ при желании Pairwise / Use-Case). Плюс 5 классических путаниц терминов.</p>',
    },
    steps: [
      'По каждой технике Ch.4 — одна строка «когда применять» + мини-пример (1 фраза).',
      'Блок «5 путаниц»: пара терминов → одна строка различия (severity/priority, validation/verification, smoke/sanity, retesting/regression, EP/BVA).',
      'Всё на <strong>одну страницу</strong> — если не влезает, режь до сути.',
      'Проверь шпаргалку «вслепую»: закрой syllabus и попробуй по ней решить 2-3 вопроса.',
    ],
    example: {
      head: 'Образец · одна строка шпаргалки',
      html: '<p>«BVA — берём, когда есть числовая граница (длина, цена, индекс). Мини-пример: пароль «Артели» ≥8 символов → проверяем 7·8·9».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'что писать у техники', body: 'Не определение, а «когда применять»: EP — «дели вход на классы»; BVA — «3 значения вокруг границы»; Decision Table — «3-4 взаимозависимых условия»; State Transition — «есть состояния и переходы».' },
      { label: 'Подсказка 2', sub: 'путаницы одной строкой', body: 'severity = тяжесть для системы, priority = срочность для бизнеса. Validation = «то ли построили», verification = «правильно ли построили». Одна строка различия на пару.' },
      { label: 'Подсказка 3', sub: 'не влезает на страницу', body: 'Режь определения (они в глоссарии) и редкие техники; оставляй «когда применять» + примеры + путаницы. Шпаргалка про быстрый доступ к сути.' },
    ],
    selfCheck: [
      'Все основные техники Ch.4 на странице: «когда применять» + мини-пример.',
      'Блок из 5 путаниц с однострочной разницей.',
      'Влезает на одну страницу (сжато до сути).',
      'Проверена «вслепую» хотя бы на 2-3 вопросах.',
    ],
    submitNote: 'Одностраничная шпаргалка — переиспользуемый артефакт: на экзамен (повторить за 10 минут) и в работу (памятка).',
    razbor: {
      etalon:
        '<p>Сильная шпаргалка — это «когда что», а не пересказ. EP — «дели вход на классы, по одному представителю»; BVA — «3 значения вокруг границы»; Decision Table — «взаимозависимые условия → исходы»; State Transition — «состояния и переходы». Путаницы — одной строкой различия. Влезает на страницу и работает «вслепую».</p>',
      mistakes: [
        'Пересказала весь Ch.4 на 3 страницы — это конспект, а не шпаргалка; за 10 минут перед экзаменом не повторить.',
        'Техники без «когда применять» — а на экзамене вопрос именно «выбери технику для ситуации».',
        'Путаницы без различия (просто два термина рядом) — не помогает их различать.',
      ],
      qa: [
        { q: 'Зачем «когда применять», а не определение?', a: 'Экзамен и работа спрашивают выбор техники под ситуацию (K3). Определение — K1, его недостаточно. «Когда применять» — это рабочее знание.' },
        { q: 'Шпаргалка не влезает на страницу — что резать?', a: 'Режь определения (они в глоссарии) и редкие техники, оставляй «когда применять» + примеры + путаницы. Шпаргалка про быстрый доступ к сути, не полноту.' },
      ],
    },
  },
  {
    id: 'hw_s06_05',
    sprintId: 'S06',
    code: 'HW-S06-05',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Техника · мини-набор тестов по ней на форме · что техника поймала (включая баг) · K-уровень с обоснованием.' },
    },
    atelier: {
      screen: 'sign-up',
      sandbox: true,
      intro: 'Форма регистрации «Артели» в режиме тренажёра. Примени технику руками — и проверь, ловит ли она реальные баги. K3 (применение) тренируется только на практике.',
    },
    title: 'K3-блиц: техника на живой форме',
    estimateMin: 35,
    priority: 'P2',
    portfolio: {
      artefact: 'K3-блиц: техника тест-дизайна, применённая к живой форме, + что она поймала',
      shows: 'применяешь технику руками, а не только знаешь определение — это и валит на CTFL (K3)',
    },
    brief:
      '<p>Экзамен валит не на определениях (K1), а на применении (K3). Здесь берёшь технику из Ch.4 и применяешь её к живой форме — и видишь, что она реально ловит. Лучшая подготовка к K3-вопросам «выбери технику под ситуацию».</p>',
    buddyAdvice:
      'Определение «BVA — это…» не поможет на K3-вопросе. Поможет память тела: применишь технику руками один раз — на экзамене узнаешь ситуацию.',
    given: {
      label: 'Дано · форма + техника на выбор',
      html: '<p>Форма регистрации в песочнице. Выбери одну технику из Ch.4 (EP / BVA / Decision Table / State Transition) и примени к подходящему полю или логике.</p>',
    },
    steps: [
      'Выбери технику и поле/логику под неё (BVA на длину пароля, EP на email, State Transition на «отправил → ошибка → исправил → отправил»).',
      'Составь мини-набор тестов по технике (4-6) и прогони руками в песочнице.',
      'Зафиксируй, что техника поймала (включая зашитый баг — короткий пароль / email без @ проходят).',
      'Назови K-уровень задачи и обоснуй (обычно K3 — применение).',
    ],
    example: {
      head: 'Образец · техника на форме «Артели»',
      html: '<p>«BVA на длине пароля: 7 символов (отказ) · 8 (проходит) · 9 (проходит). Поймала: в тренажёре пароль „a“ (1 символ) проходит мимо правила ≥8 — баг валидации. K3: техника выбрана под поле и применена руками».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'техника под структуру', body: 'Диапазон (длина) → BVA; набор форматов (email) → EP; последовательность состояний формы → State Transition. Выбор техники — уже часть навыка.' },
      { label: 'Подсказка 2', sub: 'техника ловит баг', body: 'BVA на длине пароля (7·8·9) поймает, что «a» (1 символ) проходит мимо правила ≥8. Это ценность техники, а не теория.' },
      { label: 'Подсказка 3', sub: 'почему это K3', body: 'Ты не вспоминаешь определение (K1), а ВЫБИРАЕШЬ и ПРИМЕНЯЕШЬ технику к ситуации — это K3, ровно тот уровень, что валит на экзамене.' },
    ],
    selfCheck: [
      'Техника выбрана осознанно под структуру поля/логики.',
      'Мини-набор тестов (4-6) прогнан руками в песочнице.',
      'Зафиксировано, что техника поймала (включая баг).',
      'Назван K-уровень с обоснованием.',
    ],
    submitNote: 'Применение техники руками — тренировка ровно того навыка (K3), что проверяет CTFL и спрашивают на собесе.',
    razbor: {
      etalon:
        '<p>Сильный блиц показывает технику как инструмент, а не теорию: BVA на длине пароля (7·8·9) ловит, что «a» проходит мимо правила ≥8. Назван K3: техника выбрана под ситуацию и применена. Это ровно то, на чём валит экзамен, и что отрабатывается только руками.</p>',
      mistakes: [
        'Техника описана в теории, но не применена к форме — это K1, экзамен валит не на нём.',
        'Техника не по адресу (Decision Table к одному полю) — выбор техники тоже часть K3.',
        'Не отметил, что техника поймала — непонятно, зачем её применяли.',
      ],
      qa: [
        { q: 'Почему K3 нельзя натренировать чтением?', a: 'K1/K2 (знать/понять) читаются. K3 (применить) — навык, как езда на велосипеде: описанием не освоить. Поэтому экзамен и валит — все читали, мало применяли руками.' },
        { q: 'Техника не поймала ни одного бага — провал?', a: 'Нет: «техника применена, багов в этой области нет» — валидный результат. Но проверь, ту ли область взял: если поле тривиальное, возьми технику посложнее или другое поле.' },
      ],
    },
  },
  // ─── S07 · Bug Lifecycle / Reporting · калибровка · 11-полей · расследование · охота ──
  {
    id: 'hw_s07_01',
    sprintId: 'S07',
    code: 'HW-S07-01',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'По сценарию: Severity + Priority + обоснование. Сценарий 5: состояние жизненного цикла + обоснование.' },
    },
    title: 'Калибровка Severity / Priority · 5 сценариев',
    estimateMin: 30,
    priority: 'P1',
    portfolio: {
      artefact: 'Калибровка Severity и Priority на 5 сценариях + состояние жизненного цикла',
      shows: 'выставляешь severity и priority без раздумий — то, на чём джунов ловят на собесе',
    },
    brief:
      '<p>Severity и Priority — то, на чём чаще всего ловят на собесе и где теряют доверие тимлида в первый месяц. Severity — техническое влияние (что сломалось), Priority — бизнес-срочность (когда чинить). Их путают, и зря.</p>',
    buddyAdvice:
      'Опечатка в подвале — Severity Trivial, Priority высокий (страдает бренд). Если в каждом сценарии Severity = Priority — значит ты их не различаешь.',
    given: {
      label: 'Дано · 5 сценариев «Артели»',
      html:
        '<p>1. Опечатка «Pivacy Policy» в подвале «Артели», до запуска рекламы 2 дня.<br/>' +
        '2. На оформлении заказа у 100% мобильных не срабатывает «Подтвердить заказ» (на десктопе работает).<br/>' +
        '3. Аналитика менеджера не грузится при большом диапазоне данных; менеджер смотрит раз в год.<br/>' +
        '4. После оплаты «Заказ #undefined» вместо номера ORD-XXXXXX, в 5% случаев.<br/>' +
        '5. Разработчик влил фикс в master, билд выложен на staging — какое состояние тикета?</p>',
    },
    example: {
      head: 'Образец · калибровка одного сценария',
      html: '<p>«Сценарий 1 (опечатка в подвале перед рекламой): Severity Trivial (ничего не сломано технически), Priority высокий (видно всем, бьёт по бренду перед кампанией). Классика: техника низкая — бизнес высокий».</p>',
    },
    steps: [
      'Для сценариев 1-4: определи <strong>Severity</strong> (Critical/Major/Minor/Trivial) + <strong>Priority</strong> (P0-P3) + 1-2 строки обоснования.',
      'Сценарий 5: назови <strong>состояние жизненного цикла</strong> тикета + одну строку обоснования (это не калибровка, а вопрос про lifecycle).',
      'Проследи, чтобы хотя бы в одном сценарии Severity ≠ Priority.',
    ],
    hints: [
      { label: 'Подсказка 1', sub: 'severity vs priority', body: 'Severity — что сломалось (техника), Priority — когда чинить (бизнес). Опечатка в видном месте: severity Trivial, priority высокий.' },
      { label: 'Подсказка 2', sub: 'мобильный checkout', body: 'Почти всегда high priority (теряются транзакции). Severity зависит от обхода: есть desktop-обход → Major; обхода нет → Critical.' },
      { label: 'Подсказка 3', sub: 'сценарий 5', body: 'Фикс влит, но QA ещё не проверил → состояние «Resolved» / «Awaiting QA verification», не «Closed».' },
    ],
    selfCheck: [
      'Сценарии 1-4: severity + priority + обоснование.',
      'Сценарий 5: состояние названо и обосновано.',
      'Минимум в одном сценарии severity ≠ priority.',
      'Обоснования по сути (техника vs бизнес), не «потому что важно».',
    ],
    submitNote: 'Калибровка S/P — частый блиц на собесе; держи этот разбор под рукой.',
    razbor: {
      etalon:
        '<p>Сильная калибровка разводит оси: 1 — Trivial/высокий (бренд); 2 — Critical или Major (зависит от обхода)/P0 (деньги); 3 — Major/низкий (раз в год); 4 — Major/высокий (доверие к оплате, хоть и 5%); 5 — Resolved/Awaiting QA. Хотя бы раз S≠P — признак, что оси различаешь.</p>',
      mistakes: [
        'Severity = Priority во всех сценариях — оси не различаются.',
        'Обоснование «важно/неважно» без техника-vs-бизнес.',
        '«Order #undefined в 5%» занижено до Trivial — это бьёт по доверию к оплате.',
      ],
      qa: [
        { q: 'Где самый большой разрыв S и P и почему?', a: 'Опечатка в подвале перед кампанией: severity Trivial (ничего не сломано), priority высокий (видно всем, бренд). Классика «техника низкая — бизнес высокий».' },
        { q: 'Сценарий 5 — следующее действие QA?', a: 'Верификация фикса на staging: воспроизвести исходные шаги, убедиться, что баг ушёл и рядом ничего не отвалилось. Прошло — Closed; нет — Reopened.' },
      ],
    },
  },
  {
    id: 'hw_s07_02',
    sprintId: 'S07',
    code: 'HW-S07-02',
    surface: { kind: 'bugReport', config: { variant: 'full' } },
    atelier: {
      screen: 'place-order',
      sandbox: true,
      intro: 'Оформление заказа в sandbox. Найди баг руками и оформи полный баг-репорт в форме ниже (все поля).',
    },
    title: 'Полный баг-репорт на 11 полей (по находке)',
    estimateMin: 40,
    priority: 'P1',
    portfolio: {
      artefact: 'Полный баг-репорт на 11 полей по реальной находке, с явным списком приложений',
      shows: 'собираешь баг-репорт, по которому PM понимает, что тебе можно доверить проект',
    },
    brief:
      '<p>Полный 11-полевой баг-репорт — то, по чему PM понимает, можно ли оставить тебя наедине с проектом. Расширяем 6-полевую базу до 11: Pre-conditions, Priority, Frequency, Build, Business Impact, Attachments.</p>',
    buddyAdvice:
      'Business Impact — не «пользователю неудобно», а «часть транзакций потеряна → revenue loss». Attachments — конкретно: «скриншот до/после, HAR для POST /checkout», не «приложу скриншот».',
    given: {
      label: 'Дано · оформление заказа (песочница)',
      html: '<p>Пройди оформление в панели (sandbox: зашиты баги — индекс принимает не-цифры, пустой купон проходит). Найди один баг для полного отчёта.</p>',
    },
    steps: [
      'Найди баг в оформлении (sandbox).',
      'Заполни форму полного баг-репорта ниже: заголовок, описание, серьёзность, приоритет, окружение, шаги, ожидаемо, фактически, частота, влияние на бизнес.',
      'В поле <strong>«Приложения»</strong> перечисли, что приложил бы как evidence: скриншот шага / HAR / console log — конкретно, не «приложу скриншот».',
    ],
    example: {
      head: 'Образец · ключевые поля репорта',
      html:
        '<p><strong>Title:</strong> Пустой промокод применяется как валидный на шаге оплаты «Артели» (place-order), 100% случаев.<br/>' +
        '<strong>Severity:</strong> Major · <strong>Priority:</strong> высокий · <strong>Frequency:</strong> Always (5/5).<br/>' +
        '<strong>Business Impact:</strong> абуз промо и потенциальная потеря выручки. <strong>Приложения:</strong> скриншот шага «Оплата», запрос применения купона в Network.</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'Title по формуле', body: '[Что]+[где]+[когда]. Не «купон не работает», а «Пустой купон применяется как валидный на шаге оплаты (place-order), 100% случаев».' },
      { label: 'Подсказка 2', sub: 'Frequency — цифра', body: 'Always/Often/Sometimes/Rare на основе попыток (5/5 = Always), а не «иногда».' },
      { label: 'Подсказка 3', sub: 'Business Impact', body: 'Конкретно про деньги/риск: пустой купон проходит → потенциальная потеря выручки и абуз промо.' },
    ],
    selfCheck: [
      'Баг найден в песочнице и воспроизводится.',
      'Все 11 полей заполнены (не «—» для галочки).',
      'Список приложений конкретный (что именно приложить).',
      'Title по формуле, Frequency — цифра, Business Impact — про деньги/риск.',
    ],
    submitNote: '11-полевой репорт — эталон портфолио; severity + priority + business impact особенно смотрят.',
    razbor: {
      etalon:
        '<p>Сильный репорт воспроизводим и «продаёт» баг бизнесу: Title узнаваем, Business Impact в деньгах/риске, Attachments перечислены поимённо (скриншот шага, HAR запроса). 11 полей не ради числа: Pre-conditions и Build спасают от «не воспроизводится».</p>',
      mistakes: [
        'Поля заполнены «—» для галочки — каждое поле снимает вопрос, а не набирает счётчик.',
        'Business Impact = «неудобно» вместо денег/риска.',
        '«Приложу скриншот» вместо конкретного списка evidence — разработчик не знает, что именно нужно.',
      ],
      qa: [
        { q: 'Что PM попросит уточнить в слабом репорте?', a: 'Обычно Steps («не воспроизводится») или Business Impact («и что?»). Атомарные шаги + impact в деньгах закрывают оба вопроса заранее.' },
        { q: 'Какой репорт «не воспроизведётся» у разработчика?', a: 'Где нет Pre-conditions/Environment/Build: баг живёт в конкретном состоянии или браузере, а разработчик пробует «у себя» в другом — закроет как невоспроизводимый.' },
      ],
    },
  },
  {
    id: 'hw_s07_03',
    sprintId: 'S07',
    code: 'HW-S07-03',
    surface: {
      kind: 'freeText',
      config: { minChars: 300, placeholder: 'Гипотеза → проверка → вывод · evidence (что приложить) · полный баг-репорт · абзац рефлексии процесса.' },
    },
    atelier: {
      screen: 'profile',
      sandbox: true,
      intro: 'Профиль в sandbox (зашиты баги: имя не обновляется после save, и др.). Расследуй один баг, а не просто «нашёл и отдал».',
    },
    title: 'Мини-расследование бага',
    estimateMin: 40,
    priority: 'P2',
    portfolio: {
      artefact: 'Расследование бага: гипотезы + evidence + полный репорт + рефлексия процесса',
      shows: 'не «нашёл и отдал», а копаешь так, что разработчик сразу понимает — половина работы QA',
    },
    brief:
      '<p>Расследование — половина работы QA. Не «нашёл и кинул», а «нашёл, копнул, отдал так, чтобы разработчик сразу понял». Тренируем поток на одном баге.</p>',
    buddyAdvice:
      'Сначала гипотеза, потом проверка. «Имя не обновилось» — гипотеза: UI не перечитывает после записи? Проверка: navigate/refresh чинит? Это сужает баг для разработчика.',
    given: {
      label: 'Дано · профиль (песочница)',
      html: '<p>Профиль в панели (sandbox). Выбери один баг (например, имя не обновляется после сохранения, пока не перейдёшь на другую страницу).</p>',
    },
    steps: [
      'Выбери один баг в профиле (sandbox).',
      'Расследуй: сформулируй гипотезу, проверь её (refresh/navigate, DevTools если применимо), собери evidence (что приложить).',
      'Оформи полный баг-репорт + (если случай сложный) короткую заметку о влиянии на смежные фичи.',
      'Один абзац рефлексии: какую гипотезу подтвердил первой, сколько заняло, что добавил сверх базового шаблона.',
    ],
    example: {
      head: 'Образец · гипотеза → проверка',
      html: '<p>«Наблюдение: имя в профиле «Артели» не обновилось после сохранения. Гипотеза: UI не перечитывает данные после записи. Проверка: переход на другую страницу и обратно — имя обновилось. Вывод: нет refetch после мутации, navigate чинит. Evidence: скриншот „до / после перехода“».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'гипотеза сужает', body: '«Stale после save» → «UI не делает refetch после мутации; navigate перечитывает». Разработчик сразу знает, где смотреть (кэш/refetch).' },
      { label: 'Подсказка 2', sub: 'evidence', body: 'Скриншот «до navigate / после», запрос профиля в Network — что именно показывает расхождение, а не «приложу скрин».' },
      { label: 'Подсказка 3', sub: 'сверх шаблона', body: 'Callout «риск регрессии: тот же паттерн кэша может быть в корзине» или duplicate-check — это и отличает junior+ от junior.' },
    ],
    selfCheck: [
      'Баг расследован: гипотеза → проверка → вывод (не просто «нашёл»).',
      'Собран конкретный evidence (что доказывает).',
      'Полный баг-репорт оформлен.',
      'Есть абзац рефлексии процесса.',
    ],
    submitNote: 'Расследование + рефлексия — сильный артефакт: показывает мышление, а не только находку.',
    razbor: {
      etalon:
        '<p>Сильное расследование сужает баг до причины: «имя stale после save, navigate чинит → UI не делает refetch после мутации». Evidence показывает delta (до/после navigate). Рефлексия честная: первая гипотеза, время, что добавил (регресс-риск кэша в других местах).</p>',
      mistakes: [
        '«Нашёл баг» без гипотезы и проверки — это находка, а не расследование.',
        'Evidence «приложу скрин» без указания, что он доказывает.',
        'Нет рефлексии — упускается половина ценности упражнения (рост процесса).',
      ],
      qa: [
        { q: 'Зачем QA сужать причину, если чинит разработчик?', a: 'Суженный баг чинится быстрее: «refetch после save» вместо «что-то с профилем». Экономит раунды «не понял / не воспроизвёл».' },
        { q: 'Что изменишь в процессе во второй раз?', a: 'Обычно — раньше формулировать гипотезу (не тыкать наугад) и собирать evidence по ходу, а не вспоминать потом.' },
      ],
    },
  },
  {
    id: 'hw_s07_04',
    sprintId: 'S07',
    code: 'HW-S07-04',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Состояния по порядку · действие QA на каждом · критерий Verified↔Reopened · типичные косяки.' },
    },
    title: 'Проведи баг по жизненному циклу',
    estimateMin: 30,
    priority: 'P2',
    portfolio: {
      artefact: 'Прогон бага по состояниям жизненного цикла с действием QA на каждом',
      shows: 'понимаешь, что происходит с багом после «завёл» — и где роль QA',
    },
    brief:
      '<p>Баг не заканчивается на «завёл тикет». У него жизненный цикл: New → Assigned → In Progress → Resolved → Verified/Reopened → Closed. QA участвует на ключевых переходах. Тут проводишь баг по всем состояниям.</p>',
    buddyAdvice:
      'Reopened — нормальный исход, не провал. Если фикс не прошёл верификацию, ты обязан вернуть тикет, а не «закрыть, чтобы не ругались».',
    given: {
      label: 'Дано · баг из прошлой домашки',
      html: '<p>Возьми любой баг, который оформил (HW-S07-02 или -03). Проведи его по состояниям жизненного цикла своей команды.</p>',
    },
    steps: [
      'Выпиши состояния жизненного цикла бага (New/Open → Assigned → In Progress → Resolved/Fixed → Verified/Reopened → Closed).',
      'Для каждого состояния — одна строка: <strong>что делает QA</strong> (или «ждёт разработчика»).',
      'Опиши развилку Verified ↔ Reopened: по какому критерию решаешь закрыть или вернуть.',
      'Отметь, где живут типичные косяки (закрыли без верификации; reopened без новых данных).',
    ],
    example: {
      head: 'Образец · одно состояние',
      html: '<p>«Resolved (разработчик влил фикс, билд на staging) → QA: воспроизвести исходные шаги бага „Артели“, убедиться, что баг ушёл и рядом ничего не отвалилось (мини-регрессия). Прошло → Closed; нет → Reopened с новыми данными».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'роль QA не везде', body: 'На In Progress QA ждёт; на Resolved — верифицирует; на Reopened — даёт новые данные, почему фикс не прошёл.' },
      { label: 'Подсказка 2', sub: 'критерий Closed', body: 'Исходные шаги больше не воспроизводят баг И рядом ничего не отвалилось (мини-регрессия). Иначе Reopened.' },
      { label: 'Подсказка 3', sub: 'anti-pattern', body: '«Закрыли, потому что разработчик сказал, что починил» — без своей верификации это дыра в процессе.' },
    ],
    selfCheck: [
      'Все основные состояния выписаны по порядку.',
      'У каждого — действие QA (или «ждёт»).',
      'Описан критерий Verified ↔ Reopened.',
      'Отмечены типичные косяки на переходах.',
    ],
    submitNote: 'Понимание lifecycle отличает «нашёл баг» от «довёл баг до закрытия» — то, что ждут от джуна на работе.',
    razbor: {
      etalon:
        '<p>Сильный прогон показывает, что QA владеет переходами: Resolved → верификация → (прошло) Closed / (нет) Reopened с новыми данными. Критерий Closed измерим (исходные шаги не воспроизводят + мини-регрессия). Косяки названы: закрытие без верификации, reopened без объяснения.</p>',
      mistakes: [
        'Состояния есть, но без действия QA — непонятно, где твоя роль.',
        'Closed по слову разработчика, без своей верификации.',
        'Reopened без новых данных — разработчик не поймёт, что не так, тикет пинг-понгует.',
      ],
      qa: [
        { q: 'Почему Reopened — не провал QA?', a: 'Наоборот: вернуть непрошедший фикс — прямая работа QA. Закрыть «чтобы не ругались» — вот провал: баг уедет в прод.' },
        { q: 'Кто двигает тикет в Closed?', a: 'Обычно QA после верификации (или по workflow — разработчик, но с QA-подтверждением). Ключ: Closed без верификации — дыра в процессе.' },
      ],
    },
  },
  {
    id: 'hw_s07_05',
    sprintId: 'S07',
    code: 'HW-S07-05',
    surface: { kind: 'acList', config: { minItems: 3, itemLabel: 'находка' } },
    atelier: {
      screen: 'marketplace',
      sandbox: true,
      intro: 'Магазин в sandbox (баги зашиты в корзине, форме, оформлении, профиле — ходи по разделам). Найди 3 бага разной серьёзности.',
    },
    title: 'Охота: 3 бага разной severity + калибровка',
    estimateMin: 40,
    priority: 'P1',
    portfolio: {
      artefact: '3 найденных бага разной severity с калибровкой Severity и Priority',
      shows: 'находишь баги руками и калибруешь их влияние — связка hunt + триаж',
    },
    brief:
      '<p>Связываем находку и калибровку: ищешь баги руками в песочнице и сразу выставляешь Severity и Priority. Цель — 3 бага РАЗНОЙ серьёзности, чтобы потренировать разброс, а не три одинаковых.</p>',
    buddyAdvice:
      'Не три «корзина глючит». Один критичный (деньги/данные), один средний, один косметический — так тренируется разброс severity.',
    given: {
      label: 'Дано · магазин (песочница)',
      html: '<p>Магазин в панели (sandbox). Ходи по разделам (корзина, форма, оформление, профиль) — в каждом зашиты баги.</p>',
    },
    steps: [
      'Найди <strong>3 бага разной серьёзности</strong> в разных разделах песочницы.',
      'На каждый — строка: что и где · Severity · Priority · одна фраза обоснования (почему именно так).',
      'Проследи разброс: не три Critical и не три Trivial.',
    ],
    example: {
      head: 'Образец · одна находка',
      html: '<p>«Корзина: пустой промокод применяется как валидный (обход купона) · Severity Critical (деньги/абуз) · Priority высокий · обоснование: скидка проходит без реального кода — прямая потеря выручки».</p>',
    },
    hints: [
      { label: 'Подсказка 1', sub: 'где какой', body: 'Отрицательный итог / обход купона — Critical (деньги); сломанный пустой экран — Major (UX-тупик); кривой текст ошибки — Minor.' },
      { label: 'Подсказка 2', sub: 'severity ≠ priority', body: 'Среди находок будет случай, где техника низкая, а бизнес высокий (косметика на видном месте). Отметь его.' },
      { label: 'Подсказка 3', sub: 'не дубли', body: '«× не убирает товар» и «корзина не обновляется» — возможно один баг. Бери три РАЗНЫХ.' },
    ],
    selfCheck: [
      '3 бага из разных разделов, воспроизводимы.',
      'У каждого Severity + Priority + обоснование.',
      'Разброс severity (не все одинаковые).',
      'Нет дублей — три разных бага.',
    ],
    submitNote: 'Hunt + триаж в одном — то, чем QA занимается ежедневно; сильный практический артефакт.',
    razbor: {
      etalon:
        '<p>Сильная охота даёт разброс: Critical (тихая скидка / обход купона — деньги), Major (регресс пустой корзины — UX-тупик), Minor (текст ошибки). У каждого severity по технике, priority по бизнесу; хотя бы раз они расходятся. Три РАЗНЫХ бага, не вариации одного.</p>',
      mistakes: [
        'Три бага одной severity — не потренирован разброс.',
        'Severity и priority совпадают везде — оси не различаются.',
        'Дубли одного бага под разными словами вместо трёх разных находок.',
      ],
      qa: [
        { q: 'Нашёл только косметику — что делать?', a: 'Ходи в денежные/данные разделы (оформление, корзина): там живут Critical/Major. Косметика — на поверхности, серьёзное — на крайних и негативных путях.' },
        { q: 'Severity ставишь ты, а priority — кто?', a: 'Severity — QA (техническая тяжесть). Priority часто согласуется с PM/тимлидом (бизнес). На собесе показывай, что различаешь, кто за что отвечает.' },
      ],
    },
  },
  // ─── S08 · SQL для тестировщиков · встроенная SQL-песочница (sql-playground) ──
  {
    id: 'hw_s08_01',
    sprintId: 'S08',
    code: 'HW-S08-01',
    surface: { kind: 'code', config: { codeLang: 'sql', minChars: 60 } },
    atelier: {
      screen: 'sql-playground',
      intro: 'SQL-песочница ниже — пиши и гоняй все 5 запросов прямо тут, прикладывай результаты.',
    },
    title: 'SQL-разогрев · 5 запросов',
    estimateMin: 35,
    priority: 'P1',
    portfolio: {
      artefact: '5 SQL-запросов (фильтрация, DISTINCT, BETWEEN, NULL trap, LIKE+JOIN) с результатами и интерпретацией',
      shows: 'читаешь и пишешь базовый SQL без шпаргалки — фундамент проверки гипотез о данных',
    },
    brief:
      '<p>SQL — не «знание синтаксиса», а инструмент проверки гипотез о данных. Эти 5 запросов закрепляют базу: фильтрация, сортировка, NULL trap, LIKE. После разминки читаешь чужие запросы и пишешь свои без шпаргалки.</p>',
    buddyAdvice:
      'NULL trap: WHERE x = NULL всегда вернёт 0 строк (NULL не сравнивается через =). Работают только IS NULL / IS NOT NULL.',
    given: {
      label: 'Дано · БД Артели',
      html: '<p>Данные маркетплейса Артель в SQL-песочнице ниже: <code>orders</code> (заказы), <code>users</code> (покупатели), <code>listings</code> (работы мастеров), <code>masters</code> (Кира, Лев, Марина…), <code>reviews</code>. Промокод в данных — <code>ARTEL10</code>. Пиши и запускай запросы прямо тут.</p>',
    },
    example: {
      head: 'Образец · предсказание → запрос → факт (шаг 4)',
      html:
        '<p>Предсказание перед запуском: «промокодом пользуется меньшинство, ~30%».</p>' +
        '<p><code>SELECT COUNT(*) FROM orders WHERE promo_code IS NULL;</code> → 12 из 18 заказов без промокода (67%).</p>' +
        '<p>Предсказание не сошлось — без скидки в Артели покупают чаще, чем казалось. Через <code>promo_code = NULL</code> вернулось бы 0 (NULL не сравнивается через <code>=</code>).</p>',
    },
    steps: [
      '«Какие заказы принесли больше всего денег на этой неделе?» — топ-3 оплаченных заказа за 7 дней (WHERE status=\'paid\' + ORDER BY total DESC LIMIT 3). Перед запуском прикинь порядок сумм.',
      '«Какие вообще статусы бывают у заказов Артели?» — DISTINCT status. Сверь с ожидаемыми (paid / pending / shipped / cancelled / refunded). Есть лишний статус или опечатка? Отметь — это аномалия данных.',
      '«Сколько заказов в среднем сегменте 1000–5000 ₽?» — BETWEEN, сортировка по сумме. Сначала предскажи число строк, потом проверь: совпало?',
      '«Кто покупает без промокода?» — заказы, где promo_code <strong>IS NULL</strong> (не = NULL). Сравни с числом заказов по ARTEL10.',
      '«Клиенты с почтой gmail — для опроса о качестве» — JOIN users + LIKE \'%@gmail.com\'.',
    ],
    formatNote: 'Механика «предскажи → проверь»: перед каждым запросом запиши предсказание (сколько строк / порядок сумм), после — факт и почему разошлось. Это тренирует гипотезу, а не «просто посмотреть».',
    hints: [
      { label: 'Подсказка 1', sub: 'NULL trap', body: 'Только IS NULL / IS NOT NULL. Операторы = и != с NULL не работают — вернут 0 строк.' },
      { label: 'Подсказка 2', sub: 'LIKE wildcards', body: '% — любое число символов, _ — один символ. "%@gmail.com" — заканчивается на @gmail.com.' },
      { label: 'Подсказка 3', sub: '0 строк не всегда баг', body: 'Может быть особенность тестовых данных. Запиши предположение, а не вывод «таких нет».' },
    ],
    selfCheck: [
      '5 запросов прогнаны; у каждого есть предсказание ДО и факт ПОСЛЕ.',
      'Шаг 2: статусы сверены с ожидаемыми, аномалия отмечена (или явно «всё как в доке»).',
      'Шаг 4 — через IS NULL, не = NULL; есть сравнение с ARTEL10.',
      'Шаг 5 — JOIN users + LIKE.',
    ],
    submitNote: 'Базовый SQL — то, что отделяет «верю логам» от «проверил в БД».',
    razbor: {
      etalon:
        '<p>Сильный набор не попадается в NULL trap (IS NULL), правильно использует LIMIT/ORDER и интерпретирует 0 строк как данные, а не панику. JOIN + LIKE — связка двух таблиц по подстроке email.</p>',
      mistakes: [
        'promo_code = NULL вместо IS NULL — 0 строк всегда, ложный вывод «нет таких».',
        'ORDER/LIMIT без понимания, что и как сортируется — мусорный топ.',
        '0 строк трактуется как «баг» без проверки данных.',
      ],
      qa: [
        { q: 'Что вернёт WHERE promo_code != "STUDIO10"? Включит ли NULL?', a: 'NULL не войдёт: сравнение NULL != "..." даёт UNKNOWN, не TRUE. Чтобы включить — добавь OR promo_code IS NULL. Классическая ловушка.' },
        { q: 'Запрос 5 вернул 0 строк — 2 гипотезы?', a: '(1) в тестовых данных нет gmail-адресов; (2) JOIN не тот (LEFT vs INNER) или email в другой таблице. Проверяются двумя быстрыми запросами.' },
      ],
    },
  },
  {
    id: 'hw_s08_02',
    sprintId: 'S08',
    code: 'HW-S08-02',
    surface: { kind: 'code', config: { codeLang: 'sql', minChars: 60 } },
    atelier: {
      screen: 'sql-playground',
      intro: 'SQL-песочница ниже — пиши JOIN и агрегаты прямо тут, прикладывай результаты.',
    },
    title: 'JOIN + GROUP BY · 4 запроса',
    estimateMin: 35,
    priority: 'P1',
    portfolio: {
      artefact: '4 запроса с JOIN + GROUP BY + HAVING + поиск «сирот», с интерпретацией',
      shows: 'отвечаешь «сколько X у каждого Y» по нескольким таблицам без подглядывания',
    },
    brief:
      '<p>Реальные вопросы PM и аналитиков требуют JOIN двух-трёх таблиц и агрегацию. После 4 запросов отвечаешь «сколько X у каждого Y» уверенно, без Stack Overflow.</p>',
    buddyAdvice:
      'COUNT(o.id), не COUNT(*): для пользователя без заказов COUNT(*) даст 1 (NULL-строка считается), COUNT(o.id) — корректный 0.',
    given: {
      label: 'Дано · БД Артели (masters, listings, orders, order_items)',
      html: '<p>Данные маркетплейса Артель в SQL-песочнице: <code>masters</code> (Кира, Лев, Марина…), <code>listings</code> (работы, поле <code>status</code>: active / draft / rejected), <code>orders</code> + <code>order_items</code> (что в заказе), <code>categories</code>. Ты сегодня — аналитик Артели.</p>',
    },
    example: {
      head: 'Образец · запрос + действие (шаг 1)',
      html:
        '<p><code>SELECT m.name, COUNT(l.id) AS active<br>FROM masters m LEFT JOIN listings l ON l.master_id = m.id AND l.status = \'active\'<br>GROUP BY m.id ORDER BY active;</code></p>' +
        '<p>У Марины 0 активных листингов, хотя аккаунт заведён. <strong>Действие:</strong> менеджер пишет ей — помочь выставить первую работу. <code>COUNT(l.id)</code>, а не <code>COUNT(*)</code>, — иначе 0 показался бы как 1.</p>',
    },
    steps: [
      '«Сколько активных листингов у каждого мастера, включая тех, у кого 0?» — masters LEFT JOIN listings (status=\'active\') + COUNT(l.id) + GROUP BY. Кто завёл аккаунт, но ничего не выставил?',
      '«Какая категория приносит больший средний чек?» — AVG по категориям (order_items → listings → category + GROUP BY).',
      '«Каких мастеров поднять в рекомендации?» — мастера с ≥3 оплаченными продажами за 30 дней (WHERE paid и дата ДО group, HAVING COUNT ≥3 после).',
      '«Кого из мастеров поддержать?» — мастера без единой продажи (LEFT JOIN order_items через listings + WHERE oi.id IS NULL).',
    ],
    formatNote: 'Роль аналитика: к каждому запросу — строка «действие»: что менеджер Артели сделает с этой цифрой (поднять в выдаче / написать мастеру / оставить как есть). Цифра без действия — не работа аналитика.',
    hints: [
      { label: 'Подсказка 1', sub: 'HAVING vs WHERE', body: 'WHERE фильтрует строки до агрегации, HAVING — группы после. Обе в WHERE = ошибка или неверный результат.' },
      { label: 'Подсказка 2', sub: 'поиск сирот', body: 'LEFT JOIN + IS NULL = «строки левой таблицы без пары справа». Самый частый паттерн поиска «сирот».' },
      { label: 'Подсказка 3', sub: 'COUNT(o.id) vs COUNT(*)', body: 'Для нулевых групп разница принципиальна: COUNT(*) считает NULL-строку как 1, COUNT(o.id) даёт честный 0.' },
    ],
    selfCheck: [
      '4 запроса прогнаны; к каждому — строка «действие» аналитика.',
      'Шаг 1: COUNT(l.id), не COUNT(*); LEFT JOIN (мастера с 0 видны).',
      'Шаг 3: WHERE (paid, 30 дней) до GROUP BY, HAVING после.',
      'Шаг 4: LEFT JOIN + IS NULL (не NOT IN).',
    ],
    submitNote: 'JOIN + агрегация — ежедневный инструмент QA для проверки данных.',
    razbor: {
      etalon:
        '<p>Сильный набор различает WHERE/HAVING и LEFT/INNER: «сколько у каждого» через LEFT JOIN + COUNT(o.id) даёт честные нули; «сироты» через IS NULL. Интерпретация — что цифра ГОВОРИТ, а не просто результат.</p>',
      mistakes: [
        'COUNT(*) для нулевых групп — пользователь без заказов показывает 1.',
        'HAVING-условие в WHERE (или наоборот) — ошибка или неверный результат.',
        'NOT IN с подзапросом, где есть NULL — вернёт 0 строк; нужен LEFT JOIN + IS NULL.',
      ],
      qa: [
        { q: 'INNER вместо LEFT в запросе 1 — кто пропадёт?', a: 'Пользователи без заказов: INNER оставляет только тех, у кого есть пара. Для «включая 0» нужен LEFT.' },
        { q: 'Запрос 3 через WHERE EXISTS — что читабельнее?', a: 'Для агрегатного порога «≥N» HAVING COUNT обычно яснее; EXISTS — когда важно только наличие. Зависит от задачи.' },
      ],
    },
  },
  {
    id: 'hw_s08_03',
    sprintId: 'S08',
    code: 'HW-S08-03',
    surface: {
      kind: 'freeText',
      config: { minChars: 300, placeholder: 'Гипотезы (2-3) · запросы (полный SQL) · вывод по каждому · финальное действие (баг-репорт или закрытие).' },
    },
    atelier: {
      screen: 'sql-playground',
      intro: 'SQL-песочница ниже. Проверяй гипотезы запросами прямо тут — сначала гипотеза, потом запрос.',
    },
    title: 'SQL-расследование бага по жалобе',
    estimateMin: 40,
    priority: 'P2',
    portfolio: {
      artefact: 'SQL-расследование по жалобе: 2-3 гипотезы → запросы → вывод → действие',
      shows: 'идёшь от жалобы к данным дисциплинированно (гипотеза → запрос), а не «погляжу в таблицу»',
    },
    brief:
      '<p>Половина SQL-работы QA — «прийди в БД и проверь, что реально произошло». Дисциплина: сначала гипотеза, потом запрос. «Погляжу, что в таблице» — другой, исследовательский режим.</p>',
    buddyAdvice:
      'Гипотеза сначала, запрос потом. «Письмо не пришло» → гипотезы: (A) строки в email_queue нет, (B) есть, но job не отправил, (C) заказ не оформился. Каждую — точечным запросом.',
    given: {
      label: 'Дано · жалоба мастера Артели',
      html: '<p>Мастер Лев пишет в поддержку: «заказ на моё кольцо оформлен и оплачен, но в кабинете продаж его нет». Номер заказа известен (любой из песочницы, например 1042). Таблицы: orders, order_items, listings, masters.</p>',
    },
    example: {
      head: 'Образец · одна строка таблицы гипотез',
      html:
        '<p>Гипотеза B: «работа привязана к другому мастеру».</p>' +
        '<p><code>SELECT m.name FROM order_items oi JOIN listings l ON l.id = oi.listing_id JOIN masters m ON m.id = l.master_id WHERE oi.order_id = 1042;</code> → master = Кира.</p>' +
        '<p>Вердикт: подтверждено — кольцо Льва, а listing.master_id указывает на Киру, данные разъехались. Кабинет Льва фильтрует по master_id, потому заказ и не виден. Действие: баг-репорт на привязку работы.</p>',
    },
    steps: [
      'Прочитай жалобу и сформулируй 2-3 КОНКРЕТНЫЕ гипотезы, почему заказ не виден Льву. Например: (A) листинг сменил статус на rejected; (B) listing.master_id указывает на другого мастера; (C) заказ в статусе pending, а кабинет показывает только paid.',
      'Веди таблицу гипотез: гипотеза | запрос | что вернул | вердикт. Первую проверь запросом к order_items + listings по order_id.',
      'Не подтвердилось — следующая строка таблицы, следующая гипотеза. Дисциплина: один запрос проверяет ровно одну гипотезу.',
      'Нашёл причину — зафиксируй, ЧТО именно в данных не так (например, listing.master_id ≠ Лев, хотя работа его).',
      'Финал: баг-репорт (если баг данных/логики) или close-as-not-a-bug (например, заказ реально pending — это ожидание оплаты, а не баг).',
    ],
    formatNote: 'Механика «таблица гипотез»: гипотеза → запрос → результат → вердикт, по строке. Один запрос — одна гипотеза; так расследование не превращается в «поброжу по таблицам».',
    hints: [
      { label: 'Подсказка 1', sub: 'гипотеза конкретна', body: '«Строки email в email_queue для user_id=X не создано», а не «проблема с email». Под конкретную гипотезу пишется точечный запрос.' },
      { label: 'Подсказка 2', sub: 'нет нужной таблицы', body: 'Возьми ближайшую (events / notifications / audit_log) и отметь это в выводе.' },
      { label: 'Подсказка 3', sub: 'не-баг тоже результат', body: 'Если данные показывают, что письмо ушло (sent_at заполнен) — close-as-not-a-bug: проблема не на нашей стороне.' },
    ],
    selfCheck: [
      '2-3 конкретные гипотезы (не «что-то с заказом»).',
      'Таблица гипотез заполнена: гипотеза → запрос → результат → вердикт.',
      'Каждый запрос воспроизводим и проверяет ровно одну гипотезу.',
      'Финальное действие: баг-репорт на разъехавшиеся данные или обоснованное закрытие.',
    ],
    submitNote: 'Расследование через SQL сразу поднимает ценность QA в команде.',
    razbor: {
      etalon:
        '<p>Сильное расследование идёт по гипотезам: запрос к email_queue показывает «строки нет» → баг на стороне постановки в очередь; или «строка есть, sent_at NULL» → job не отработал. Вывод опирается на данные, финал — конкретное действие, а не «наверное, баг».</p>',
      mistakes: [
        '«Погляжу в таблицу» без гипотезы — тонешь в данных.',
        'Гипотезы расплывчатые — под них не написать точечный запрос.',
        'Нет финального действия — расследование без вывода бесполезно.',
      ],
      qa: [
        { q: 'Какую гипотезу подтвердил первой — догадку или вторую?', a: 'Часто не первую: первая («заказ не оформился») отпадает быстрым запросом, реальная причина глубже (job не отправил). В этом ценность дисциплины.' },
        { q: 'Сложнее, если жалоба от 3 клиентов за день?', a: 'Запросы становятся агрегатными (GROUP BY user_id, COUNT отправленных) — ищешь паттерн, а не одну строку. Это уже масштаб инцидента.' },
      ],
    },
  },
  {
    id: 'hw_s08_04',
    sprintId: 'S08',
    code: 'HW-S08-04',
    surface: { kind: 'code', config: { codeLang: 'sql', minChars: 60 } },
    atelier: {
      screen: 'sql-playground',
      intro: 'SQL-песочница ниже. Докажи ловушки на данных (две версии запроса), а не на словах.',
    },
    title: 'NULL и спец-операторы · ловушки данных',
    estimateMin: 30,
    priority: 'P2',
    portfolio: {
      artefact: 'Драйв по NULL / BETWEEN / NOT IN / LIKE-ловушкам + проверки качества данных',
      shows: 'не попадаешься в NULL-ловушки — частый источник ложных выводов QA в SQL',
    },
    brief:
      '<p>NULL и спец-операторы — главный источник ложных выводов в SQL. «0 строк» из-за NULL-ловушки выглядит как «таких нет», а на деле запрос неверен. Отрабатываем ловушки и проверки качества данных.</p>',
    buddyAdvice:
      'Видишь = NULL или != в условии с возможным NULL — стоп. NULL не равен ничему, даже самому себе.',
    given: {
      label: 'Дано · аудит качества данных Артели',
      html: '<p>В данных Артели (listings, users, orders, order_items, reviews) <strong>посажено несколько проблем качества</strong> — их надо найти запросами. Nullable-поля: promo_code, deleted_at, phone. Пиши в SQL-песочнице ниже.</p>',
    },
    example: {
      head: 'Образец · одна пойманная аномалия',
      html:
        '<p><code>SELECT id, title, price FROM listings WHERE price &lt;= 0;</code> → 1 строка: «Свеча Инжир» с price = -350.</p>' +
        '<p>Отрицательная цена — посаженная аномалия: в корзине даст отрицательный подытог, а в отчёте занизит выручку. Действие: баг-репорт + правило валидации <code>price &gt; 0</code> на форме листинга.</p>',
    },
    steps: [
      'NULL-ловушка на реальном поле: покажи разницу WHERE promo_code != "ARTEL10" против (… OR promo_code IS NULL) — сколько заказов теряется из-за NULL.',
      'BETWEEN-границы: проверь на цене листингов, включает ли BETWEEN 1000 AND 5000 обе границы.',
      'NOT IN + NULL: «листинги, не заказанные ни разу» через NOT IN (SELECT listing_id FROM order_items). Если там есть NULL — что вернёт? Почини через LEFT JOIN + IS NULL.',
      'Охота на аномалии: найди минимум 3 посаженные проблемы и подтверди запросом (кандидаты: отрицательная цена листинга, дубли по email в users, отзыв на удалённый листинг, заказ с total ≠ сумме order_items).',
    ],
    formatNote: 'Механика «аудит данных»: каждую найденную аномалию подтверждай запросом (не «на глаз») и предлагай правило валидации, которое бы её не пустило.',
    hints: [
      { label: 'Подсказка 1', sub: 'NULL != X', body: 'Даёт UNKNOWN, не TRUE — строка выпадает из результата. Добавь OR ... IS NULL, чтобы вернуть её.' },
      { label: 'Подсказка 2', sub: 'BETWEEN', body: 'BETWEEN a AND b включает обе границы (≥a AND ≤b). Про верхнюю часто забывают.' },
      { label: 'Подсказка 3', sub: 'NOT IN + NULL', body: 'NOT IN (подзапрос с NULL) → 0 строк всегда. Чини на NOT EXISTS или LEFT JOIN + IS NULL.' },
    ],
    selfCheck: [
      'Показана потеря строк из-за NULL != (две версии запроса с числами).',
      'Проверены границы BETWEEN на цене листингов.',
      'NOT IN-ловушка показана и починена (LEFT JOIN + IS NULL).',
      'Найдено ≥3 посаженные аномалии, каждая подтверждена запросом + предложено правило валидации.',
    ],
    submitNote: 'NULL-грамотность отличает «запрос вернул 0» от «запрос неверен» — экономит ложные баг-репорты.',
    razbor: {
      etalon:
        '<p>Сильная работа доказывает ловушки на данных: «!= "X" вернул 40 строк, с OR IS NULL — 47; 7 потерянных — это NULL». NOT IN с NULL → 0, починено через NOT EXISTS. Качество: отрицательные суммы и дубли email выловлены.</p>',
      mistakes: [
        'Объяснил NULL-ловушку словами, но не показал на запросе разницу.',
        'BETWEEN принят за «строго между» — потеряны границы.',
        'NOT IN оставлен с NULL-подзапросом — тихий 0.',
      ],
      qa: [
        { q: 'Почему NULL = NULL не TRUE?', a: 'NULL — «неизвестно». Два неизвестных не обязательно равны, поэтому NULL = NULL даёт UNKNOWN. Для проверки — IS NULL.' },
        { q: 'Как быстро поймать NULL-ловушку в чужом запросе?', a: 'Ищи != / NOT IN / = по nullable-полям. COUNT до и после OR IS NULL покажет потерю строк.' },
      ],
    },
  },
  {
    id: 'hw_s08_05',
    sprintId: 'S08',
    code: 'HW-S08-05',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Ожидание (какие таблицы/как меняются) · запросы «до» и «после» · сравнение · несоответствия данных.' },
    },
    atelier: {
      screen: 'sql-playground',
      intro: 'SQL-песочница ниже. Делай snapshot до/после и сравнивай с ожиданием — что фича реально пишет в БД.',
    },
    title: 'SQL-приёмка фичи (Gray-box)',
    estimateMin: 40,
    priority: 'P1',
    portfolio: {
      artefact: 'SQL-проверка эффекта фичи: что меняется в данных после действия + чек-лист несоответствий',
      shows: 'подтверждаешь, что фича пишет в БД то, что обещает UI — Gray-box приёмка',
    },
    brief:
      '<p>UI говорит «успех», но что реально произошло в данных? SQL-приёмка проверяет, что фича пишет в БД то, что обещает. Это Gray-box: UI-действие + проверка в данных — там живут «тихие» баги.</p>',
    buddyAdvice:
      'После действия в UI спроси: какие таблицы должны были измениться и как? Потом проверь запросом. Расхождение UI и данных — частый тихий баг.',
    given: {
      label: 'Дано · фича Артели «оформление заказа» + доки',
      html: '<p>По документации Артели после оформления должны измениться: <code>orders +1 (status = paid)</code>, <code>cashback_ledger +1</code>, и <strong>кэшбэк = 5% от суммы заказа</strong>, плюс письмо в <code>email_queue</code>. Оформи заказ в песочнице ниже и проверь данные.</p>',
    },
    example: {
      head: 'Образец · сверка с докой',
      html:
        '<p>Дока: кэшбэк = 5%. Заказ на 2000 ₽ → ожидание <code>cashback_ledger.amount = 100</code>.</p>' +
        '<p>«После»: <code>SELECT amount FROM cashback_ledger WHERE order_id = 1055;</code> → 50.</p>' +
        '<p>Запись есть, но сумма вдвое меньше доки (2.5% вместо 5%). Тихий баг: неверный процент кэшбэка. UI показал «начислено», данные противоречат документации.</p>',
    },
    steps: [
      'Выпиши ожидание ИЗ ДОКИ: какие таблицы и как меняются + конкретное число (кэшбэк = 5% от суммы заказа).',
      'Snapshot «до»: COUNT + ключевые поля для пользователя (orders, cashback_ledger).',
      'Оформи заказ в песочнице, сделай snapshot «после», посчитай дельту.',
      'Сверь дельту с докой построчно: orders +1 (status=paid)? cashback_ledger +1 и amount = 5% от total? email_queue +1? Любое расхождение доки/UI/данных — баг.',
    ],
    formatNote: 'Механика «сверь с докой»: берёшь конкретное число из документации Артели (кэшбэк 5%) и проверяешь, что данные ему соответствуют. Расхождение доки и данных — такой же баг, как падение.',
    hints: [
      { label: 'Подсказка 1', sub: 'ожидание сначала', body: '«orders +1 строка status = paid; cashback_ledger +1, если был кэшбэк». Без ожидания не поймёшь, что не так.' },
      { label: 'Подсказка 2', sub: 'snapshot до/после', body: 'COUNT и ключевые поля до, те же после — дельта. Классика приёмки данных.' },
      { label: 'Подсказка 3', sub: 'тихий баг', body: 'UI «успех», но cashback_ledger пуст — пользователь не получил кэшбэк, хотя видел «начислено». UI это не покажет.' },
    ],
    selfCheck: [
      'Ожидание выписано ИЗ ДОКИ, включая число (кэшбэк 5%).',
      'Есть snapshot «до» и «после» (COUNT + ключевые поля).',
      'Сумма кэшбэка сверена с 5% от total (совпала / не совпала).',
      'Несоответствия доки/UI/данных зафиксированы (или явно «всё сошлось»).',
    ],
    submitNote: 'Gray-box приёмка через SQL ловит «тихие» баги данных, которые UI не показывает.',
    razbor: {
      etalon:
        '<p>Сильная приёмка начинается с ожидания (какие таблицы/как), делает snapshot до/после и ловит дельту: «orders +1 ок, но cashback_ledger не изменился — кэшбэк не начислен, хотя UI сказал начислено». Это Gray-box: UI + данные вместе.</p>',
      mistakes: [
        'Проверка без ожидания — непонятно, верная дельта или нет.',
        'Только «после» без «до» — не видно изменения.',
        'Доверие UI («написал успех — значит ок») без проверки данных — там и живут тихие баги.',
      ],
      qa: [
        { q: 'Почему UI «успех» недостаточно?', a: 'UI показывает, что фронт думает об успехе. Данные показывают, что реально записалось. Между ними — баги (запись не прошла, начисление не создано), которые UI не видит.' },
        { q: 'Что важнее в snapshot — COUNT или конкретные поля?', a: 'Оба: COUNT ловит «не создалось / создалось лишнее», поля (status, amount) — «создалось, но неверно». Вместе — полная дельта.' },
      ],
    },
  },
  // ─── S09 · HTTP & API testing · встроенный API Explorer (api-explorer) ──
  {
    id: 'hw_s09_01',
    sprintId: 'S09',
    code: 'HW-S09-01',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'По запросу: метод+URL · статус · ключевое из тела/заголовков · 1-2 строки интерпретации.' },
    },
    atelier: {
      screen: 'api-explorer',
      intro: 'API Explorer ниже — шли все 5 запросов прямо в нём, фиксируй статус и тело ответа.',
    },
    title: 'Основы HTTP · 5 запросов',
    estimateMin: 35,
    priority: 'P1',
    portfolio: {
      artefact: '5 HTTP-запросов (GET/POST/DELETE) с проверкой статус-кодов, заголовков и формы ответа',
      shows: 'читаешь и пишешь HTTP-запросы без потерь — фундамент API-тестирования',
    },
    brief:
      '<p>HTTP-запросы — фундамент API-тестирования. Эти 5 закрепляют разницу методов и статус-кодов. После них читаешь чужие баг-репорты с curl и пишешь свои так, что разработчик не переспрашивает.</p>',
    buddyAdvice:
      '400 vs 422: 400 — запрос сломан (битый JSON, нет обязательного поля); 422 — формат валиден, но не прошла бизнес-валидация (price < 0). Зафиксируй, какой использует Артель.',
    given: {
      label: 'Дано · API Артели',
      html: '<p>Endpoints Артели: /api/users/me, /api/orders, /api/listings. Шли запросы в API Explorer ниже.</p>',
    },
    example: {
      head: 'Образец · один готовый запрос (шаг 2)',
      html:
        '<p><code>POST /api/orders</code> с телом без обязательного <code>listing_id</code> → <strong>422</strong>.<br>Тело: <code>{ "error": "validation", "fields": ["listing_id"] }</code>.</p>' +
        '<p>JSON структурно валиден, но не прошло бизнес-правило (нет товара) — это 422, а не 400. Ответ называет поле — фронту есть что показать.</p>',
    },
    steps: [
      'GET /api/users/me → предскажи статус ДО отправки, потом сверь. Проверь форму ответа, Content-Type.',
      'POST /api/orders с телом без обязательного listing_id → предскажи: 400 или 422? Отправь, проверь, какой и почему (структура сломана vs бизнес-правило).',
      'DELETE /api/listings/<несуществующий id> → предскажи код, проверь идемпотентность (повтори запрос — тот же результат, это норма).',
      'GET /api/orders без Authorization → предскажи 401/403, проверь, что тело объясняет необходимость авторизации.',
      'POST /api/listings с валидным body → предскажи 201 + Location. Проверь статус, заголовок Location и id в теле.',
    ],
    formatNote: 'Механика «предскажи код»: перед каждым запросом запиши ожидаемый статус, потом сверь с фактом. Расхождение — либо ты не понял контракт, либо API его нарушает; оба — ценная находка.',
    hints: [
      { label: 'Подсказка 1', sub: '400 vs 422', body: '400 — структура сломана; 422 — структура ок, нарушено бизнес-правило.' },
      { label: 'Подсказка 2', sub: '201 vs 200 для POST', body: '201 — создан ресурс (+ заголовок Location); 200 — запрос ок, ресурс не создан (POST /login).' },
      { label: 'Подсказка 3', sub: 'идемпотентность', body: 'Повторный DELETE даёт тот же статус — это нормально, не баг.' },
    ],
    selfCheck: [
      '5 запросов отправлены; у каждого есть предсказанный код и фактический.',
      'Шаг 2: различены 400 (структура) и 422 (бизнес), названо непрошедшее поле.',
      'Шаг 3 — проверена идемпотентность повтором.',
      'Шаг 5 — проверен Location и id в теле.',
    ],
    submitNote: 'HTTP-база — то, без чего любой API-баг-репорт «не воспроизводится».',
    razbor: {
      etalon:
        '<p>Сильный набор различает коды осмысленно: 400 (структура) vs 422 (бизнес), 201 (+Location) vs 200, 401 на отсутствие auth. Идемпотентность DELETE проверена повтором, а не предположена.</p>',
      mistakes: [
        '400 и 422 как синонимы — теряется, где структура, где бизнес.',
        '201 без проверки Location — половина контракта POST не проверена.',
        '«401» принято без чтения тела — а оно должно объяснять, что нужна авторизация.',
      ],
      qa: [
        { q: 'Что Артель даёт на 422 — список полей?', a: 'Хороший API в 422 возвращает, какие поля не прошли (machine-readable). Без списка фронту нечего показать пользователю.' },
        { q: 'Невалидный token vs истёкший — один 401 или разные?', a: 'Часто оба 401, но причина в теле разная (invalid_token vs token_expired). Важно: истёкший лечится refresh, невалидный — повторным логином.' },
      ],
    },
  },
  {
    id: 'hw_s09_02',
    sprintId: 'S09',
    code: 'HW-S09-02',
    surface: { kind: 'code', config: { codeLang: 'bash', minChars: 40 } },
    atelier: {
      screen: 'api-explorer',
      intro: 'API Explorer ниже — собери цепочку авторизации прямо тут (login → токен → защищённый запрос).',
    },
    title: 'Цепочка авторизации + безопасный cURL',
    estimateMin: 35,
    priority: 'P1',
    portfolio: {
      artefact: 'Рабочая цепочка авторизации (login → token → защищённый запрос) + негативный тест + безопасный cURL',
      shows: 'строишь авторизованные запросы и делишься ими без утечки токенов',
    },
    brief:
      '<p>Авторизация — первое, обо что спотыкается API-новичок. Тут собираешь цепочку login → token → защищённый запрос, проверяешь негатив и учишься делиться запросом без утечки токена.</p>',
    buddyAdvice:
      'Реальный токен в shareable curl = доступ ко всему API. Всегда заменяй на плейсхолдер <REDACTED_TOKEN> перед отправкой кому-либо.',
    given: {
      label: 'Дано · API Артели с авторизацией',
      html: '<p>POST /api/auth/login (даёт token), GET /api/users/me (требует Authorization). Работай в API Explorer ниже (или перенеси в Postman).</p>',
    },
    example: {
      head: 'Образец · рабочая цепочка + безопасный cURL',
      html:
        '<p><code>curl -X POST /api/auth/login -d \'{"email":"kira@artel.ru","password":"…"}\'</code> → <code>{ "token": "eyJ…" }</code></p>' +
        '<p><code>curl /api/users/me -H "Authorization: Bearer &lt;REDACTED_TOKEN&gt;"</code> → 200 + мои данные.</p>' +
        '<p>Токен взят из ответа login, а в shareable-curl заменён плейсхолдером — настоящий токен = доступ ко всему API.</p>',
    },
    steps: [
      'Цепочка в API Артели: POST /api/auth/login → возьми token из ответа → подставь в Authorization запроса GET /api/users/me. Должно вернуть 200 с твоими данными.',
      'Негативный тест: тот же запрос с пустым/битым токеном → предскажи и проверь (ожидание 401). Зафиксируй.',
      'Оформи запрос как curl (Copy as cURL в Postman или вручную), <strong>замени реальный токен на &lt;REDACTED_TOKEN&gt;</strong>.',
      'Утечка-чек: пройди curl по 4 типам секретов — токен, email/телефон, адрес доставки, внутренние id — и вычисти каждый перед шерингом.',
    ],
    formatNote: 'Механика «утечка-чек»: прежде чем отдать curl кому-либо, пройди его по списку — токен, персональные данные, адрес, внутренние id. Реальный токен в чате = доступ ко всему API Артели.',
    hints: [
      { label: 'Подсказка 1', sub: 'цепочка в Postman', body: 'Во вкладке Tests login-запроса: pm.environment.set("authToken", pm.response.json().token); дальше {{authToken}} в Authorization.' },
      { label: 'Подсказка 2', sub: 'переменные', body: '{{variable}} работает в URL, headers, body — везде; токен хранится в environment, не в самом запросе.' },
      { label: 'Подсказка 3', sub: 'порядок важен', body: '/users/me ДО login упадёт (пустой токен → 401) — это показывает, что цепочка реальна, а не захардкожена.' },
    ],
    selfCheck: [
      'Цепочка работает: защищённый запрос проходит с токеном из login (200).',
      'Негативный тест с невалидным токеном → 401 (проверено).',
      'cURL оформлен, токен заменён плейсхолдером.',
      'Названо, какие ещё данные скрыть перед шерингом.',
    ],
    submitNote: 'Безопасная работа с токенами отличает аккуратного QA; утечка токена в чате — частый инцидент.',
    razbor: {
      etalon:
        '<p>Сильная цепочка переиспользует токен из login через переменную, а не копипастит руками; негатив (невалидный токен → 401) проверен; в shareable curl токен скрыт. Понимание: токен = ключ, обращаться как с паролем.</p>',
      mistakes: [
        'Токен вставлен в запрос руками — при истечении всё ломается, цепочки нет.',
        'Реальный токен оставлен в curl при шеринге — утечка доступа.',
        'Негатив не проверен — неизвестно, реагирует ли API на плохой токен.',
      ],
      qa: [
        { q: '/users/me ДО login — что будет?', a: 'Пустой {{authToken}} → запрос уйдёт без валидного токена → 401. Нормально и показывает, что защита работает.' },
        { q: 'Что ещё скрыть в curl кроме токена?', a: 'Email, телефон, адрес, внутренние id, ключи в URL — всё, что в shareable артефакте даёт лишний доступ или инфо.' },
      ],
    },
  },
  {
    id: 'hw_s09_03',
    sprintId: 'S09',
    code: 'HW-S09-03',
    surface: {
      kind: 'freeText',
      config: { minChars: 300, placeholder: 'Гипотезы (2-3) · запрос/HAR · воспроизведение (Frequency) · проверка в SQL · 11-полевой баг-репорт с curl+SQL-evidence.' },
    },
    atelier: {
      screen: 'api-explorer',
      intro: 'API Explorer ниже. Воспроизводи и проверяй гипотезы запросами прямо тут — сначала гипотеза, потом запрос.',
    },
    title: 'Расследование API-бага',
    estimateMin: 40,
    priority: 'P2',
    portfolio: {
      artefact: 'Расследование API-бага: гипотезы → запрос/HAR → проверка в SQL → 11-полевой баг-репорт',
      shows: 'не веришь одному ответу API — проверяешь реальное состояние в БД',
    },
    brief:
      '<p>Расследование API-бага — «не верь одному ответу, проверь в данных». Дисциплина: гипотеза → запрос/HAR → воспроизведение → проверка в SQL → баг-репорт. После этого API-баги чинятся с первого захода.</p>',
    buddyAdvice:
      'Критичный flow (платежи/заказы/кэшбэк/auth) — после API-вызова проверь реальное состояние в БД через SQL. Иначе примешь stale cache за баг (или наоборот).',
    given: {
      label: 'Дано · известный API-баг Артели',
      html: '<p>В API Артели есть баг в одном endpoint (например, GET /api/cashback возвращает устаревший баланс кэшбэка). Работай в API Explorer ниже.</p>',
    },
    example: {
      head: 'Образец · гипотеза → запрос → проверка в БД',
      html:
        '<p>Гипотеза: «GET /api/cashback отдаёт значение из кэша, а не из БД».</p>' +
        '<p><code>GET /api/cashback</code> → <code>{ "balance": 500 }</code>. Проверка в SQL: <code>SELECT SUM(amount) FROM cashback_ledger WHERE user_id = 42;</code> → 650.</p>' +
        '<p>API и БД расходятся (500 ≠ 650) → API отдаёт stale-значение. Правило «после API — проверь в БД» сработало.</p>',
    },
    steps: [
      'Гипотезы: 2-3 корневые причины ДО расследования. Например: (A) API отдаёт значение из кэша; (B) фоновое начисление кэшбэка не отработало; (C) процент считается неверно.',
      'Веди таблицу: гипотеза | запрос (API или SQL) | результат | вердикт. Найди подозрительный запрос (DevTools Network / HAR или прямо в API Explorer).',
      'Воспроизведи баг повтором — Frequency цифрой («5 из 5»), а не «иногда».',
      'Правило «API → SQL»: сверь ответ API с реальным состоянием в БД (SELECT … FROM cashback_ledger). Совпало — stale выше (кэш/фронт); разошлось — баг бэкенда.',
      'Баг-репорт по API (11 полей): curl + статус + body + SQL-evidence + Frequency.',
    ],
    formatNote: 'Механика «API → SQL»: не верь одному ответу API — сверь с данными. Ответ мог прийти из кэша; правда — в БД. Совпадение или расхождение сдвигает поиск бага ближе к клиенту или к бэкенду.',
    hints: [
      { label: 'Подсказка 1', sub: 'правило «API → SQL»', body: 'Ответ API может быть из кэша. Реальное состояние — в БД. Расхождение API и БД = баг (или норма TTL — тоже вывод).' },
      { label: 'Подсказка 2', sub: 'HAR', body: 'DevTools Network → Save all as HAR — все запросы/ответы с заголовками, лучший артефакт расследования.' },
      { label: 'Подсказка 3', sub: 'гипотезы заранее', body: '2-3 кандидата ДО расследования, не задним числом — иначе подгонишь объяснение под то, что нашёл.' },
    ],
    selfCheck: [
      '2-3 гипотезы перечислены ДО расследования.',
      'Баг воспроизведён, Frequency подтверждена повтором (цифра).',
      'Проверка в SQL сделана (реальное состояние БД vs ответ API).',
      'Баг-репорт с curl + статусом + body + SQL-evidence.',
    ],
    submitNote: 'API-расследование с SQL-проверкой отличает «API вернул странное» от «вот доказанная причина».',
    razbor: {
      etalon:
        '<p>Сильное расследование сверяет API и БД: «GET /cashback вернул 500, в cashback_ledger сумма 650 → API отдаёт stale значение из кэша». Гипотезы заранее, Frequency цифрой, баг-репорт с curl + SQL-evidence. Правило соблюдено: не доверять одному ответу.</p>',
      mistakes: [
        'Поверил ответу API без проверки в БД — поймал кэш как баг (или пропустил реальный).',
        'Гипотезы задним числом — подгонка под находку.',
        'Frequency «иногда» без повтора — разработчик не поймёт, насколько критично.',
      ],
      qa: [
        { q: 'Какая гипотеза подтвердилась — первая?', a: 'Часто не первая: «бэкенд считает неверно» отпадает, когда SQL показывает верную сумму в БД, а API отдаёт старую → значит кэш/инвалидация.' },
        { q: 'SQL показал баланс = ответу API — где тогда stale?', a: 'Если БД и API совпадают, а пользователь видит старое — stale выше: фронт (кэш UI) или CDN. Сдвигаешь поиск ближе к клиенту.' },
      ],
    },
  },
  {
    id: 'hw_s09_04',
    sprintId: 'S09',
    code: 'HW-S09-04',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'Контракт (поля+типы) · фактический ответ · расхождения по полям/типам/заголовкам · краевые состояния (null/пусто/нет ключа).' },
    },
    atelier: {
      screen: 'api-explorer',
      intro: 'API Explorer ниже. Получи ответ и сверь его форму с контрактом — поля, типы, заголовки.',
    },
    title: 'Контракт ответа · проверка формы',
    estimateMin: 30,
    priority: 'P2',
    portfolio: {
      artefact: 'Проверка контракта ответа: схема/поля/типы/заголовки против документации',
      shows: 'проверяешь, что ответ соответствует контракту, а не «вроде вернул что-то»',
    },
    brief:
      '<p>API-баг часто не «упал», а «вернул не то»: лишнее поле, не тот тип, пропавший заголовок. Контракт-проверка ловит расхождения формы ответа со спекой — то, что фронт ломает молча.</p>',
    buddyAdvice:
      '200 OK не значит «корректно». Проверяй ТЕЛО: те ли поля, те ли типы, нет ли null там, где фронт ждёт строку.',
    given: {
      label: 'Дано · контракт GET /api/users/me (Артель)',
      html: '<p>Ожидаемые поля: id (number), email (string), name (string), createdAt (ISO string). Шли запрос в API Explorer и сверь.</p>',
    },
    example: {
      head: 'Образец · одно расхождение контракта',
      html:
        '<p>Ответ: <code>{ "id": "42", "email": "kira@artel.ru", "passwordHash": "…" }</code> при 200 OK.</p>' +
        '<p>Два бага контракта: <code>id</code> строкой вместо числа (фронт сломается на арифметике) и лишнее поле <code>passwordHash</code> — утечка внутреннего. 200 OK их не отменяет.</p>',
    },
    steps: [
      'Получи ответ GET /api/users/me в API Explorer.',
      'Построй таблицу контракта: поле | ожидаемый тип | факт | вердикт. Пройди каждое поле (id number, email string, name string, createdAt ISO) — тип совпал?',
      'Три «состояния пустоты»: что в поле, если данных нет — null, нет ключа или пустая строка? Зафиксируй, что отдаёт API (фронт обрабатывает их по-разному).',
      'Лишние поля: нет ли в ответе внутреннего (passwordHash, internalFlags)? Лишнее — это утечка. Проверь и заголовок Content-Type. Расхождения — баги контракта даже при 200 OK.',
    ],
    formatNote: 'Механика «таблица контракта»: поле | ожидаемый тип | факт | вердикт, по строке. 200 OK проверяет доставку, таблица — правильность формы. Лишнее поле в ответе — не мелочь, а утечка внутреннего.',
    hints: [
      { label: 'Подсказка 1', sub: 'тип важен', body: 'id как "12" (строка) вместо 12 (число) — фронт сломается на арифметике. Проверяй типы, не только наличие.' },
      { label: 'Подсказка 2', sub: 'три состояния пустоты', body: 'null, отсутствие ключа и пустая строка — разное; фронт обрабатывает по-разному. Зафиксируй, что отдаёт API.' },
      { label: 'Подсказка 3', sub: 'лишнее поле', body: 'Тоже расхождение: может утечь внутреннее (passwordHash, internalFlags). Лишнее в ответе — security-сигнал.' },
    ],
    selfCheck: [
      'Таблица контракта заполнена: поле | ожидаемый тип | факт | вердикт.',
      'Типы проверены (не только наличие полей); проверен Content-Type.',
      'Проверены три состояния пустоты (null / нет ключа / пустая строка).',
      'Найдены лишние/утёкшие поля или явно «лишнего нет»; расхождения зафиксированы.',
    ],
    submitNote: 'Контракт-проверка ловит «тихие» API-баги (не тот тип / лишнее поле), которые 200 OK прячет.',
    razbor: {
      etalon:
        '<p>Сильная проверка идёт по контракту построчно: поля на месте, типы те (id число, createdAt валидный ISO), нет лишних (нет passwordHash). Краевые: что в name, если пусто. 200 OK — не индульгенция, тело проверено.</p>',
      mistakes: [
        '«200 OK — значит ок» без проверки тела — пропущены не тот тип / лишнее поле.',
        'Проверено наличие полей, но не типы — id-строка сломает фронт.',
        'Лишнее чувствительное поле в ответе не замечено — утечка/security.',
      ],
      qa: [
        { q: 'Почему 200 OK недостаточно?', a: '200 говорит «запрос обработан», не «тело корректно». Не тот тип, лишнее поле, null вместо строки — всё это 200, но баги контракта.' },
        { q: 'Кто страдает от не того типа в ответе?', a: 'Фронт: ждёт число — получает строку, падает на арифметике/сравнении. Контракт-баги «тихие» на API, но громкие на клиенте.' },
      ],
    },
  },
  {
    id: 'hw_s09_05',
    sprintId: 'S09',
    code: 'HW-S09-05',
    surface: {
      kind: 'freeText',
      config: { minChars: 250, placeholder: 'По проверке: что подал (нет токена/чужой id/мусор) · что вернул API · это дыра или нет · последствие.' },
    },
    atelier: {
      screen: 'api-explorer',
      intro: 'API Explorer ниже. Целенаправленно ломай запросы (нет токена, чужой id, мусор в параметрах) и смотри, что API отдаёт лишнего.',
    },
    title: 'Негативное и security API-тестирование · охота',
    estimateMin: 40,
    priority: 'P1',
    portfolio: {
      artefact: 'Набор негативных и security-проверок API (auth, доступ к чужому ресурсу, инъекции) с находками',
      shows: 'ломаешь API целенаправленно — там, где happy-path тесты молчат',
    },
    brief:
      '<p>Happy-path тесты API ничего не ловят на безопасности. Негативное и security-тестирование — про «а что если подсунуть чужой id, убрать токен, вставить мусор в параметр». Тут охотишься на такие дыры руками.</p>',
    buddyAdvice:
      'Самая частая дыра API у джунов — IDOR: можно ли через GET /api/orders/<чужой_id> прочитать чужой заказ? Проверь это первым делом.',
    given: {
      label: 'Дано · API Артели',
      html: '<p>Endpoints Артели с авторизацией и параметрами (orders, users, cashback). Атакуй их в API Explorer ниже.</p>',
    },
    example: {
      head: 'Образец · одна находка (IDOR)',
      html:
        '<p><code>GET /api/orders/1007</code> (чужой заказ) с моим токеном → <strong>200</strong> + чужие данные (email, адрес доставки).</p>' +
        '<p>Должно быть 403/404. Это IDOR: API проверил, что я залогинен, но не что заказ мой. Severity высокий — читаются чужие персональные данные.</p>',
    },
    steps: [
      'Составь таблицу атак: атака | запрос | ожидание | факт | дыра?. Заполняй по ходу — это и есть артефакт.',
      'Auth-негативы: без токена / истёкший / чужой токен на /api/orders — какие коды? (ожидание 401/403).',
      'IDOR (проверь первым — топ-дыра): GET /api/orders/<чужой id> с твоим токеном — отдаёт чужие данные? Должно 403/404. «Залогинен» ≠ «владелец».',
      'Мусор/инъекция в параметрах: отрицательный/огромный id, строка вместо числа, OR 1=1, ../../ — вернёт 4xx или упадёт 500 со стэктрейсом (сам по себе security-баг)?',
      'На каждую подтверждённую дыру — запись в духе баг-репорта: severity, шаги, что именно утекло (чужие персональные данные / стэктрейс).',
    ],
    formatNote: 'Механика «таблица атак»: атака | запрос | ожидание | факт | дыра?. Security-тест — не «потыкал вокруг», а список целенаправленных атак с вердиктом по каждой строке.',
    hints: [
      { label: 'Подсказка 1', sub: 'IDOR — топ-дыра', body: 'API проверяет, что ты залогинен, но не что ресурс ТВОЙ. Подмени id в URL на чужой — отдаст ли?' },
      { label: 'Подсказка 2', sub: 'инъекция в параметр', body: 'Мусор вроде OR 1=1 или ../../ — не для взлома, а проверить санитизацию: ждём 4xx, а не 500 со стэктрейсом.' },
      { label: 'Подсказка 3', sub: 'утечка в ошибке', body: '500 со стэктрейсом или SQL-текстом — сам по себе security-баг: раскрывает внутренности системы.' },
    ],
    selfCheck: [
      'Таблица атак заполнена: атака | запрос | ожидание | факт | дыра?.',
      'Проверены auth-негативы (нет токена / истёкший / чужой) и IDOR (чужой заказ).',
      'Подсунут мусор/инъекция в параметры (проверено, что не 500 со стэктрейсом).',
      'На каждую дыру — запись severity + что утекло (или явно «дыр не найдено»).',
    ],
    submitNote: 'Негативное/security API-тестирование сразу выделяет на собесе: большинство джунов тестируют только happy-path.',
    razbor: {
      etalon:
        '<p>Сильная охота бьёт по контролю доступа: IDOR (чужой заказ читается → Critical), отсутствие 401 на пустой токен, 500 со стэктрейсом на мусор. Инъекция проверяет санитизацию (ждём 4xx, не 500). Находки названы как security-баги с последствием.</p>',
      mistakes: [
        'Только happy-path с валидным токеном — безопасность не тронута.',
        'IDOR не проверен — самая частая и дорогая дыра пропущена.',
        '500 со стэктрейсом принят как «просто ошибка» — это утечка внутренностей, security-баг.',
      ],
      qa: [
        { q: 'Почему IDOR опаснее, чем 500?', a: 'IDOR = чтение/изменение чужих данных (утечка, нарушение приватности, иногда деньги). 500 обычно отказ в обслуживании, не утечка. IDOR — прямой доступ к чужому.' },
        { q: 'Инъекция вернула 4xx — это хорошо?', a: 'Да: API распознал мусор и отверг (санитизация работает). Плохо — 500 со стэктрейсом/SQL-ошибкой (нет санитизации, утечка внутренностей).' },
      ],
    },
  },
  // ─── S10 · Postman MASTER + SoapUI (API-инструменты) ─────────────────────────
  {
    id: 'hw_s10_01',
    sprintId: 'S10',
    code: 'HW-S10-01',
    title: 'Коллекция Postman с нуля: 5 запросов + цепочка авторизации',
    estimateMin: 45,
    priority: 'P1',
    portfolio: { artefact: 'Экспорт коллекции Postman (JSON) + скриншот Runner 5/5', shows: 'умение собрать рабочий API-набор с авторизацией и проверками без туториала' },
    surface: { kind: 'freeText', config: { minChars: 60, placeholder: 'Ссылка на экспорт коллекции (или вставь JSON) + что показал Collection Runner (5/5 pass). Коротко: какие 2 проверки самые «защитные» и почему.' } },
    atelier: { screen: 'api-explorer', intro: 'Собери цепочку прямо в API Explorer: получи токен, подставь его в заголовок, дёрни защищённый endpoint. Прочувствуй, как токен «течёт» от логина к остальным запросам.' },
    brief: 'Собрать рабочую коллекцию с авторизацией с нуля — собес-навык джуна. После этого упражнения у тебя есть шаблон, который повторяется за 20 минут на любом проекте без подглядывания.',
    buddyAdvice: 'Не гонись за красотой коллекции. Гонись за тем, чтобы токен подставлялся через переменную, а не руками. Один раз собрал цепочку — дальше это рефлекс.',
    given: { label: 'Дано · API Артели (кэшбэк)', html: 'API Explorer Артели ниже. 5 endpoints вокруг кэшбэка покупателя: логин, баланс, заказы, списание, аудит. Базовый адрес и токен — через переменные окружения.' },
    example: {
      head: 'Образец · одна строгая проверка (не «существует»)',
      html:
        '<p><code>pm.test("balance — число", () =&gt; {<br>&nbsp;&nbsp;pm.expect(pm.response.json().balance).to.be.a("number");<br>});</code></p>' +
        '<p>Строгая проверка поймает, если API Артели вернёт <code>"100"</code> строкой вместо <code>100</code>. Проверка «поле существует» прошла бы даже на <code>null</code> — и регрессия контракта уехала бы в прод молча.</p>',
    },
    steps: [
      'Создай коллекцию «Артель · Кэшбэк» и Environment «Dev» с переменными <code>base_url</code>, <code>auth_token</code> (пока пусто), <code>user_id</code>.',
      'Добавь 5 запросов к API Артели: <code>POST /api/auth/login</code>, <code>GET /api/users/{{user_id}}/cashback</code>, <code>GET /api/orders?user={{user_id}}</code>, <code>POST /api/cashback/redeem</code> (Bearer), <code>GET /api/cashback/audit?user_id={{user_id}}</code>.',
      'В логине сохрани токен: <code>pm.environment.set("auth_token", ...)</code>. Остальные запросы берут <code>{{auth_token}}</code> в Authorization.',
      'У каждого запроса минимум 2 проверки: статус и одна строгая проверка тела (тип поля, не просто «существует»).',
      'Прогони через Collection Runner — 5 из 5 pass. Экспортируй коллекцию в JSON.',
    ],
    formatNote: 'Сдай ссылку на экспорт (или JSON) + скриншот Runner. Никаких захардкоженных URL и токенов — всё через {{переменные}}.',
    hints: [
      { label: 'Подсказка 1', sub: 'проверки слабые', body: 'Строгая проверка ловит регрессию: <code>pm.expect(body.balance).to.be.a("number")</code> поймает, если бэкенд вернёт строку «100» вместо числа. Проверка «поле существует» проходит даже на <code>null</code>.' },
      { label: 'Подсказка 2', sub: 'токен руками', body: 'Если копируешь токен в каждый запрос вручную — ты не собрал цепочку. Токен живёт в переменной окружения и подставляется сам.' },
    ],
    selfCheck: [
      'Коллекция экспортирована, Runner показывает 5 из 5 pass.',
      'Токен сохраняется в логине и подставляется через {{auth_token}}, не вручную.',
      'У каждого запроса есть строгая проверка тела (тип/значение), не только статус.',
      'Нет захардкоженных URL и токенов — всё через переменные.',
    ],
    submitNote: 'Сдаётся ссылка/JSON коллекции + что показал Runner.',
    razbor: {
      etalon: '<p>Рабочая коллекция = переменные + цепочка токена + строгие проверки. Логин кладёт <code>auth_token</code> в Environment, остальные запросы его читают. Проверки бьют по типам полей, а не по факту наличия. Экспорт в JSON делает коллекцию переносимой и пригодной для Newman/CI.</p>',
      mistakes: [
        'Токен скопирован в каждый запрос руками — при смене токена всё рассыпается.',
        'Проверки только на статус 200 — тело может прийти кривым, тест этого не заметит.',
        'base_url захардкожен в URL — коллекцию нельзя переключить на stage/prod.',
        'Проверка «поле существует» на nullable-поле — ложный пропуск, если поле пришло null.',
      ],
      qa: [
        { q: 'Какая проверка самая защитная?', a: 'Та, что бьёт по типу и форме данных: число — число, массив — массив нужной длины. Она ловит молчаливую регрессию контракта, когда статус остался 200, а тело поехало.' },
        { q: 'Что проверить при смене base_url на prod?', a: 'Что все запросы используют {{base_url}}, а не зашитый адрес; что токен валиден для нового окружения; и что данные prod не ломают строгие проверки (другие id, другой объём).' },
      ],
    },
  },
  {
    id: 'hw_s10_02',
    sprintId: 'S10',
    code: 'HW-S10-02',
    title: 'Тот же запрос в SoapUI: инструмент — сменное оборудование',
    estimateMin: 35,
    priority: 'P2',
    portfolio: { artefact: 'Скриншоты SoapUI TestCase (3 проверки pass) + сравнение с Postman + абзац рефлексии', shows: 'владение более чем одним API-инструментом — логика теста не привязана к Postman' },
    surface: { kind: 'freeText', config: { minChars: 60, placeholder: 'Что показал SoapUI (3 проверки pass) · совпал ли ответ с Postman · 1 абзац: что проще/труднее/иначе.' } },
    brief: 'Логика тестирования не Postman-specific. Перенос одного запроса в SoapUI учит видеть инструмент как сменное оборудование. На собесе «у нас SoapUI» — не растеряешься.',
    buddyAdvice: 'Не учи SoapUI целиком. Перенеси один запрос и заметь: проверки те же (статус, JSONPath, SLA), просто кликами вместо кода. Этого достаточно.',
    given: { label: 'Дано · один запрос Артели', html: 'Один критичный запрос из прошлой домашки (например <code>GET /api/cashback</code> Артели) и его ожидаемый ответ.' },
    example: {
      head: 'Образец · та же проверка кликами',
      html:
        '<p>В SoapUI к шагу <code>GET /api/cashback</code> добавлены Assertions: Valid HTTP Status = 200, JSONPath Match <code>$.balance</code> — число, Response SLA 1000 мс.</p>' +
        '<p>Ответ совпал с Postman до байта → проверяется API Артели, а не инструмент. Assertions в SoapUI — это те же <code>pm.test</code>, только кликами.</p>',
    },
    steps: [
      'Поставь SoapUI Open Source, создай REST Project «Артель · Cross-Tool».',
      'Повтори тот же запрос: тот же URL, заголовки, авторизация (через свойства проекта, не вручную в каждом шаге).',
      'Добавь 3 проверки через UI: Valid HTTP Status (200), JSONPath Match (<code>$.balance</code> — число), Response SLA (1000 мс).',
      'Прогони TestCase. Сравни ответ бок о бок с Postman для того же endpoint.',
      'Если ответы различаются — расследуй (не тот env? битый токен? опечатка в URL?) и зафиксируй причину.',
    ],
    formatNote: 'Сдай скриншоты SoapUI (3 проверки pass) + сравнение с Postman + 1 абзац рефлексии.',
    hints: [
      { label: 'Подсказка 1', sub: 'где проверки', body: 'Проверки в SoapUI добавляются к шагу запроса (Assertions). Это GUI-эквивалент <code>pm.test</code>: под капотом те же JsonPath/Regex/XPath.' },
      { label: 'Подсказка 2', sub: 'ответы разошлись', body: 'Разный ответ на «тот же» запрос почти всегда = разный вход: другое окружение, старый токен, лишний/потерянный заголовок. Сверь по одному.' },
    ],
    selfCheck: [
      'SoapUI TestCase прогнан, 3 проверки pass.',
      'Авторизация вынесена в свойства проекта, не вбита в каждый шаг.',
      'Есть сравнение ответа Postman vs SoapUI для одного endpoint.',
      'Абзац рефлексии: что проще, что труднее, что иначе.',
    ],
    razbor: {
      etalon: '<p>Один и тот же запрос в двух инструментах даёт один ответ — потому что тестируется API, а не инструмент. SoapUI делает проверки кликами (Assertions), Postman — кодом (<code>pm.test</code>). Навык переносимый: меняется обвязка, не суть.</p>',
      mistakes: [
        'Авторизация вбита руками в каждый шаг — то же, что хардкод токена в Postman.',
        'Сравнение «на глаз» без фиксации — расхождение есть, но причина не найдена.',
        'Вывод «SoapUI хуже/лучше» без критерия — сравнивать надо под сценарий, не вообще.',
      ],
      qa: [
        { q: 'Чего из сетапа Postman нет в SoapUI (или наоборот)?', a: 'В Postman сценарии и переменные ближе к коду (скрипты, Newman, CI). SoapUI сильнее в SOAP/legacy и GUI-проверках без кода. Это не минус, а разные ниши.' },
        { q: 'Команде из 5 QA — что выбрать?', a: 'Зависит от стека: REST + CI + код-ревью тестов → Postman/Newman; SOAP/legacy + люди без кода → SoapUI. Критерий — сценарии команды, не мода.' },
      ],
    },
  },
  {
    id: 'hw_s10_03',
    sprintId: 'S10',
    code: 'HW-S10-03',
    title: 'AI-ревью своих тестов: что взять, что отбросить и почему',
    estimateMin: 35,
    priority: 'P2',
    skillAxis: 'api',
    portfolio: { artefact: 'Сводка ревью: предложения AI + вердикт по каждому + обновлённый код проверок', shows: 'критичное чтение AI-предложений — AI как второе мнение, не как решение' },
    surface: { kind: 'freeText', config: { minChars: 80, placeholder: 'Сводка в формате: предложение AI → взять/отбросить → почему. Минимум 3 предложения, и не все «взять». Плюс обновлённый код проверок.' } },
    brief: 'AI хорошо генерирует тесты, но без ревью джун рискует принять галлюцинацию. Здесь тренируется критичное чтение: что взять, что отбросить и почему.',
    buddyAdvice: 'AI — это второе мнение, не приговор. Хорошее предложение бери, выдуманное поле отбрасывай с обоснованием. Сбалансированный разбор ценнее, чем «AI сказал — сделала».',
    given: { label: 'Дано', html: 'Код проверок (<code>pm.test</code>) одного запроса Артели из первой домашки (например <code>GET /api/cashback</code>) и форма ожидаемого ответа.' },
    example: {
      head: 'Образец · одно предложение AI → вердикт',
      html:
        '<p>AI предложил: «проверь поле <code>cashback_expires_at</code>». Чек-лист: такого поля в ответе <code>/api/cashback</code> Артели нет → <strong>галлюцинация, отбросить</strong>.</p>' +
        '<p>AI также предложил негатив «redeem больше баланса → 4xx». В реальном контракте это упущено → <strong>взять</strong>. Вот и весь навык: не «AI сказал», а «сверил с контрактом».</p>',
    },
    steps: [
      'Скопируй проверки одного запроса в AI-чат. Промпт: «Проревьюй мои проверки. Endpoint: … Ожидаемый ответ: … Что упущено? Какие крайние случаи?».',
      'Собери минимум 3 предложения AI.',
      'Прогони каждое по чек-листу: endpoint и метод верны? имена полей совпадают с реальным API? проверка не даёт ложный pass? значение реально такое? крайний случай релевантен?',
      'Возьми 1-2 лучших предложения, обнови код проверок.',
      'Отбрось 1-2 с обоснованием «не релевантно, потому что …». Хотя бы одно поймай на галлюцинации (выдуманное поле/endpoint).',
    ],
    formatNote: 'Сдай сводку (предложение → вердикт → почему) + обновлённый код. Не все «взять» и не все «отбросить».',
    hints: [
      { label: 'Подсказка 1', sub: 'как ловить выдумку', body: 'Галлюцинация AI — это поле или путь, которого нет в твоём ответе. Чек-лист (поля совпадают? endpoint есть?) ловит это за секунды.' },
      { label: 'Подсказка 2', sub: 'сколько брать', body: '«Не всё взять» — не квота. Если 3 из 3 реально хороши — возьми все три и так и запиши. Важно показать, что ты проверяла, а не штамповала.' },
    ],
    selfCheck: [
      'В сводке минимум 3 предложения AI с вердиктом по каждому.',
      'Решения сбалансированы — не все «взять» и не все «отбросить».',
      'Хотя бы одно предложение поймано на галлюцинации с пояснением.',
      'Обновлённый код проверок приложен.',
    ],
    razbor: {
      etalon: '<p>AI-ревью = генерация + критичное чтение. AI хорош в крайних случаях (негатив, границы) — частое слепое пятно джуна. Но он выдумывает поля и пути. Чек-лист (endpoint/поля/ложный pass/значения/релевантность) отделяет полезное от галлюцинации. Итог — обновлённый код и явные «почему».</p>',
      mistakes: [
        'Все предложения приняты — ревью не было, был копипаст.',
        'Все отброшены — защитная поза, упущены реально полезные крайние случаи.',
        'Вердикт без причины — «отбросила» без «потому что» не доказывает мышление.',
        'Галлюцинация принята как факт — выдуманное поле уехало в тесты.',
      ],
      qa: [
        { q: 'Какое предложение отбросишь, не доходя до чек-листа?', a: 'То, что противоречит известному контракту: проверка поля, которого в ответе нет, или статуса, который endpoint не отдаёт. Это очевидная выдумка.' },
        { q: 'AI дал 10 крайних случаев — сколько взять?', a: 'Столько, сколько окупает поддержку: критичные негативы и границы — да; экзотику, которой нет в требованиях, — в бэклог. Покрытие против стоимости сопровождения.' },
      ],
    },
  },
  {
    id: 'hw_s10_04',
    sprintId: 'S10',
    code: 'HW-S10-04',
    title: 'Охота на авторизацию: без токена, с битым, с чужим id',
    estimateMin: 40,
    priority: 'P1',
    portfolio: { artefact: 'Список негативных проверок авторизации: запрос → код → вердикт (ок/дыра)', shows: 'умение проверять не happy-path, а защиту API — частый источник дорогих дыр' },
    surface: { kind: 'acList', config: { itemLabel: 'находка', minItems: 4, requirement: 'Защищённый endpoint кэшбэка. Проверь, как он отвечает на «неправильную» авторизацию.' } },
    atelier: { screen: 'api-explorer', intro: 'Прямо в API Explorer дёрни защищённый endpoint четырьмя способами: без токена, с битым токеном, с чужим user_id, с истёкшим токеном. Запиши код ответа на каждый и реши: это защита или дыра.' },
    brief: 'Happy-path с валидным токеном проверяют все. Деньги текут там, где забыли проверить запрос без токена, с чужим id или с мусором вместо токена. Эта охота — про защиту, а не про «работает».',
    buddyAdvice: 'Не ищи 200. Ищи, где должно быть 401/403, а пришло 200 — это и есть дыра. Чужой user_id, который вернул чужой баланс, дороже любого упавшего теста.',
    given: { label: 'Дано · защищённый endpoint Артели', html: 'Защищённый запрос Артели (например <code>GET /api/users/{id}/cashback</code>) и валидный токен покупателя A.' },
    example: {
      head: 'Образец · одна находка (IDOR)',
      html:
        '<p><code>GET /api/users/{id_покупателя_B}/cashback</code> под токеном A → <strong>200</strong> + баланс кэшбэка B.</p>' +
        '<p>Ожидалось 403. Вердикт: <strong>дыра (IDOR)</strong>. Токен A валиден, но сервер не проверил, что ресурс принадлежит A — читается чужой баланс. Это утечка, а не «работает».</p>',
    },
    steps: [
      'Запрос без заголовка Authorization → запиши код. Ожидаемо 401.',
      'Запрос с битым токеном (<code>Bearer xxx</code>) → код. Ожидаемо 401.',
      'Запрос валидным токеном пользователя A, но с <code>user_id</code> пользователя B → код и тело. Если вернулись данные B — это IDOR, дыра.',
      'Запрос с истёкшим/отозванным токеном → код. Ожидаемо 401.',
      'По каждой проверке зафиксируй: запрос → код → вердикт (защита ок / дыра) и почему.',
    ],
    formatNote: 'Каждая строка-находка: что отправил, какой код, защита это или дыра.',
    hints: [
      { label: 'Подсказка 1', sub: 'что значит код', body: '401 = «не аутентифицирован» (нет/битый токен). 403 = «аутентифицирован, но нельзя». 200 на чужой id — почти всегда IDOR.' },
      { label: 'Подсказка 2', sub: 'не вижу дыры', body: 'Если всё отдаёт 401/403 как надо — это валидный результат, так и запиши: «защита держит». Охота не обязана найти дыру, обязана проверить.' },
    ],
    selfCheck: [
      'Проверены все четыре случая: без токена, битый, чужой id, истёкший.',
      'По каждому записан код ответа и вердикт (защита/дыра).',
      'IDOR проверен явно — запрос с чужим user_id под своим токеном.',
      'Вывод опирается на коды, а не на «вроде работает».',
    ],
    razbor: {
      etalon: '<p>Проверка авторизации = матрица «неправильных» входов. Нет токена и битый токен → 401. Доступ к чужому ресурсу под своим токеном → 403 или 404 (но не 200 с чужими данными — это IDOR). Истёкший токен → 401. Дыра — это любое 200 там, где ждали отказ.</p>',
      mistakes: [
        'Проверен только happy-path — защита не тронута.',
        'IDOR не проверен — самая частая и дорогая дыра пропущена.',
        'Код ответа не записан — «ошибка» без кода не отличает 401 от 500.',
        '200 с чужими данными принят как успех — это не успех, это утечка.',
      ],
      qa: [
        { q: 'Чем 401 отличается от 403?', a: '401 — «кто ты?» (нет/битый токен, не аутентифицирован). 403 — «я знаю, кто ты, но тебе нельзя» (аутентифицирован, не авторизован на ресурс).' },
        { q: 'Почему IDOR опаснее упавшего теста?', a: 'Упавший тест ловит регрессию у тебя. IDOR отдаёт чужие данные в проде: утечка, приватность, иногда деньги. Это эксплуатируемая дыра, а не дефект отображения.' },
      ],
    },
  },
  {
    id: 'hw_s10_05',
    sprintId: 'S10',
    code: 'HW-S10-05',
    title: 'Newman: коллекция из терминала и мостик в CI',
    estimateMin: 30,
    priority: 'P2',
    skillAxis: 'api',
    portfolio: { artefact: 'Вывод Newman (pass/fail) + абзац: как этот прогон встаёт в CI-гейт', shows: 'умение запускать API-проверки без GUI — шаг к автоматическому гейту в пайплайне' },
    surface: { kind: 'freeText', config: { minChars: 60, placeholder: 'Что показал Newman (сколько pass/fail) + 1 абзац: на каком шаге пайплайна это запускать и что делать при fail (блокировать мердж?).' } },
    brief: 'Коллекция, которую можно запустить из терминала, — это коллекция, которую можно запустить в CI. Newman превращает твои проверки в автоматический гейт, который ловит регрессию до релиза.',
    buddyAdvice: 'GUI хорош для исследования, терминал — для повторения. Один раз прогнал коллекцию Newman-ом — считай, ты на шаг от того, чтобы это гоняла CI без тебя.',
    given: { label: 'Дано', html: 'Экспортированная коллекция «Артель · Кэшбэк» из первой домашки и установленный Node.js.' },
    example: {
      head: 'Образец · прогон + место в CI',
      html:
        '<p><code>newman run artel-cashback.json -e dev.json</code> → 5/5 pass, exit 0.</p>' +
        '<p>Место в пайплайне: шаг после деплоя на stage. При fail (<code>exit ≠ 0</code>) — <strong>блокировать мердж</strong>, а не только предупреждать. Гейт, который не блокирует, — это уведомление, а не гейт.</p>',
    },
    steps: [
      'Поставь Newman (<code>npm i -g newman</code>) и прогони коллекцию: <code>newman run artel-cashback.json -e dev.json</code>.',
      'Убедись, что вывод показывает те же pass, что и Collection Runner в GUI.',
      'Добавь отчёт: <code>newman run cashback.json -r htmlextra</code> (бонус) — приложи, если получилось.',
      'Опиши абзацем: на каком шаге CI это запускать (после деплоя на stage?) и что делать при fail — блокировать мердж/релиз или только предупреждать.',
    ],
    formatNote: 'Сдай вывод Newman (pass/fail) + абзац про место в пайплайне и реакцию на fail.',
    hints: [
      { label: 'Подсказка 1', sub: 'зачем терминал', body: 'CI не умеет кликать в Postman GUI. Newman — это та же коллекция, но запускаемая командой, которую CI может вызвать сама.' },
      { label: 'Подсказка 2', sub: 'fail в CI', body: 'Newman возвращает ненулевой код выхода при упавшей проверке — этого достаточно, чтобы CI пометил шаг красным и (если настроено) заблокировал мердж.' },
    ],
    selfCheck: [
      'Коллекция прогнана через Newman из терминала, вывод приложен.',
      'Результат совпадает с прогоном в GUI (те же pass).',
      'Описано место прогона в пайплайне (после какого шага).',
      'Описана реакция на fail — блокировать или предупреждать.',
    ],
    razbor: {
      etalon: '<p>Newman = Postman без GUI. Та же коллекция, запуск командой, ненулевой код выхода при провале. Это и есть мост в CI: шаг пайплайна вызывает <code>newman run</code>, красный результат блокирует мердж. Так ручные проверки превращаются в автоматический гейт качества.</p>',
      mistakes: [
        'Прогон есть, но не сверен с GUI — неясно, та ли это коллекция и то ли окружение.',
        'Нет реакции на fail — гейт, который не блокирует, не гейт, а уведомление.',
        'Newman запускают без окружения — переменные пустые, запросы валятся не по делу.',
      ],
      qa: [
        { q: 'Почему Newman, а не Collection Runner для CI?', a: 'Collection Runner живёт в GUI — CI не может его кликнуть. Newman — CLI: его вызывает скрипт пайплайна, а код выхода говорит «прошло/упало».' },
        { q: 'Куда в пайплайне ставить этот прогон?', a: 'После деплоя на тестовую среду и до продвижения дальше: если API-проверки красные — дальше не пускаем. Это дешёвый ранний гейт.' },
      ],
    },
  },
  // ─── S11 · SQL deep + DB (глубже в данные) ───────────────────────────────────
  {
    id: 'hw_s11_01',
    sprintId: 'S11',
    code: 'HW-S11-01',
    title: 'Глубокий SQL: подзапрос, CTE, рекурсия, окно, LAG',
    estimateMin: 40,
    priority: 'P1',
    portfolio: { artefact: '5 продвинутых SQL-запросов с результатами и комментариями', shows: 'умение отвечать на senior-вопросы аналитики — подзапросы, CTE, оконные функции' },
    surface: { kind: 'code', config: { codeLang: 'sql', minChars: 80 } },
    atelier: { screen: 'sql-playground', intro: 'Пиши и гоняй все пять запросов прямо в SQL Playground на demo-БД. Сначала собери правильный результат, потом причёсывай синтаксис.' },
    brief: 'Базовые SELECT/JOIN/GROUP BY закрывают 70% работы, но senior-вопросы требуют подзапросов, CTE и оконных функций. После этих пяти запросов «сколько каждый юзер потратил со временем» перестаёт быть загадкой.',
    buddyAdvice: 'Не пиши сразу красиво. Сначала получи правильный результат хоть как, потом разбей на CTE по шагам. Рекурсию всегда страхуй лимитом глубины.',
    given: { label: 'Дано · БД Артели', html: 'users (покупатели), orders, order_items, listings (работы мастеров), masters, categories (дерево категорий Артели) на demo-БД.' },
    example: {
      head: 'Образец · оконная функция (шаг 4)',
      html:
        '<p><code>SELECT m.name, o.created_at,<br>&nbsp;&nbsp;SUM(oi.price * oi.qty) OVER (PARTITION BY m.id ORDER BY o.created_at) AS running<br>FROM …</code></p>' +
        '<p>Нарастающая выручка Киры по дням. Окно (PARTITION BY master) сохраняет строки и добавляет накопленный итог сбоку — GROUP BY схлопнул бы их в одну.</p>',
    },
    steps: [
      'Подзапрос (correlated): заказы Артели с суммой больше средней по своему месяцу.',
      'Цепочка CTE: топ-3 категории Артели по выручке за 30 дней (3 связанных CTE + оконная функция).',
      'Рекурсивный CTE: обойди дерево категорий Артели от корня, посчитай суммарное число листингов в каждом поддереве. Защитный лимит глубины обязателен.',
      'Оконная функция: нарастающий итог выручки по каждому мастеру во времени (SUM OVER с PARTITION BY master_id + ORDER BY).',
      'LAG: покупатели с разрывом больше 60 дней между двумя последними заказами (кандидаты на реактивацию).',
    ],
    formatNote: 'Сдай 5 запросов + что вернул каждый. Рекурсия — с лимитом глубины; окно — с PARTITION BY и ORDER BY, не голый OVER ().',
    hints: [
      { label: 'Подсказка 1', sub: 'рекурсия зациклилась', body: 'Рекурсивный CTE = якорь (корень) + рекурсивная часть (self-join по родителю). Без условия выхода/лимита глубины он крутится бесконечно. Добавь <code>WHERE depth &lt; 100</code>.' },
      { label: 'Подсказка 2', sub: 'окно или группировка', body: 'GROUP BY схлопывает строки, оконная функция оставляет их и добавляет агрегат сбоку. «Нарастающий итог по каждому юзеру» — это окно: <code>SUM(...) OVER (PARTITION BY user_id ORDER BY date)</code>.' },
    ],
    selfCheck: [
      'Все 5 запросов запускаются на demo-БД и дают осмысленный результат.',
      'Рекурсивный CTE имеет защитный лимит глубины.',
      'Оконная функция использует PARTITION BY и ORDER BY, а не голый OVER ().',
      'Многошаговая логика разбита на CTE, а не свёрнута в один нечитаемый запрос.',
    ],
    razbor: {
      etalon: '<p>Подзапрос отвечает на «относительно чего» (среднее по месяцу). CTE разбивает многошаговую логику на читаемые куски. Рекурсия обходит деревья (категории, оргструктура, ветки комментариев) — всегда с лимитом. Оконные функции считают «по ходу» (нарастающий итог, разрыв через LAG), не схлопывая строки. Это язык аналитических вопросов.</p>',
      mistakes: [
        'Рекурсия без лимита глубины — риск бесконечного цикла на циклической ссылке.',
        'OVER () без PARTITION BY — агрегат по всей таблице вместо «по каждому юзеру».',
        'Вся логика в один запрос с вложенными подзапросами — нечитаемо, чинить невозможно.',
        'GROUP BY там, где нужна оконная функция — теряются строки, на которые опирался вопрос.',
      ],
      qa: [
        { q: 'Где ещё в работе QA пригодится рекурсивный CTE?', a: 'Везде, где дерево: категории товаров, оргструктура, файловая система, ветки комментариев, граф зависимостей. Обход «от корня вниз с накоплением» — типовая задача.' },
        { q: 'Чем correlated-подзапрос платит за гибкость?', a: 'Скоростью: он выполняется построчно для внешнего запроса. Иногда это единственный способ выразить «относительно своей группы», но на больших таблицах его заменяют оконной функцией.' },
      ],
    },
  },
  {
    id: 'hw_s11_02',
    sprintId: 'S11',
    code: 'HW-S11-02',
    title: 'MongoDB и Redis руками: 5 операций + сравнение с SQL',
    estimateMin: 35,
    priority: 'P2',
    portfolio: { artefact: 'Вывод 3 запросов Mongo + 4 команд Redis + абзац сравнения с SQL', shows: 'первое касание NoSQL — на собесе «работала с Mongo?» не вызывает ступора' },
    surface: { kind: 'freeText', config: { minChars: 60, placeholder: 'Вывод 3 запросов Mongo + 4 команд Redis (по типу данных) + 1 абзац: что интуитивнее джуну — SQL или Mongo, и для чего Redis явно лучше Postgres.' } },
    brief: 'Реальные продукты комбинируют PostgreSQL с NoSQL: Mongo — для гибкой схемы документов, Redis — для кэша и очередей. Эти операции дают первое прикосновение, чтобы вопрос про Mongo не застал врасплох.',
    buddyAdvice: 'Не учи Mongo и Redis вглубь. Сделай по несколько операций и пойми НИШУ каждого: Mongo — документы с гибкой схемой, Redis — быстрый key-value под кэш и очереди.',
    given: { label: 'Дано', html: 'Локальный Mongo (или Atlas free) и Redis (или Redis Cloud / docker). Тема — Артель: листинги (гибкая схема под разные категории), профили мастеров, очередь модерации, лидерборд мастеров по продажам.' },
    example: {
      head: 'Образец · Redis под свою нишу',
      html:
        '<p>Лидерборд мастеров Артели на sorted set: <code>ZADD leaderboard 42 "Кира"</code>, <code>ZREVRANGE leaderboard 0 2 WITHSCORES</code> → топ-3.</p>' +
        '<p>В Postgres это ORDER BY + LIMIT с пересчётом; в Redis лидерборд обновляется и читается за миллисекунды. Ниша Redis — не «ещё одна БД», а готовый паттерн (кэш / очередь / лидерборд).</p>',
    },
    steps: [
      'Mongo: коллекция listings, 10 документов (разные категории — у свечи и украшения разный набор полей). 3 запроса: find с проекцией, агрегация $group, find с вложенным фильтром.',
      'Redis: 4 паттерна — string (кэш) GET/SET, hash (профиль) HGETALL, list (очередь) LPUSH/RPOP, sorted set (лидерборд) ZADD/ZRANGE.',
      'Сравнение: возьми один запрос «выбрать имя и цену по категории» и напиши его и в SQL, и в Mongo.',
      'Абзацем: что читабельнее джуну и для какого случая Redis объективно уместнее реляционной БД.',
    ],
    formatNote: 'Сдай вывод 7 операций (3 Mongo + 4 Redis) + сравнение + абзац. Не получилась локальная установка — возьми облачный free или docker и отметь это.',
    hints: [
      { label: 'Подсказка 1', sub: 'redis под что', body: 'У каждого типа Redis своя ниша: string — кэш, hash — профиль, list — очередь задач, sorted set — лидерборд. Это не «ещё одна БД», а набор готовых паттернов.' },
      { label: 'Подсказка 2', sub: 'mongo vs sql', body: 'SQL декларативен и про таблицы; Mongo — про форму JSON-документа. Один и тот же вопрос выглядит по-разному: <code>db.products.find({category: "X"}, {name: 1, _id: 0})</code> против привычного SELECT.' },
    ],
    selfCheck: [
      'Mongo: 3 запроса (проекция, агрегация, вложенный фильтр) с выводом.',
      'Redis: 4 паттерна по разным типам данных с выводом.',
      'Есть один и тот же запрос в SQL и в Mongo бок о бок.',
      'Абзац сравнения: читабельность + ниша Redis.',
    ],
    razbor: {
      etalon: '<p>NoSQL — не «замена SQL», а инструменты под другую форму данных. Mongo хранит документы с гибкой схемой (товар с разным набором полей), Redis держит данные в памяти под низкую задержку (кэш, очередь, лидерборд). Реляционная БД остаётся для связей и транзакций. Зрелый продукт сочетает их, а не выбирает один.</p>',
      mistakes: [
        'Redis воспринят как «ещё одна таблица» — упущены паттерны (кэш/очередь/лидерборд).',
        'Mongo-проекция без отключения _id — в выводе лишнее поле.',
        'Вывод «Mongo лучше/хуже SQL» без сценария — сравнивать надо под форму данных, не вообще.',
      ],
      qa: [
        { q: 'Для чего Redis объективно лучше Postgres?', a: 'Для latency-критичного простого key-value: кэш страницы, счётчики, лимиты, очередь задач, лидерборд. В памяти и без джойнов — миллисекунды.' },
        { q: 'Когда гибкая схема Mongo — это риск?', a: 'Когда данные на самом деле структурны и связаны: «свобода» оборачивается тем, что в одной коллекции документы с разными полями, и тесты/аналитика по ним хрупкие.' },
      ],
    },
  },
  {
    id: 'hw_s11_03',
    sprintId: 'S11',
    code: 'HW-S11-03',
    title: 'Seed-скрипт на Faker с защитой от удара по проду',
    estimateMin: 35,
    priority: 'P3',
    portfolio: { artefact: 'Python seed-скрипт (Faker, multi-locale, транзакция, ENV-guard) + вывод прогона', shows: 'умение наполнять тестовую БД безопасно — защитные привычки, которые не ломают прод' },
    surface: { kind: 'freeText', config: { minChars: 60, placeholder: 'Ссылка на скрипт (или код) + сводка прогона (сколько создано) + скриншот отказа при ENV=production. Коротко: что вызвало бы ROLLBACK.' } },
    brief: 'Скрипт для наполнения тестовой БД — типичный артефакт QA. Главная ловушка — случайно запустить против прода. Здесь тренируются защитные привычки: ENV-guard, транзакция, fail-fast.',
    buddyAdvice: 'Первая строка после импортов — проверка окружения. Не «допишу потом». Скрипт без неё однажды снесёт прод-данные, и это будет твой худший день.',
    given: { label: 'Дано', html: 'Локальный PostgreSQL со схемой Артели (users, masters, listings, orders, order_items с FK).' },
    example: {
      head: 'Образец · защитный ENV-guard (шаг 4)',
      html:
        '<p><code>assert os.environ.get("ENV") in ("dev", "staging"), "seed запрещён вне dev/staging"</code></p>' +
        '<p>Запуск с <code>ENV=production</code> падает на первой строке (AssertionError), ни одна фейковая запись не уходит в боевую БД Артели. Это привычка «безопасность до объёма», а не «допишу потом».</p>',
    },
    steps: [
      'Скрипт на Python с Faker и минимум 4 локалями (Unicode-покрытие из коробки).',
      'Создай данные в порядке FK: 30 users → 10 masters → 60 listings → 100 orders → 200 order_items.',
      'Заверни запись в транзакцию: BEGIN → цикл → проверка количества → COMMIT, иначе ROLLBACK.',
      'Fail-fast guard: <code>assert os.environ.get("ENV") in ("dev", "staging")</code> сразу после импортов.',
      'Лог-сводка в конце: сколько чего создано, pass/fail.',
    ],
    formatNote: 'Сдай скрипт + сводку прогона + скриншот отказа при ENV=production. Порядок вставки уважает FK.',
    hints: [
      { label: 'Подсказка 1', sub: 'порядок вставки', body: 'FK диктует порядок: сначала родители (users, products), потом дети (orders ссылается на user, order_items — на order и product). Иначе нарушение constraint.' },
      { label: 'Подсказка 2', sub: 'зачем транзакция', body: 'Без транзакции упавший на середине скрипт оставит БД полузаполненной. BEGIN/COMMIT делают наполнение «всё или ничего», ROLLBACK откатывает при провале проверки.' },
    ],
    selfCheck: [
      'Скрипт прогнан против локального Postgres, счёт через SELECT совпал со сводкой.',
      'Запись в транзакции: COMMIT после проверки, ROLLBACK при несовпадении.',
      'ENV-guard отказывает запуску при ENV=production (есть демонстрация).',
      'Порядок вставки уважает FK, логи читаемые.',
    ],
    razbor: {
      etalon: '<p>Production-уровня seed = безопасность важнее объёма. ENV-guard (fail-fast по окружению) не пускает скрипт на прод. Транзакция делает наполнение атомарным: проверка количества решает COMMIT или ROLLBACK. Порядок по FK не даёт нарушить связи. Multi-locale Faker заодно ловит Unicode-баги. Это привычка, а не разовая аккуратность.</p>',
      mistakes: [
        'Нет ENV-guard — один неверный конфиг, и скрипт пишет в прод.',
        'Без транзакции — падение на середине оставляет битые данные.',
        'Дети вставляются раньше родителей — нарушение FK, скрипт валится не по делу.',
        'Сводка не сверена с реальным COUNT — «создано 100», а в БД 87.',
      ],
      qa: [
        { q: 'Что было бы без ENV-guard при запуске на прод-кредах?', a: 'Скрипт молча налил бы фейковых users/orders в боевую БД: мусор в аналитике, возможно письма реальным «пользователям», в худшем случае — порча или перезапись данных.' },
        { q: 'Какая проверка должна вызывать ROLLBACK?', a: 'Любая, где факт не сходится с планом: COUNT(orders) ≠ ожидаемому, нарушение FK, исключение в цикле. Если данные не такие, как задумано, — откат, а не COMMIT.' },
      ],
    },
  },
  {
    id: 'hw_s11_04',
    sprintId: 'S11',
    code: 'HW-S11-04',
    title: 'Расследование данными: баланс не сходится',
    estimateMin: 40,
    priority: 'P1',
    portfolio: { artefact: 'Запрос-расследование + найденное расхождение + вывод о причине', shows: 'умение доказать баг данными, а не «кажется» — сверка агрегатов, поиск дублей и пропусков' },
    surface: { kind: 'freeText', config: { minChars: 80, placeholder: 'Запрос(ы), которыми ловила расхождение + что нашла (дубль? пропуск? не та сумма?) + вывод: это баг данных, расчёта или ожидаемо.' } },
    atelier: { screen: 'sql-playground', intro: 'Расследуй прямо в SQL Playground: сравни заявленный баланс с суммой транзакций, найди строки-виновники. Запрос — твой микроскоп.' },
    brief: 'Пользователь жалуется: «кэшбэк-баланс не сходится». «Кажется, баг» — не аргумент. Здесь доказываешь расхождение запросом: сверяешь агрегат с источником, находишь дубль или пропуск.',
    buddyAdvice: 'Не верь полю «баланс». Пересчитай его из транзакций и сравни. Расхождение покажет, кто врёт — поле, расчёт или данные. Это и есть расследование.',
    given: { label: 'Дано · кэшбэк Артели', html: 'Таблицы users (поле balance) и cashback_ledger (начисления/списания кэшбэка) на demo-БД Артели. У одного покупателя баланс не равен сумме транзакций.' },
    example: {
      head: 'Образец · пересчёт истины из источника',
      html:
        '<p><code>SELECT user_id, SUM(amount) AS real_balance FROM cashback_ledger GROUP BY user_id;</code> — сравни с полем <code>users.balance</code>.</p>' +
        '<p>У покупателя Alice: поле balance = 650, SUM(ledger) = 500. Разница 150 — подозреваемый. Разворот строк: дубль начисления по <code>external_id</code>. Поле balance — кэш, верить нельзя; истина — в ledger.</p>',
    },
    steps: [
      'Пересчитай баланс из транзакций (сумма начислений минус списания) по каждому пользователю.',
      'Сравни пересчёт с полем balance — найди пользователей с расхождением.',
      'У виновника копни глубже: дубль транзакции? пропущенное списание? транзакция с NULL-суммой? не та дата?',
      'Сформулируй вывод: это баг данных (дубль/пропуск), баг расчёта (поле обновляется неверно) или ожидаемо (например, pending-транзакции).',
    ],
    formatNote: 'Сдай запросы + найденное расхождение + вывод о причине. Цифры, а не «вроде не сходится».',
    hints: [
      { label: 'Подсказка 1', sub: 'с чего начать', body: 'Сначала агрегат: <code>SUM(amount)</code> по пользователю из транзакций против поля balance. Разница &ne; 0 — список подозреваемых. Потом разворачивай конкретного.' },
      { label: 'Подсказка 2', sub: 'ищу дубль', body: 'Дубль ловится через GROUP BY по «природному ключу» транзакции (например external_id) с HAVING COUNT(*) &gt; 1. NULL в сумме — отдельная проверка: он молча выпадает из SUM.' },
    ],
    selfCheck: [
      'Баланс пересчитан из транзакций и сопоставлен с полем balance.',
      'Найден конкретный пользователь и строка-причина расхождения.',
      'Проверены типовые виновники: дубль, пропуск, NULL-сумма.',
      'Вывод классифицирует: баг данных / баг расчёта / ожидаемо.',
    ],
    razbor: {
      etalon: '<p>Расследование данными = пересчитать истину из источника и сравнить с «витриной». Поле balance — производное, ему нельзя верить на слово. SUM транзакций даёт эталон; разница локализует пользователя; разворот строк находит причину (дубль по external_id, NULL-сумма, пропущенное списание). Итог — не «кажется», а «вот строка и вот цифра».</p>',
      mistakes: [
        'Доверились полю balance — расхождения не видно, потому что не пересчитали.',
        'NULL-сумма не учтена — она выпадает из SUM и прячет дыру.',
        'Нашли разницу, но не развернули причину — «не сходится» без виновника не чинится.',
        'Вывод без классификации — неясно, чинить данные, расчёт или это норма.',
      ],
      qa: [
        { q: 'Почему нельзя верить полю balance напрямую?', a: 'Это денормализованный кэш: его обновляет код. Баг в обновлении, гонка, пропущенное событие — и поле расходится с реальной суммой транзакций. Источник истины — транзакции.' },
        { q: 'Чем дубль опаснее пропуска для баланса?', a: 'Дубль начисления добавляет деньги из воздуха (компания платит лишнее), пропуск — недоплачивает (жалоба пользователя). Оба баги, но дубль чаще «тихий» и дороже.' },
      ],
    },
  },
  {
    id: 'hw_s11_05',
    sprintId: 'S11',
    code: 'HW-S11-05',
    title: 'Почему запрос тормозит: EXPLAIN и индекс',
    estimateMin: 35,
    priority: 'P2',
    portfolio: { artefact: 'План EXPLAIN до/после + добавленный индекс + замер времени', shows: 'умение читать план запроса и ускорять его индексом — мостик к перформанс-тестированию' },
    surface: { kind: 'freeText', config: { minChars: 60, placeholder: 'План EXPLAIN до (что было — seq scan?) + какой индекс добавила + план после + время до/после. Вывод: почему индекс помог.' } },
    atelier: { screen: 'sql-playground', intro: 'Прогони EXPLAIN прямо в SQL Playground: посмотри план медленного запроса, добавь индекс, прогони EXPLAIN снова и сравни. Увидь, как seq scan превращается в index scan.' },
    brief: 'Запрос на большой таблице тормозит. «Добавь индекс» — не магия, а чтение плана. Здесь учишься смотреть EXPLAIN, находить полное сканирование и убирать его индексом. Это вход в перформанс.',
    buddyAdvice: 'Не угадывай индекс. Сначала прочитай EXPLAIN: где seq scan по большой таблице с фильтром — туда и индекс. Потом перечитай план и убедись, что он стал index scan.',
    given: { label: 'Дано · крупная таблица orders Артели', html: 'Demo-БД Артели с крупной таблицей orders. Медленный запрос: фильтр по user_id и диапазону дат без подходящего индекса.' },
    example: {
      head: 'Образец · seq scan → index scan',
      html:
        '<p>EXPLAIN до: <code>Seq Scan on orders (rows=50000)</code> — полный проход по всем заказам Артели.</p>' +
        '<p>Добавила <code>CREATE INDEX idx_orders_user_date ON orders(user_id, created_at);</code>. EXPLAIN после: <code>Index Scan using idx_orders_user_date</code>. Время: 180 мс → 4 мс. Индекс убрал полный проход по фильтруемым колонкам.</p>',
    },
    steps: [
      'Прогони EXPLAIN (или EXPLAIN ANALYZE) на медленном запросе. Найди Seq Scan по большой таблице.',
      'Определи, по каким колонкам идёт фильтр/соединение — кандидаты в индекс.',
      'Создай индекс на этих колонках. Прогони EXPLAIN снова.',
      'Сравни: Seq Scan стал Index Scan? Замерь время до/после.',
      'Сформулируй: почему индекс помог и когда он, наоборот, может навредить (запись, маленькая таблица).',
    ],
    formatNote: 'Сдай план до/после + сам индекс + время до/после + короткий вывод.',
    hints: [
      { label: 'Подсказка 1', sub: 'что искать в плане', body: 'Seq Scan = чтение всей таблицы. На большой таблице с выборочным фильтром это и есть тормоз. Index Scan / Index Only Scan — признак, что индекс заработал.' },
      { label: 'Подсказка 2', sub: 'индекс не помог', body: 'Индекс не используется, если фильтр идёт по функции от колонки, тип не совпадает или таблица мелкая (планировщик считает seq scan дешевле). Сверь колонку индекса с колонкой фильтра.' },
    ],
    selfCheck: [
      'Снят план EXPLAIN до изменения — виден Seq Scan.',
      'Индекс создан по колонкам фильтра/соединения.',
      'План после показывает Index Scan, время измерено до и после.',
      'Есть вывод: почему помог и когда индекс вредит.',
    ],
    razbor: {
      etalon: '<p>Ускорение запроса начинается с плана, а не с догадки. EXPLAIN показывает, КАК БД исполняет запрос: Seq Scan по большой таблице с выборочным фильтром — главный кандидат на индекс по колонке фильтра. После индекса план переходит в Index Scan, время падает. Но индекс не бесплатен: он замедляет запись и бесполезен на мелких таблицах — поэтому решение принимают по плану и замеру, а не «на всякий случай».</p>',
      mistakes: [
        'Индекс добавлен «наугад» без чтения плана — может вообще не использоваться.',
        'Нет замера до/после — «стало быстрее» без цифр недоказуемо.',
        'Индекс по колонке, которой нет в фильтре — план не изменился.',
        'Вывод без обратной стороны — индексы лепят на всё, запись деградирует.',
      ],
      qa: [
        { q: 'Почему «добавить индекс на всё» — плохая идея?', a: 'Каждый индекс надо поддерживать при каждой записи (INSERT/UPDATE/DELETE), он занимает место и замедляет запись. Индексы ставят точечно — под реальные медленные запросы из плана.' },
        { q: 'Как это связано с перформанс-тестированием (S12)?', a: 'Чтение EXPLAIN — это микро-перформанс на уровне запроса. На уровне системы тот же подход: измерить (план/метрику), найти узкое место, убрать, перемерить. Масштаб другой, цикл тот же.' },
      ],
    },
  },
  // ─── S12 · Performance + Security (нефункциональное) ──────────────────────────
  {
    id: 'hw_s12_01',
    sprintId: 'S12',
    code: 'HW-S12-01',
    title: 'Нефункциональный аудит публичной страницы: топ-5 находок',
    estimateMin: 45,
    priority: 'P1',
    portfolio: { artefact: 'Сводный отчёт: топ-5 находок (Performance/A11y/Security/Usability) с категорией, impact и приоритетом', shows: 'умение за час найти и приоритизировать нефункциональные проблемы четырьмя инструментами' },
    surface: { kind: 'freeText', config: { minChars: 100, placeholder: 'Топ-5 находок: категория · что нашла · evidence (Lighthouse score / axe violation / клавиатура / контраст) · 1 строка business impact · приоритет. Ранжируй по impact × likelihood.' } },
    brief: 'Нефункциональный аудит — сильный артефакт джуна на собесе: «за час нашла 5 находок на публичном сайте». Здесь тренируется связка четырёх инструментов (Lighthouse + axe + клавиатура + контраст) и приоритизация.',
    buddyAdvice: 'Не вали в кучу 30 мелочей. Прогони 4 инструмента, собери находки, отранжируй топ-5 по «больно × часто». Отчёт на одну страницу ценнее простыни.',
    given: { label: 'Границы', html: 'Основная цель — витрина Артели (каталог/карточка товара). Можно и любой ПУБЛИЧНЫЙ e-commerce / лендинг. НЕ корпоративный production, НЕ персональные данные, в рамках terms of service сайта. Security-проба (XSS) — только в своём sandbox или где тестирование разрешено.' },
    example: {
      head: 'Образец · одна находка из топ-5',
      html:
        '<p>Категория: <strong>Accessibility</strong>. Нашла: на карточке товара Артели кнопка «В корзину» — контраст 2.9:1 (axe violation, ниже 4.5:1).</p>' +
        '<p>Business impact: слабовидящие не видят главный CTA каталога → теряем заказы. Приоритет: высокий (важный путь × много людей).</p>',
    },
    steps: [
      'Lighthouse (DevTools): score + Web Vitals + топ-3 opportunities. Сохрани HTML-отчёт.',
      'axe DevTools: список violations + скриншот.',
      'Навигация с клавиатуры: пройди Tab по всем интерактивным элементам. Ловушка фокуса, нет skip-link, не виден фокус — это находки.',
      'Контраст: проверь 5 подозрительных элементов (серый текст, кнопки).',
      'Собери отчёт на 1 страницу: топ-5 находок, у каждой категория (Performance/Accessibility/Security/Usability/Other), evidence, 1 строка impact, приоритет.',
    ],
    formatNote: 'Сдай отчёт + evidence (HTML Lighthouse, скриншоты). Приоритизация явная, не несортированный список.',
    hints: [
      { label: 'Подсказка 1', sub: 'что считать находкой', body: 'Lighthouse score &lt; 90 — есть что чинить. axe violation — почти всегда реальная a11y-проблема. Ловушка клавиатуры (нельзя выйти Tab-ом) — серьёзно: часть пользователей не сможет уйти со страницы.' },
      { label: 'Подсказка 2', sub: 'как приоритизировать', body: 'impact × likelihood: «контраст кнопки оплаты» (много людей, важный путь) выше, чем «alt у декоративной иконки в подвале». Бизнес-строка делает приоритет очевидным.' },
    ],
    selfCheck: [
      'Сделаны 4 обязательных проверки (Lighthouse, axe, клавиатура, контраст), evidence приложен.',
      'Топ-5 находок отранжированы по impact × likelihood, не просто список.',
      'У каждой находки есть категория и 1 строка business impact.',
      'Отчёт укладывается в одну страницу.',
    ],
    razbor: {
      etalon: '<p>Нефункциональный аудит = широкий охват + приоритизация. Четыре инструмента дают разные срезы: Lighthouse — производительность и базовая a11y/SEO, axe — доступность глубже, клавиатура — реальная навигация без мыши, контраст — читаемость. Сила не в количестве находок, а в топ-5, отранжированных по влиянию на бизнес. Это то, что команда сайта реально починит.</p>',
      mistakes: [
        'Свалка из 30 мелочей без приоритета — команда не знает, за что хвататься.',
        'Только Lighthouse — пропущены клавиатура и контраст, которые автотул не ловит.',
        'Находка без business impact — «контраст низкий» не продаёт починку, «кнопку оплаты не видно слабовидящим» продаёт.',
        'XSS-проба на сайте без разрешения — выход за границы, так нельзя.',
      ],
      qa: [
        { q: 'Какие 2 находки команда реально починит?', a: 'Те, что дёшевы и заметны: контраст ключевой кнопки, отсутствующий label у поля. Дорогую переархитектуру производительности отложат, мелкий a11y-фикс сделают быстро.' },
        { q: 'Почему клавиатуру нельзя заменить автотулом?', a: 'axe ловит около 85% и не чувствует «застрял в модалке» или «фокус невидим». Ловушки фокуса и логику перехода видит только живая навигация Tab-ом.' },
      ],
    },
  },
  {
    id: 'hw_s12_02',
    sprintId: 'S12',
    code: 'HW-S12-02',
    title: 'Матрица доступов: роли × endpoints + ручная проверка',
    estimateMin: 35,
    priority: 'P1',
    skillAxis: 'non-functional',
    portfolio: { artefact: 'Матрица доступов (роли × endpoints) + 3 проверенные ячейки + находки', shows: 'умение системно проверять авторизацию — broken authz и IDOR из топа OWASP' },
    surface: { kind: 'table', config: { tableColumns: ['Роль', 'Endpoint', 'Ожидал (код)', 'Факт / вывод'], minRows: 3 } },
    atelier: { screen: 'api-explorer', intro: 'Проверяй ячейки прямо в API Explorer: дёрни endpoint под разными ролями (гость / пользователь / чужой пользователь) и сверь фактический код с ожидаемым (200/401/403/404).' },
    brief: 'Broken authz и IDOR — лидеры OWASP Top 10. Матрица «роли × endpoints» превращает «проверь безопасность» в системную таблицу, где каждая ячейка — ожидаемый ответ. После этого у тебя готовый артефакт и опыт ручной проверки.',
    buddyAdvice: 'Не тыкай наугад. Сначала таблица: роли по строкам, endpoints по столбцам, в ячейке — ожидаемый код. Потом проверь самые подозрительные ячейки и сравни факт с ожиданием.',
    given: { label: 'Дано · роли и endpoints Артели', html: 'Роли Артели: гость, покупатель, мастер, менеджер. Endpoints: /api/listings, /api/orders, /api/orders/{id}, /api/cashback, /api/moderation. Проверяй в API Explorer ниже.' },
    example: {
      head: 'Образец · одна проверенная ячейка',
      html:
        '<p>Роль: <strong>покупатель</strong>. Endpoint: <code>GET /api/moderation</code> (панель менеджера). Ожидал: 403. Факт: <strong>200</strong> + очередь модерации.</p>' +
        '<p>Вывод: дыра — vertical privilege escalation, покупатель видит инструменты менеджера Артели. Severity high, черновик баг-репорта.</p>',
    },
    steps: [
      'Выпиши 4 роли Артели (гость, покупатель, мастер, менеджер) и минимум 5 endpoints.',
      'Построй матрицу роли × endpoints, в каждой ячейке — ожидаемый код (200/401/403/404). Например: гость → /api/moderation = 401; покупатель → чужой /api/orders/{id} = 403.',
      'Проверь руками минимум 3 ячейки: дёрни endpoint под ролью, сравни факт с ожиданием.',
      'Проверь оба направления: horizontal (покупатель к чужому заказу — IDOR) и vertical (покупатель к /api/moderation — эскалация прав).',
      'Нашла дыру — черновик баг-репорта: severity, impact, шаги воспроизведения, предлагаемый фикс.',
    ],
    formatNote: 'Строка таблицы = проверенная ячейка: роль · endpoint · ожидал (код) · факт/вывод (ок или дыра).',
    hints: [
      { label: 'Подсказка 1', sub: 'какой код ждать', body: '200 — разрешено; 401 — нет авторизации; 403 — авторизован, но нельзя; 404 — существование скрыто от неавторизованного. 404 против 403 — осознанный выбор: 404 не подтверждает, что ресурс есть.' },
      { label: 'Подсказка 2', sub: 'horizontal vs vertical', body: 'Horizontal: та же роль, другой пользователь (A видит данные B) — это IDOR. Vertical: повышение прав (обычный пользователь открывает админ-страницу). Проверяй оба направления.' },
    ],
    selfCheck: [
      'Матрица полная: все ячейки (роли × endpoints) с ожидаемым ответом.',
      'Минимум 3 ячейки проверены руками, факт сравнён с ожиданием.',
      'IDOR проверен явно — доступ к чужому ресурсу под своей ролью.',
      'На найденную дыру — черновик баг-репорта с severity и шагами.',
    ],
    razbor: {
      etalon: '<p>Матрица доступов делает проверку авторизации системной, а не случайной. Роли × endpoints = карта ожиданий; каждая ячейка — контракт (кому какой код). Ручная проверка сверяет реальность с картой. Дыра — любое расхождение в сторону «больше доступа, чем положено»: 200 вместо 403, чужие данные под своим токеном (IDOR). Артефакт переиспользуется при каждом релизе.</p>',
      mistakes: [
        'Проверка наугад без матрицы — нет системы, дыры между «тыками» пропущены.',
        'IDOR не проверен — самая частая authz-дыра осталась в слепой зоне.',
        'Только vertical (админка) — horizontal (чужой пользователь) забыт.',
        'Дыра найдена, но без баг-репорта — разработчик не поймёт severity и как воспроизвести.',
      ],
      qa: [
        { q: 'Какие endpoints вероятнее имеют сломанную authz?', a: 'Те, что принимают id ресурса в URL/параметре (профиль, заказ, документ): легко подменить чужой id. И «свежие» endpoints, добавленные после основной модели прав.' },
        { q: 'Добавили роль moderator — насколько вырастет матрица?', a: 'На целую строку: плюс одна роль на число endpoints ячеек. Поэтому матрицу держат как живой артефакт — новая роль или endpoint сразу добавляет ячейки для проверки.' },
      ],
    },
  },
  {
    id: 'hw_s12_03',
    sprintId: 'S12',
    code: 'HW-S12-03',
    title: 'AI в двух режимах: генерация и интерпретация, с ревью',
    estimateMin: 30,
    priority: 'P2',
    skillAxis: 'non-functional',
    portfolio: { artefact: '6 промптов (3 генерация + 3 интерпретация) с заметками ревью + сбалансированная сводка', shows: 'осознанное применение AI в QA — где ускоряет, где галлюцинирует' },
    surface: { kind: 'freeText', config: { minChars: 80, placeholder: 'Для каждого из 6 промптов: ответ AI + 1-2 заметки (сильная сторона / галлюцинация). Сводка: сильные стороны AI · пробелы · где применять, где нет.' } },
    brief: 'AI в QA полезен в двух режимах: генерация (идеи тестов, payloads, матрицы) и интерпретация (Lighthouse-отчёт, waterfall). Здесь тренируется оба с критичным ревью каждого ответа — где AI ускоряет, где выдумывает.',
    buddyAdvice: 'AI — усилитель, не оракул. На фаззинге (XSS, инъекции) он быстр и широк, на твоём домене — самоуверенно ошибается. Лови по одной галлюцинации и по одной сильной стороне в каждом ответе.',
    given: { label: 'Дано', html: 'Доступ к AI-чату (Claude/ChatGPT) и материалы прошлых домашек S12 по Артели (находки аудита витрины, матрица доступов ролей Артели, Lighthouse-отчёт).' },
    example: {
      head: 'Образец · один промпт → ревью',
      html:
        '<p><strong>Генерация:</strong> «дай XSS-payloads для поля отзыва Артели» → 8 вариантов, широко (сильная сторона AI).</p>' +
        '<p><strong>Интерпретация:</strong> «разбери Lighthouse Артели» → предложил «включить CDN для <code>/api/cashback</code>», но это API, а не статика — <strong>галлюцинация</strong> (перепутал контекст). Вердикт: фаззинг взять, CDN-совет отбросить.</p>',
    },
    steps: [
      'Секция A (генерация), 3 промпта: набор XSS-payload, скелет матрицы доступов, список нарушений API-контракта. Сохрани ответы.',
      'Секция B (интерпретация), 3 промпта: разбор Lighthouse-отчёта, анализ waterfall загрузки, рекомендации по оптимизации. Сохрани ответы.',
      'К каждому ответу применяй чек-лист: отметь 1-2 сильные стороны и/или галлюцинации (выдуманный endpoint/поле/уязвимость).',
      'Собери сводку: сильные стороны AI, его пробелы, где в твоём процессе он помогает, а где нет.',
    ],
    formatNote: 'Сдай 6 ответов + заметки ревью + сводку. Хотя бы одна галлюцинация и одна сильная сторона зафиксированы.',
    hints: [
      { label: 'Подсказка 1', sub: 'где AI силён', body: 'Генерация тривиального фаззинга (XSS, SQL-инъекции, матрицы) — AI быстр и широк, часто шире, чем джун вспомнит сам. Это его честная сильная сторона.' },
      { label: 'Подсказка 2', sub: 'где AI врёт', body: 'Интерпретация без контекста твоего продукта: AI уверенно трактует waterfall, но не знает, что «медленный» запрос у вас кэшируется. И выдумывает поля/endpoints, которых нет.' },
    ],
    selfCheck: [
      'Сделаны 6 промптов (3 генерация + 3 интерпретация), ответы сохранены.',
      'Поймана и записана хотя бы одна галлюцинация.',
      'Отмечена хотя бы одна реальная сильная сторона AI.',
      'Сводка сбалансирована — не «AI всё верно» и не «всё неверно», конкретные наблюдения.',
    ],
    razbor: {
      etalon: '<p>AI в QA = два режима с разной надёжностью. Генерация (payloads, матрицы, идеи) — сильная сторона: быстро и широко, человек проверяет. Интерпретация (отчёты, метрики) — осторожно: AI самоуверен и без контекста продукта. Критичное ревью каждого ответа (сильная сторона / галлюцинация) превращает AI из «решения» во «второе мнение». Сводка фиксирует, где он реально окупается.</p>',
      mistakes: [
        'Все ответы приняты — ревью не было, в тесты уехала галлюцинация.',
        'Все отброшены — упущена реальная скорость AI на фаззинге и черновиках.',
        'Сводка-лозунг («AI крутой/бесполезный») без конкретных наблюдений.',
        'Не отмечено, ГДЕ применять — без этого упражнение не меняет рабочий процесс.',
      ],
      qa: [
        { q: 'Какой промпт дал самый полезный ответ и почему?', a: 'Обычно — узкий и контекстный: «дай XSS-payload для поля поиска, которое экранирует &lt; и &gt;». Конкретика на входе = конкретика на выходе, меньше простора для выдумки.' },
        { q: 'Когда отказаться от AI и сделать руками?', a: 'Когда решение зависит от знания твоего продукта/прода (это норма или баг? кэшируется ли?) или когда цена ошибки высока. Там AI — подсказка, а решение за человеком.' },
      ],
    },
  },
  {
    id: 'hw_s12_04',
    sprintId: 'S12',
    code: 'HW-S12-04',
    title: 'Перформанс: базовый замер и деградация под нагрузкой',
    estimateMin: 40,
    priority: 'P1',
    portfolio: { artefact: 'Замер времени отклика: baseline (1 запрос) vs под нагрузкой (N параллельных) + вывод о точке деградации', shows: 'умение измерять производительность, а не «кажется быстро» — baseline, нагрузка, узкое место' },
    surface: { kind: 'freeText', config: { minChars: 80, placeholder: 'Endpoint + baseline (время 1 запроса) + результат под нагрузкой (N параллельных: среднее/95-й перцентиль, ошибки) + вывод: где начинает деградировать и что вероятно узкое место.' } },
    brief: '«Быстро» без числа — не результат. Перформанс начинается с baseline: сколько endpoint отвечает на 1 запрос и как держится под N параллельных. Здесь учишься мерить деградацию, а не угадывать.',
    buddyAdvice: 'Сначала baseline на один запрос — это твой ноль. Потом дай нагрузку и смотри не на среднее, а на 95-й перцентиль и ошибки: именно хвост портит жизнь пользователям.',
    given: { label: 'Дано', html: 'Endpoint Артели из sandbox (или твоя коллекция «Артель · Кэшбэк» из S10) и инструмент нагрузки на выбор: k6, JMeter, ab или Newman с итерациями. НЕ нагружай чужой production без разрешения.' },
    example: {
      head: 'Образец · baseline → деградация',
      html:
        '<p>Endpoint <code>GET /api/listings</code> Артели. Baseline (1 запрос): 40 мс. Нагрузка 50 параллельных: среднее 120 мс, <strong>95-й перцентиль 900 мс</strong>, 2% ошибок.</p>' +
        '<p>Точка деградации ≈ 30 параллельных. Гипотеза узкого места: выборка листингов без индекса по category (тот же приём, что в EXPLAIN, S11).</p>',
    },
    steps: [
      'Baseline: замерь время отклика на 1 запрос несколько раз, возьми типичное значение.',
      'Нагрузка: дай N параллельных запросов (начни с малого, например 10, потом 50). Запиши среднее, 95-й перцентиль, долю ошибок.',
      'Найди точку деградации: при каком N время/ошибки резко растут.',
      'Сформулируй гипотезу узкого места: БД без индекса? нет кэша? лимит соединений? Свяжи с тем, что видела в EXPLAIN (S11).',
    ],
    formatNote: 'Сдай baseline + результаты под нагрузкой (с перцентилем и ошибками) + вывод. Только разрешённые для нагрузки цели.',
    hints: [
      { label: 'Подсказка 1', sub: 'среднее врёт', body: 'Среднее прячет хвост. 95-й перцентиль говорит «каждый 20-й запрос медленнее этого» — именно его чувствуют пользователи. Смотри на перцентиль и долю ошибок, не только на average.' },
      { label: 'Подсказка 2', sub: 'без разрешения нельзя', body: 'Нагрузочный тест чужого production — это фактически мини-DDoS. Бери sandbox, свой endpoint или явно разрешённую цель. Это вопрос этики и закона, не только вежливости.' },
    ],
    selfCheck: [
      'Снят baseline на одиночный запрос.',
      'Проведена нагрузка с несколькими уровнями N, записаны перцентиль и ошибки.',
      'Найдена точка, где начинается деградация.',
      'Есть гипотеза узкого места, цель нагрузки была разрешённой.',
    ],
    razbor: {
      etalon: '<p>Перформанс = измеримая величина с условиями. Baseline (один запрос) — точка отсчёта. Нагрузка показывает, как система держит параллелизм: смотрят 95-й перцентиль и ошибки, не среднее. Точка деградации (где хвост и ошибки взлетают) указывает на узкое место — обычно БД без индекса, отсутствие кэша или лимит соединений. Тот же цикл, что в EXPLAIN, только на уровне системы: измерить, найти бутылочное горло, объяснить.</p>',
      mistakes: [
        'Только среднее — деградация в хвосте не видна, вывод ложно-оптимистичный.',
        'Один уровень нагрузки — нет точки деградации, непонятно, где ломается.',
        'Нагрузили чужой production без разрешения — это атака, а не тест.',
        'Числа без гипотезы — «медленно» зафиксировано, но причина не названа.',
      ],
      qa: [
        { q: 'Почему 95-й перцентиль важнее среднего?', a: 'Среднее сглаживает выбросы: при average 200 мс каждый 20-й запрос может быть 2 секунды. Пользователь чувствует именно эти медленные хвосты, поэтому SLA пишут на перцентиле.' },
        { q: 'Как это связано с EXPLAIN из S11?', a: 'Под нагрузкой первым обычно сыпется БД. Медленный запрос с Seq Scan, терпимый в baseline, под параллелизмом становится узким местом. Перформанс системы часто чинится индексом на уровне запроса.' },
      ],
    },
  },
  {
    id: 'hw_s12_05',
    sprintId: 'S12',
    code: 'HW-S12-05',
    title: 'Security-смоук: HTTPS, заголовки и санитизация поля',
    estimateMin: 35,
    priority: 'P2',
    skillAxis: 'non-functional',
    portfolio: { artefact: 'Чек-лист базовой гигиены безопасности: HTTPS, security-заголовки, реакция поля на XSS-пробу', shows: 'умение за 15 минут снять базовый security-смоук — гигиена, которую проверяют первой' },
    surface: { kind: 'acList', config: { itemLabel: 'проверка', minItems: 4, requirement: 'Публичная страница (или твой sandbox). Сними базовую security-гигиену — не эксплойт, а проверка «защита вообще есть?».' } },
    atelier: { screen: 'api-explorer', intro: 'Открой ответ endpoint в API Explorer и посмотри заголовки: есть ли HSTS, Content-Security-Policy, X-Frame-Options. Заголовки ответа — первая страница security-смоука.' },
    brief: 'Security-смоук — это не взлом, а гигиена: есть ли HTTPS, есть ли защитные заголовки, санитизируется ли ввод. За 15 минут видно, заботились ли о базовой защите. Это первое, что проверяют.',
    buddyAdvice: 'Не ищи уязвимость для эксплойта. Проверь, есть ли защита вообще: HTTPS, заголовки, экранирование ввода. Отсутствие базовой гигиены — уже находка, и без всякого хакинга.',
    given: { label: 'Границы', html: 'Витрина Артели или твой sandbox. XSS-проба — только в поле поиска/отзыва в рамках правил ИЛИ в личном sandbox. НЕ эксплуатируй, только проверь реакцию.' },
    example: {
      head: 'Образец · одна проверка',
      html:
        '<p>Проверка: security-заголовки ответа Артели. Факт: нет <code>Content-Security-Policy</code>. Вердикт: <strong>находка</strong>.</p>' +
        '<p>Почему важно: без CSP любая будущая XSS в поле отзыва сможет загрузить чужой скрипт — слой защиты в глубину отсутствует. Валидная находка без всякого хакинга.</p>',
    },
    steps: [
      'HTTPS: открывается ли сайт по https, нет ли смешанного контента (http-ресурсы на https-странице).',
      'Заголовки ответа: посмотри в API Explorer/DevTools — есть ли <code>Strict-Transport-Security</code>, <code>Content-Security-Policy</code>, <code>X-Frame-Options</code>.',
      'Санитизация: введи безобидную пробу <code>&lt;b&gt;test&lt;/b&gt;</code> в поле поиска. Экранируется как текст или интерпретируется как разметка?',
      'Зафиксируй по каждой проверке: есть/нет → ок/находка → почему важно.',
    ],
    formatNote: 'Каждая строка-проверка: что проверяла, результат, это гигиена ок или находка. Без эксплуатации.',
    hints: [
      { label: 'Подсказка 1', sub: 'зачем заголовки', body: 'HSTS заставляет браузер ходить только по HTTPS; CSP ограничивает, откуда грузятся скрипты (защита от XSS); X-Frame-Options не даёт встроить сайт в iframe (защита от clickjacking). Их отсутствие — реальная находка.' },
      { label: 'Подсказка 2', sub: 'проба, не эксплойт', body: 'Цель — увидеть, экранирует ли поле ввод, а не «сломать» сайт. <code>&lt;b&gt;test&lt;/b&gt;</code>, отрисованный жирным, — сигнал слабой санитизации. Дальше эксплуатировать не нужно и нельзя.' },
    ],
    selfCheck: [
      'Проверены HTTPS и смешанный контент.',
      'Проверены ключевые security-заголовки (HSTS/CSP/X-Frame-Options).',
      'Сделана безобидная проба санитизации поля, результат записан.',
      'По каждой проверке вердикт «гигиена ок / находка» с пояснением важности.',
    ],
    razbor: {
      etalon: '<p>Security-смоук = базовая гигиена без эксплуатации. HTTPS и отсутствие смешанного контента — транспорт защищён. Заголовки (HSTS, CSP, X-Frame-Options) — браузер получил инструкции защиты. Санитизация поля — ввод не превращается в разметку/скрипт. Это не пентест: цель — «защита вообще предусмотрена?». Отсутствие любого пункта — находка, которую можно показать без всякого хакинга.</p>',
      mistakes: [
        'XSS-проба превратилась в попытку эксплуатации — это за границей и недопустимо.',
        'Заголовки не проверены — самый дешёвый сигнал гигиены пропущен.',
        'Смешанный контент не замечен — https-страница, тянущая http-скрипт, дырява.',
        'Находка без «почему важно» — разработчик не поймёт, стоит ли чинить.',
      ],
      qa: [
        { q: 'Чем смоук отличается от пентеста?', a: 'Смоук проверяет, есть ли базовая защита (HTTPS, заголовки, экранирование), и не эксплуатирует. Пентест ищет и подтверждает реальную эксплуатацию уязвимости — другая роль, разрешения и квалификация.' },
        { q: 'Почему отсутствие CSP — находка, даже если XSS не нашли?', a: 'CSP — слой защиты в глубину: даже если где-то проскочит XSS, политика не даст загрузить чужой скрипт. Его отсутствие повышает цену любой будущей XSS-дыры, поэтому это валидная находка сама по себе.' },
      ],
    },
  },
  // ─── S13 · Мобильное тестирование — попробовать руками (device-mode) ──────────
  {
    id: 'hw_s13_01',
    sprintId: 'S13',
    code: 'HW-S13-01',
    title: 'Матрица покрытия устройств: проверить нельзя всё',
    estimateMin: 35,
    priority: 'P1',
    skillAxis: 'test-design',
    portfolio: { artefact: 'Матрица «устройства × версии OS» с обоснованием выбора по риску', shows: 'умение выбрать, на чём тестировать, когда устройств тысячи — покрытие по риску, а не «всё подряд»' },
    surface: { kind: 'table', config: { tableColumns: ['Устройство × версия OS', 'В покрытии?', 'Почему (доля / риск / требование)'], minRows: 5 } },
    brief: 'Тысячи устройств и версий OS — проверить всё нельзя. Здесь учишься выбирать покрытие по риску: несколько комбинаций «устройство × версия», которые ловят максимум проблем за разумное время.',
    buddyAdvice: 'Не пытайся охватить всё — это ловушка. Возьми то, чем реально пользуются (аналитика, рынок, требования), и честно отметь, что оставляешь за бортом и почему это допустимо.',
    given: { label: 'Дано · аудитория Артели', html: 'Артель — маркетплейс с аудиторией RU/CIS (ядро), плюс EN/EU/TR. Источники выбора: аналитика (устройства покупателей), рыночная статистика региона, требования проекта.' },
    example: {
      head: 'Образец · одна строка матрицы',
      html:
        '<p>Устройство × OS: <strong>iPhone 13 / iOS 17</strong>. В покрытии? <strong>Да</strong>. Почему: ~35% мобильной аудитории Артели, текущая iOS — основная масса покупателей.</p>' +
        '<p>Контр-строка: <strong>Xiaomi Redmi 9 / Android 10 — нет</strong>: &lt;3% трафика, осознанно вне покрытия (не забыли — решили).</p>',
    },
    steps: [
      'Собери исходные данные: топовые устройства и версии OS аудитории Артели (RU/CIS: заметная доля Android разных сегментов + iPhone).',
      'Построй матрицу «устройство × версия OS» — выбери 4-6 комбинаций в покрытие.',
      'У каждой комбинации — 1 строка обоснования: доля пользователей / риск / требование.',
      'Отдельно зафиксируй, что НЕ покрываешь (старые версии, редкие устройства) и почему это осознанный риск, а не забывчивость.',
    ],
    formatNote: 'Одна таблица: каждая комбинация — «в покрытии?» да/нет и причина. Строки с «нет» — это осознанно непокрытое.',
    hints: [
      { label: 'Подсказка 1', sub: 'откуда брать список', body: 'Три источника: аналитика продукта (реальные устройства пользователей), рыночная статистика по региону, требования проекта (заявленная поддержка). Пересечение даёт короткий список, а не тысячи.' },
      { label: 'Подсказка 2', sub: 'Android vs iOS', body: 'Android фрагментирован (много устройств и версий) — берут несколько ценовых сегментов и пару версий OS. iOS однороднее — хватает текущей и предыдущей версии на паре моделей.' },
    ],
    selfCheck: [
      'В матрице 4-6 комбинаций «устройство × версия OS».',
      'У каждой есть обоснование выбора (доля/риск/требование).',
      'Явно указано, что не покрывается и почему это осознанный риск.',
      'Выбор опирается на данные, а не «возьму, что под рукой».',
    ],
    razbor: {
      etalon: '<p>Покрытие устройств — риск-ориентированный выбор, а не попытка объять всё. Матрица «устройство × версия OS» из аналитики, рынка и требований даёт короткий список комбинаций, ловящих большинство реальных проблем. Сила — в явном «что не покрываем и почему»: это превращает пробел в осознанное решение, которое можно защитить перед командой.</p>',
      mistakes: [
        'Попытка «протестировать на всём» — нереально, размывает усилия, всё равно что-то пропустишь.',
        'Выбор «что под рукой» вместо данных — покрытие не отражает реальных пользователей.',
        'Непокрытое не зафиксировано — пробел выглядит как забывчивость, а не решение.',
        'Только устройства без версий OS (или наоборот) — половина матрицы, баги версий пропущены.',
      ],
      qa: [
        { q: 'Почему нельзя просто «протестировать на всех устройствах»?', a: 'Их тысячи, время и устройства ограничены. Полное покрытие физически невозможно, попытка распыляет ресурс. Риск-ориентированный выбор ловит больше реальных проблем за то же время.' },
        { q: 'Что важнее — новый флагман или старая версия OS?', a: 'Зависит от аудитории: если у пользователей много старых версий — баги там дороже. Часто старая версия OS опаснее нового устройства, потому что там копятся несовместимости.' },
      ],
    },
  },
  {
    id: 'hw_s13_02',
    sprintId: 'S13',
    code: 'HW-S13-02',
    title: 'Device-mode на demoblaze: ориентация, адаптив, тач-цели',
    estimateMin: 40,
    priority: 'P1',
    portfolio: { artefact: 'Список находок из device-mode: что ломается при повороте/узком экране/мелких тач-целях', shows: 'умение тестировать мобайл-веб руками через DevTools device-mode без реального устройства' },
    surface: { kind: 'freeText', config: { minChars: 80, placeholder: 'Находки из device-mode на demoblaze: что с поворотом (landscape), что на узком экране (адаптив), что с размером тач-целей. По каждой: пресет → что не так → почему это проблема на телефоне.' } },
    brief: 'Реальное устройство не нужно, чтобы начать: DevTools device-mode эмулирует экран, тач и повороты. Здесь руками гоняешь demoblaze как мобайл-веб и ловишь то, что на десктопе не видно — узкий экран, поворот, мелкие тач-цели.',
    buddyAdvice: 'Device-mode — не настоящий телефон, но для мобайл-веба его хватает. Крути ориентацию, сужай экран, целься в мелкие кнопки. Что неудобно пальцу — то и находка.',
    given: { label: 'Дано', html: 'Витрина Артели (каталог / карточка товара / корзина) в приложении курса — или demoblaze.com как запасной вариант. Chrome DevTools device-mode: F12 → иконка телефона (Toggle device toolbar).' },
    example: {
      head: 'Образец · одна находка',
      html:
        '<p>Пресет <strong>iPhone SE</strong> (узкий). На каталоге Артели сетка карточек не переходит в одну колонку — вторая карточка обрезается справа, появляется горизонтальный скролл.</p>' +
        '<p>Почему это проблема на телефоне: часть работ мастеров не видна, пользователь думает, что каталог кончился, и уходит.</p>',
    },
    steps: [
      'Открой витрину Артели (каталог), включи device-mode, выбери пресет телефона.',
      'Ориентация: поверни в landscape. Что ломается — обрезается, наезжает, пропадает?',
      'Адаптив: сужай экран от планшета к узкому телефону. Где вёрстка перестаёт помещаться, появляется горизонтальный скролл?',
      'Тач-цели: проверь размер кнопок и ссылок. Мелкие/близко стоящие цели (меньше комфортного пальца, ориентир ~44px) — находка.',
      'Зафиксируй находки: пресет → что не так → почему это проблема именно на телефоне.',
    ],
    formatNote: 'Сдай список находок (минимум 3) с указанием пресета устройства. Device-mode хорош для мобайл-веба, но не заменяет реальное устройство — отметь, что проверила бы на живом телефоне.',
    hints: [
      { label: 'Подсказка 1', sub: 'не вижу проблем', body: 'Сравни с десктопом: что на широком экране норм, а на узком ломается. Частые места — длинные заголовки, таблицы, модалки, фиксированные шапки при повороте.' },
      { label: 'Подсказка 2', sub: 'что не так с тач-целями', body: 'Палец крупнее курсора. Кнопки меньше ~44px или вплотную друг к другу — это промахи и фрустрация. Реальная мобильная проблема, невидимая с мышью.' },
    ],
    selfCheck: [
      'Device-mode включён, проверен хотя бы один пресет телефона.',
      'Проверены ориентация (поворот), адаптив (узкий экран) и тач-цели.',
      'Минимум 3 находки с указанием пресета и причины «почему это проблема на телефоне».',
      'Отмечено ограничение device-mode (не заменяет реальное устройство).',
    ],
    razbor: {
      etalon: '<p>Device-mode — дешёвый вход в мобильное тестирование: эмулирует экран, тач и повороты прямо в браузере. Для мобайл-веба он ловит главное — поломки адаптива, проблемы поворота, мелкие тач-цели. Но это эмуляция: реальные жесты, производительность и платформенные нюансы видит только устройство. Зрелый вывод = «вот находки в device-mode, а вот что досмотрела бы на живом телефоне».</p>',
      mistakes: [
        'Проверка только в портретной ориентации — половина багов (поворот) пропущена.',
        'Размер тач-целей не смотрели — самая частая мобильная боль осталась незамеченной.',
        'Вывод «работает», потому что вёрстка не рассыпалась визуально — а пальцем не попасть.',
        'Device-mode принят за реальное устройство — забыто его ограничение.',
      ],
      qa: [
        { q: 'Чего device-mode не покажет?', a: 'Реальную производительность и батарею, настоящие жесты и их распознавание, платформенные нюансы (разрешения, сторонние клавиатуры, вырезы экрана), поведение нативных приложений. Это про мобайл-веб, не про всё мобильное.' },
        { q: 'Почему поворот — частый источник багов?', a: 'При смене ориентации меняются ширина и высота, и вёрстка, рассчитанная на портрет, плывёт: элементы наезжают, модалки обрезаются, фиксированные шапки перекрывают контент. Разработчики часто тестируют только портрет.' },
      ],
    },
  },
  {
    id: 'hw_s13_03',
    sprintId: 'S13',
    code: 'HW-S13-03',
    title: 'Мобильные условия: слабая сеть и прерывания',
    estimateMin: 35,
    priority: 'P2',
    skillAxis: 'non-functional',
    portfolio: { artefact: 'Находки поведения при throttling/offline и при прерывании — что ломается в «неидеальных» условиях', shows: 'умение проверять мобайл не в идеале, а в реальных условиях: медленная сеть, обрывы, переключение' },
    surface: { kind: 'freeText', config: { minChars: 80, placeholder: 'Что проверила (медленная сеть / offline / прерывание-сворачивание) и что произошло: понятная ошибка или зависание/потеря данных? По каждой: условие → ожидала → получила → ок или находка.' } },
    brief: 'Телефон живёт в неидеальных условиях: метро без сети, звонок посреди оплаты, переключение приложений. Десктоп-тест этого не ловит. Здесь проверяешь, как мобайл-веб ведёт себя, когда «всё идёт не так».',
    buddyAdvice: 'Идеальная сеть — это не тест. Замедли её, выключи, сверни вкладку на середине действия. Хорошее приложение объясняет, что случилось; плохое — молча виснет или теряет данные.',
    given: { label: 'Дано', html: 'Витрина Артели (или demoblaze) в device-mode и вкладка Network в DevTools (throttling: Slow 4G / Offline).' },
    example: {
      head: 'Образец · одно условие',
      html:
        '<p>Условие: <strong>Offline</strong> посреди оформления заказа Артели. Ожидала: понятное «нет сети, попробуйте позже».</p>' +
        '<p>Получила: кнопка «Оплатить» крутит спиннер без таймаута, данные корзины теряются при возврате online. Находка: молчаливый отказ + потеря состояния.</p>',
    },
    steps: [
      'Медленная сеть: включи throttling Slow 4G и пройди ключевой путь Артели (открыть работу мастера, добавить в корзину). Есть индикатор загрузки или экран «висит мёртвым»?',
      'Offline: переключи в Offline посреди действия. Понятная ошибка «нет сети» или непонятное зависание?',
      'Прерывание: на середине действия сверни вкладку/переключись и вернись. Состояние сохранилось или сбросилось?',
      'По каждому: условие → что ожидала → что получила → ок или находка.',
    ],
    formatNote: 'Сдай находки (минимум 3 условия). Цель — поведение в неидеальных условиях, не «работает при идеальной сети».',
    hints: [
      { label: 'Подсказка 1', sub: 'где throttling', body: 'DevTools → вкладка Network → выпадающий список (No throttling / Slow 4G / Offline). Меняй прямо во время действия, чтобы поймать момент обрыва.' },
      { label: 'Подсказка 2', sub: 'что считать находкой', body: 'Находка — «молчаливая» плохость: бесконечный спиннер без таймаута, потеря введённых данных после сворачивания, отсутствие сообщения при offline. Понятная ошибка — это, наоборот, хорошо.' },
    ],
    selfCheck: [
      'Проверена медленная сеть (throttling) на ключевом пути.',
      'Проверено поведение в offline посреди действия.',
      'Проверено прерывание (сворачивание/возврат) и сохранение состояния.',
      'По каждому условию записан вердикт «ок / находка» с ожиданием и фактом.',
    ],
    razbor: {
      etalon: '<p>Мобильное тестирование — это в первую очередь неидеальные условия. Слабая и рвущаяся сеть, прерывания, переключения приложений — норма для телефона, а не край. Хорошее поведение: индикатор загрузки, понятная ошибка «нет сети», сохранение введённого после возврата. Находки — молчаливые отказы: вечный спиннер, потеря данных, отсутствие сообщения. Throttling в DevTools даёт это проверить без выхода в метро.</p>',
      mistakes: [
        'Проверка только при идеальной сети — реальные мобильные сбои не воспроизведены.',
        'Offline не проверен посреди действия — самый частый мобильный сценарий пропущен.',
        'Прерывание (сворачивание) забыто — потеря состояния не поймана.',
        'Спиннер без таймаута принят за «загрузку» — на деле это зависание.',
      ],
      qa: [
        { q: 'Почему слабая сеть — обязательная мобильная проверка?', a: 'Телефон постоянно в нестабильной сети: метро, лифт, край покрытия. Поведение при медленной/рвущейся сети — не край, а типичный день пользователя. Десктоп в стабильном Wi-Fi этого не показывает.' },
        { q: 'Прерывание звонком — что именно проверять?', a: 'Сохраняется ли состояние и введённые данные после возврата, не дублируется ли действие (например оплата), корректно ли приложение возобновляется. Потеря данных или двойное действие — серьёзные находки.' },
      ],
    },
  },
  {
    id: 'hw_s13_04',
    sprintId: 'S13',
    code: 'HW-S13-04',
    title: 'Мобильный баг-репорт: добавь устройство, OS, логи, видео',
    estimateMin: 35,
    priority: 'P1',
    portfolio: { artefact: 'Мобильный баг-репорт по находке из device-mode — с устройством, версией OS и условиями', shows: 'умение оформить баг так, чтобы мобильную находку воспроизвели: что+где+когда + устройство/OS/сеть' },
    surface: { kind: 'bugReport' },
    brief: 'Мобильный баг без устройства и версии OS почти невоспроизводим: «у меня не так» — потому что у тебя другой телефон. Здесь оформляешь баг-репорт по своей находке из device-mode, добавляя к «что+где+когда» мобильный контекст.',
    buddyAdvice: 'Возьми любую находку из прошлых домашек этого спринта. Главное мобильное добавление — окружение: устройство/пресет, версия OS, ориентация, состояние сети. Без них баг не повторят.',
    given: { label: 'Дано', html: 'Одна твоя находка из device-mode (поворот / адаптив / слабая сеть / тач-цель) на витрине Артели (или demoblaze).' },
    example: {
      head: 'Образец · мобильный баг-репорт (кратко)',
      html:
        '<p>Заголовок: каталог Артели даёт горизонтальный скролл на узком экране (iPhone SE, портрет).</p>' +
        '<p>Окружение: device-mode iPhone SE, iOS 16, портрет, Wi-Fi. Шаги: открыть каталог → пресет iPhone SE → скролл вправо. Ожидаемо: сетка в 1 колонку. Фактически: карточка обрезана. Severity: medium (вид/удобство); влияние: часть работ мастеров не видна на телефоне.</p>',
    },
    steps: [
      'Заголовок: что сломано + где + при каком условии (одной строкой).',
      'Окружение: устройство/пресет, версия OS (или мобильный браузер), ориентация, состояние сети (например Slow 4G).',
      'Шаги воспроизведения — атомарные, с условием (включи device-mode → пресет → поверни → …).',
      'Ожидаемо и фактически — так, чтобы противоречие было очевидным.',
      'Severity по технической поломке + строка про влияние на мобильного пользователя.',
    ],
    formatNote: 'Заполни форму баг-репорта. В окружении обязательно устройство/OS/ориентация/сеть — без них мобильный баг не повторят.',
    hints: [
      { label: 'Подсказка 1', sub: 'что добавить к обычному багу', body: 'К стандартному «что+где+когда» мобильный добавляет окружение: устройство/пресет, версия OS, ориентация, состояние сети. Это и есть разница между веб- и мобильным баг-репортом.' },
      { label: 'Подсказка 2', sub: 'evidence', body: 'Для мобильного особенно ценны видео или серия скриншотов: жест и поворот словами передать трудно. Отметь, что приложила бы запись device-mode.' },
    ],
    selfCheck: [
      'Заголовок: что + где + при каком условии.',
      'Окружение содержит устройство/пресет, версию OS, ориентацию, состояние сети.',
      'Шаги атомарны и включают мобильное условие (device-mode, поворот, throttling).',
      'Severity обоснована, есть строка про влияние на мобильного пользователя.',
    ],
    razbor: {
      etalon: '<p>Мобильный баг-репорт = обычный «что+где+когда» плюс окружение. Устройство/пресет, версия OS, ориентация и состояние сети превращают «у меня не воспроизводится» в «повторил по шагам». Жесты и повороты трудно описать словами, поэтому видео/серия скриншотов почти обязательны. Severity — по технической поломке, отдельная строка — про то, как это бьёт по мобильному пользователю.</p>',
      mistakes: [
        'Нет устройства и версии OS — мобильный баг практически невоспроизводим.',
        'Состояние сети не указано, хотя баг проявился на Slow 4G — потеряно условие.',
        'Ориентация забыта, хотя баг при повороте — шаги не приведут к дефекту.',
        'Нет видео/скриншотов жеста — словами поворот и свайп передаются плохо.',
      ],
      qa: [
        { q: 'Почему «у меня не воспроизводится» так часто на мобильном?', a: 'Потому что «у меня» — другое устройство, версия OS, ориентация и сеть. Без этих полей разработчик пробует на своём телефоне и не видит баг. Окружение — ключ к повтору.' },
        { q: 'Severity или priority для «текст наезжает в landscape на старом Android»?', a: 'Severity по поломке (тут средняя — функция работает, страдает вид/удобство). Priority зависит от доли таких пользователей: много старых Android в аудитории — приоритет выше.' },
      ],
    },
  },
  {
    id: 'hw_s13_05',
    sprintId: 'S13',
    code: 'HW-S13-05',
    title: 'Мини-сессия мобильного тестирования: чартер и находки',
    estimateMin: 40,
    priority: 'P2',
    portfolio: { artefact: 'Чартер + журнал находок 25-минутной мобильной сессии в device-mode', shows: 'умение провести сфокусированную мобильную сессию и собрать находки — итог всего спринта' },
    surface: { kind: 'freeText', config: { minChars: 100, placeholder: 'Чартер (что и зачем исследую, таймбокс 25 мин) + журнал находок: пресет устройства, что делала, что нашла (поворот/сеть/тач/прерывание). Минимум 4 находки + 1 вывод о покрытии.' } },
    brief: 'Финал спринта — собрать всё в одну сессию. Берёшь чартер (фокус + таймбокс), гоняешь demoblaze в device-mode 25 минут и ведёшь журнал находок. Это репетиция реальной мобильной сессии тестирования.',
    buddyAdvice: 'Не распыляйся. Один чартер, 25 минут, журнал на ходу. Смешивай условия: поворот, узкий экран, медленная сеть, прерывание. Находка — даже «здесь всё ок»: это тоже результат.',
    given: { label: 'Дано', html: 'Витрина Артели (или demoblaze) в device-mode, таймер на 25 минут и всё, что освоила в спринте (покрытие, device-mode, условия, мобильный баг-репорт).' },
    example: {
      head: 'Образец · чартер + одна запись журнала',
      html:
        '<p>Чартер: «оформление заказа Артели на узком экране и слабой сети, 25 мин».</p>' +
        '<p>Журнал: «iPhone SE, поворот в корзине — кнопка „Оформить" ушла за экран; Slow 4G на оплате — спиннер без индикатора прогресса». Вывод: покрыла корзину и оплату, не дошла до профиля мастера.</p>',
    },
    steps: [
      'Напиши чартер: что исследую и зачем (например «корзина и оформление заказа Артели на узком экране и слабой сети»), таймбокс 25 минут.',
      'Гоняй витрину Артели (или demoblaze) в device-mode по чартеру, смешивая условия: пресет устройства, поворот, throttling, прерывание.',
      'Веди журнал на ходу: пресет → действие → находка (коротко).',
      'Собери минимум 4 находки. Заверши выводом: что покрыла, что осталось за пределами таймбокса.',
    ],
    formatNote: 'Сдай чартер + журнал находок (минимум 4) + вывод о покрытии. Таймбокс 25 минут — не растягивай.',
    hints: [
      { label: 'Подсказка 1', sub: 'что писать в чартер', body: 'Чартер — это «исследую X, чтобы найти Y, за Z минут». Узкий фокус (например только оформление заказа на мобиле) даёт больше находок, чем «потестить всё».' },
      { label: 'Подсказка 2', sub: 'журнал на ходу', body: 'Не откладывай записи на конец — забудешь. Короткая строка сразу: «пресет iPhone, поворот в корзине — кнопка оплаты ушла за экран». Этого хватит, чтобы потом развернуть в баг.' },
    ],
    selfCheck: [
      'Есть чартер с фокусом и таймбоксом 25 минут.',
      'Сессия смешивала условия (устройство, поворот, сеть, прерывание).',
      'Журнал ведётся на ходу, минимум 4 находки.',
      'Есть вывод: что покрыто и что осталось за пределами сессии.',
    ],
    razbor: {
      etalon: '<p>Мобильная сессия = чартер + таймбокс + журнал. Узкий фокус и 25 минут дисциплинируют: больше находок, чем при бесцельном тыкании. Смешивание условий (поворот, сеть, прерывание, тач) воспроизводит реальный мобильный день. Журнал на ходу не даёт забыть находки. Вывод о покрытии превращает сессию в артефакт: видно, что проверено и что осталось — основа для следующей сессии.</p>',
      mistakes: [
        'Нет чартера — сессия превращается в бесцельное тыкание без фокуса.',
        'Таймбокс не соблюдён — либо 5 минут, либо растянуто на час без дисциплины.',
        'Журнал пишется в конце по памяти — половина находок потеряна.',
        'Нет вывода о покрытии — непонятно, что проверено, а что нет.',
      ],
      qa: [
        { q: 'Зачем таймбокс, если можно тестировать сколько нужно?', a: 'Таймбокс держит фокус и даёт сравнимость: «25 минут на оформление заказа» можно повторить и сравнить между билдами. Без рамки сессия расплывается и не воспроизводится.' },
        { q: 'Чартер «протестировать приложение» — почему плохой?', a: 'Слишком широкий: внимание распыляется, находки поверхностные. Узкий чартер («оформление на слабой сети») фокусирует и даёт глубокие, воспроизводимые находки за то же время.' },
      ],
    },
  },
  // ─── S14 · Первые шаги в автоматизации (Playwright + минимум Python) ──────────
  {
    id: 'hw_s14_01',
    sprintId: 'S14',
    code: 'HW-S14-01',
    title: 'Свой маленький Python-скрипт',
    estimateMin: 20,
    priority: 'P2',
    brief:
      '<p>Первый автотест начинается с умения читать и писать пару строк Python. Это задание — без Playwright и браузера: просто прогнать данные через проверку и увидеть результат в консоли. Тот же приём лежит в основе любого data-driven теста.</p>',
    buddyAdvice:
      'Не гонись за красотой кода. Цель — чтобы скрипт запустился и вывел ожидаемое. Застрял на строке — скопируй её в AI и попроси объяснить, что она делает.',
    given: {
      label: 'Дано · набор тестовых email',
      html: '<p>Пять адресов для проверки формы — часть валидные, часть нет: <code>""</code>, <code>"abc"</code>, <code>"a@"</code>, <code>"a@b.ru"</code>, <code>"qa@mail.ru"</code>.</p>',
    },
    steps: [
      'Положи пять email в <strong>список</strong> <code>emails = [...]</code>.',
      'Напиши <strong>функцию</strong> <code>check(email)</code>, которая печатает сам email и есть ли в нём символ <code>@</code> (проверка вхождения — <code>"@" in email</code>).',
      'Вызови <code>check</code> для каждого адреса через <strong>цикл</strong> <code>for e in emails:</code>.',
    ],
    formatNote:
      'Где запускать: локально (<code>python check.py</code>) или в любом онлайн-Python (например repl). Вывод — текст в консоли, по строке на адрес.',
    example: {
      head: 'Образец · начало скрипта',
      html:
        '<p><code>emails = ["", "abc", "a@", "a@b.ru", "qa@mail.ru"]</code></p>' +
        '<p><code>def check(email):</code><br>&nbsp;&nbsp;&nbsp;&nbsp;<code>print(email, "@" in email)</code></p>' +
        '<p><em>дальше — вызвать check для каждого адреса через цикл.</em></p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'не знаю, как пройтись по списку',
        body: 'Цикл: <code>for e in emails:</code>, а внутри (с отступом в 4 пробела) — вызов <code>check(e)</code>.',
      },
      {
        label: 'Подсказка 2',
        sub: 'как проверить, есть ли «собака»',
        body: 'Оператор <code>in</code> возвращает True/False: <code>"@" in "a@b.ru"</code> → True. Это и печатаем рядом с адресом.',
      },
    ],
    selfCheck: [
      'Скрипт запускается без ошибок.',
      'В консоль выводятся все 5 адресов.',
      'Для каждого видно True/False — есть ли в нём символ @.',
    ],
    submitNote: 'Сохрани сам скрипт (текст) — к нему вернёшься в S15, когда тесты начнём складывать аккуратнее.',
    razbor: {
      etalon:
        '<p>Сильное решение — это рабочий цикл «данные → функция-проверка → результат». Именно так устроен data-driven тест: один набор данных прогоняется через одну проверку, без копипасты. Список, функция и цикл — три кирпичика, на которых дальше держится весь автотест.</p>',
      mistakes: [
        'Кавычки вокруг переменной: <code>print("email")</code> выведет слово «email», а не значение. Имя переменной — без кавычек.',
        'Проверка через <code>==</code> вместо <code>in</code>: <code>email == "@"</code> сравнивает весь адрес со строкой «@», а нужно «содержит ли».',
        'Забытый отступ в теле функции или цикла — Python ответит ошибкой IndentationError.',
      ],
      qa: [
        {
          q: 'Это уже автотест?',
          a: 'Ещё нет — браузера тут нет. Но логика «прогнать данные через проверку» — ровно та, что в настоящих тестах. Playwright добавится в HW-2.',
        },
        {
          q: 'Хочу глубже в Python — куда?',
          a: 'Python — отдельная большая тема, и это нормально остановиться на минимуме. Если затянуло — официальный туториал Python и любой вводный курс будут следующим шагом после этого блока.',
        },
      ],
    },
  },
  {
    id: 'hw_s14_02',
    sprintId: 'S14',
    code: 'HW-S14-02',
    title: 'Повтори логин-тест сам',
    estimateMin: 30,
    priority: 'P1',
    portfolio: {
      artefact: 'Рабочий автотест логина (Python + Playwright)',
      shows: 'умеешь написать и запустить простой UI-автотест от начала до конца',
    },
    brief:
      '<p>Лучший способ закрепить навык — воспроизвести его без подглядывания. Соберёшь сценарий логина на demoblaze своими руками: рамка теста, локаторы, действия, осмысленная проверка. На выходе — первый автотест в твоём портфолио.</p>',
    buddyAdvice:
      'Сначала попробуй по памяти, в тикет подглядывай только когда совсем застрял. Упал тест — не страшно: прочитай последнюю строку ошибки, это и есть подсказка, где чинить.',
    given: {
      label: 'Дано',
      html: '<p>Установленный Playwright (<code>pip install playwright</code> + <code>playwright install</code>) и сайт <code>demoblaze.com</code>.</p>',
    },
    steps: [
      'Собери <strong>рамку</strong> теста: запустить браузер → новая вкладка → <code>goto</code> на demoblaze → в конце <code>browser.close()</code>.',
      'Открой форму входа: клик по ссылке <code>Log in</code> (локатор по роли и тексту).',
      'Заполни поля логина и пароля (<code>get_by_label</code> + <code>.fill(...)</code>) и нажми кнопку входа.',
      'Добавь <strong>осмысленную проверку</strong> через <code>expect(...)</code> — что после входа видно приветствие. Запусти тест и прочитай результат.',
    ],
    formatNote:
      'Где: файл <code>login_test.py</code>, запуск <code>python login_test.py</code>. Подписи полей и текст приветствия сверь на живом demoblaze.',
    example: {
      head: 'Образец · «рамка» + одно действие',
      html:
        '<p><code>page.get_by_role("link", name="Log in").click()</code></p>' +
        '<p><em>дальше — заполнить поля, нажать кнопку и проверить приветствие через expect.</em></p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'не помню, с чего начинается тест',
        body: 'Рамка одинакова в любом тесте: <code>sync_playwright()</code> → <code>launch</code> → <code>new_page()</code> → <code>goto(...)</code> → … → <code>close()</code>. Меняется только середина.',
      },
      {
        label: 'Подсказка 2',
        sub: 'какую проверку писать',
        body: 'Проверка должна покраснеть, если логин сломается. Подходит <code>expect(локатор).to_be_visible()</code> на тексте приветствия после входа.',
      },
    ],
    selfCheck: [
      'Тест запускается командой python.',
      'Использует минимум 2 разных типа локатора (например get_by_role и get_by_label).',
      'Есть осмысленная expect-проверка (а не «просто потыкал»).',
      'Нет time.sleep — полагаешься на auto-waiting.',
    ],
    submitNote: 'Сохрани файл теста — это твой первый артефакт для портфолио автоматизатора.',
    razbor: {
      etalon:
        '<p>Сильный тест читается как список понятных шагов и заканчивается проверкой, которая реально что-то гарантирует. Локаторы — по роли и тексту (как видит пользователь), а не по месту в вёрстке. Если получилось без подсказок — навык закрепился; если подглядывал — повтори через день, так память работает лучше.</p>',
      mistakes: [
        'Проверка ради галочки: <code>assert True</code> или клик без expect. Тест зелёный, но ничего не проверяет.',
        'Хрупкий локатор по структуре (<code>div:nth-child(3)</code>) вместо роли и текста — сломается от первой правки вёрстки.',
        'Вставленный «на всякий случай» <code>time.sleep</code> — Playwright ждёт готовности сам, ручное ожидание не нужно.',
      ],
      qa: [
        {
          q: 'Тест падает с TimeoutError — что делать?',
          a: 'Почти всегда дело в локаторе: элемент не найден под таким описанием. Открой demoblaze и сверь реальную роль и текст. time.sleep тут не поможет.',
        },
        {
          q: 'Обязательно headless=False?',
          a: 'Нет, но с видимым окном нагляднее на старте — видишь, что робот делает. Позже, в наборах, чаще гоняют headless (без окна), это быстрее.',
        },
      ],
    },
  },
  {
    id: 'hw_s14_03',
    sprintId: 'S14',
    code: 'HW-S14-03',
    title: 'AI + твой чек-лист',
    estimateMin: 25,
    priority: 'P1',
    portfolio: {
      artefact: 'AI-черновик теста + разбор найденного ляпа',
      shows: 'ловишь ошибки AI — главная ценность junior с AI в 2026',
    },
    brief:
      '<p>В автоматизации AI используют плотно: он быстро пишет черновик теста. Но уверенно выдумывает локаторы и проверки. Твоя ценность — не «умею просить AI», а «ловлю, где AI ошибся». Это задание — про второе.</p>',
    buddyAdvice:
      'Не правь AI-код сразу. Сначала прогони его глазами по чек-листу из T10 — так ляп виден раньше, чем ты начнёшь чинить не то.',
    given: {
      label: 'Дано',
      html: '<p>Любой AI-ассистент (Copilot / Cursor / ChatGPT) и сайт <code>demoblaze.com</code>.</p>',
    },
    steps: [
      'Попроси AI: «тест на Python + Playwright (sync API): добавить товар в корзину на demoblaze».',
      'Проведи черновик через <strong>чек-лист T10</strong>: локаторы сверены с живой страницей · каждая проверка покраснеет при поломке · нет лишних <code>time.sleep</code> · тест реально запускается.',
      'Найди минимум <strong>один ляп</strong> (выдуманный локатор / фейк-проверка / лишний sleep) и опиши его словами: что не так и как чинишь.',
    ],
    formatNote:
      'Сохрани исходный AI-черновик и рядом — свой разбор (1-3 предложения на ляп). Текст в любом документе.',
    example: {
      head: 'Образец · как звучит разбор ляпа',
      html:
        '<p>«AI написал <code>get_by_role("button", name="Add to cart")</code>, но на demoblaze это ссылка, и тест падает с таймаутом. Чиню на <code>get_by_role("link", name="Add to cart")</code>, сверив с живой страницей».</p>',
    },
    hints: [
      {
        label: 'Подсказка 1',
        sub: 'AI выдал «идеальный» код, ляпа не вижу',
        body: 'Идеальный на вид — самый опасный. Проверь каждую строку отдельно: этот локатор точно есть на странице? Эта проверка покраснеет, если функция сломается?',
      },
      {
        label: 'Подсказка 2',
        sub: 'где тут вообще можно споткнуться',
        body: 'demoblaze при добавлении в корзину показывает браузерный alert. Если AI его не учёл — это уже находка. Спроси AI, как принять alert в Playwright (<code>page.on("dialog", ...)</code>) и сверь.',
      },
    ],
    selfCheck: [
      'AI-черновик сохранён целиком.',
      'Найден и описан минимум 1 ляп своими словами.',
      'К ляпу предложен конкретный фикс (а не «переписать всё»).',
    ],
    submitNote: 'Этот разбор — отличный пример в портфолио: показывает, что ты не вставляешь AI-код вслепую.',
    razbor: {
      etalon:
        '<p>Сильный разбор называет <em>тип</em> ляпа (выдуманный локатор / фейк-проверка / лишний sleep), объясняет, <em>почему</em> это ошибка, и даёт конкретный фикс, сверенный с реальной страницей. Это и есть правило спринта в действии: AI пишет — ты проверяешь. Junior, который вставляет AI-код не глядя, опаснее junior без AI.</p>',
      mistakes: [
        '«AI всё сделал правильно» — почти всегда значит, что черновик не проверяли построчно. Перепроверь локаторы на живой странице.',
        'Фикс «переписать весь тест» вместо точечного — теряется смысл задания: найти и объяснить конкретный ляп.',
        'Пропущенный alert при add-to-cart: тест «зелёный», но шаг не выполнился — классическое слепое пятно AI.',
      ],
      qa: [
        {
          q: 'А если AI и правда не ошибся?',
          a: 'Редко, но бывает на простых случаях. Тогда найди слабое место: чаще всего это проверка, которая недостаточно строгая, или локатор, который сработал случайно. Опиши, что бы сломало этот тест.',
        },
        {
          q: 'Можно просить AI чинить свои же ляпы?',
          a: 'Да, но решение принимаешь ты: AI может предложить time.sleep или другой выдуманный локатор. Используй его как черновик, проверяй результат сам.',
        },
      ],
    },
  },
  {
    id: 'hw_s14_04',
    sprintId: 'S14',
    code: 'HW-S14-04',
    surface: { kind: 'table', config: { tableColumns: ['Хрупкий локатор', 'Устойчивый локатор', 'Почему хрупкий'], minRows: 5 } },
    title: 'Устойчивые локаторы: перепиши хрупкие',
    estimateMin: 35,
    priority: 'P1',
    portfolio: {
      artefact: 'Таблица: 5 хрупких локаторов → устойчивые + обоснование',
      shows: 'пишешь локаторы, которые не ломаются от каждой правки вёрстки',
    },
    brief:
      '<p>Локатор — то, чем тест находит элемент. Хрупкий локатор (по месту в вёрстке) ломается от первой правки дизайна, и тест краснеет не из-за бага. Устойчивый смотрит на элемент как пользователь: по роли и тексту. Навык экономит часы на поддержке тестов.</p>',
    buddyAdvice:
      'Сверяй каждый локатор с живой страницей demoblaze. Если не можешь словами сказать, что он находит, — он хрупкий.',
    given: {
      label: 'Дано · 5 хрупких локаторов',
      html:
        '<p>1. <code>page.locator(".col-lg-4:nth-child(2) .btn")</code><br/>' +
        '2. <code>page.locator("xpath=/html/body/div[2]/div/div[1]/a")</code><br/>' +
        '3. <code>page.locator("#tbodyid > tr:nth-child(1) > td:nth-child(2)")</code><br/>' +
        '4. <code>page.locator("button.btn.btn-success.btn-lg")</code><br/>' +
        '5. <code>page.locator("a[onclick=\'logout()\']")</code></p>',
    },
    example: {
      head: 'Образец · одна строка таблицы',
      html:
        '<p>Хрупкий: <code>page.locator(".col-lg-4:nth-child(2) .btn")</code>. Устойчивый: <code>page.get_by_role("link", name="Add to cart")</code>.</p>' +
        '<p>Почему хрупкий: завязан на позицию в сетке — добавили карточку выше, и <code>:nth-child(2)</code> указывает на другой товар. Тест краснеет без всякого бага.</p>',
    },
    steps: [
      'Открой demoblaze и определи, какой элемент находит каждый из 5 локаторов.',
      'Для каждого — строка таблицы: хрупкий → устойчивый (get_by_role / get_by_label / get_by_text) → почему исходный хрупкий и от чего сломается.',
      'Сверь каждый устойчивый локатор с живой страницей: словами скажи, что он находит.',
    ],
    hints: [
      { label: 'Подсказка 1', sub: 'не понимаю, что находит локатор', body: 'Вставь селектор в DevTools (Ctrl+F в панели Elements) или в Playwright Inspector — увидишь подсветку элемента.' },
      { label: 'Подсказка 2', sub: 'нет роли у элемента', body: 'У кнопок роль button, у ссылок link. На demoblaze «Add to cart» — это ссылка (link), хотя выглядит кнопкой. Сверь на живой странице.' },
      { label: 'Подсказка 3', sub: 'почему nth-child плохо', body: 'Он завязан на порядок в вёрстке. Добавили блок выше — и :nth-child(3) указывает на другой элемент. Тест краснеет без всякого бага.' },
    ],
    selfCheck: [
      'Все 5 локаторов переписаны в устойчивые.',
      'Минимум 3 используют get_by_role / get_by_label / get_by_text.',
      'У каждого объяснено, от чего ломался исходный.',
      'Устойчивые сверены с живой страницей demoblaze.',
    ],
    submitNote: 'Сравнение «хрупкий → устойчивый» — наглядный артефакт в портфолио и частая тема собеса по автоматизации.',
    razbor: {
      etalon:
        '<p>Сильная переделка убирает зависимость от вёрстки: <code>:nth-child</code>, длинные xpath и цепочки классов меняются на <code>get_by_role("link", name="Add to cart")</code> и т.п. Ключ — локатор описывает, ЧЕМ элемент является для пользователя, а не ГДЕ лежит в дереве.</p>',
      mistakes: [
        'Переписали один хрупкий селектор в другой хрупкий (сменили nth-child на класс) — устойчивости не прибавилось.',
        'Локатор по тексту, который часто меняется (цена, счётчик), — тоже хрупкий, но по другой причине.',
        'Не сверили с живой страницей: «Add to cart» взяли как button, а это link — тест упадёт.',
      ],
      qa: [
        { q: 'Когда xpath всё-таки уместен?', a: 'Когда у элемента нет ни роли, ни текста, ни подписи — редкий случай. Тогда короткий xpath по стабильному атрибуту лучше длинного по структуре. Но сначала ищи role/text.' },
        { q: 'А data-testid?', a: 'Лучший вариант, если разработчики их проставляют: <code>get_by_test_id</code>. Не зависит ни от вёрстки, ни от текста. На demoblaze их нет, поэтому тренируемся на role/text.' },
      ],
    },
  },
  {
    id: 'hw_s14_05',
    sprintId: 'S14',
    code: 'HW-S14-05',
    surface: {
      kind: 'code',
      config: {
        codeLang: 'python',
        minChars: 140,
        starter: 'from playwright.sync_api import sync_playwright, expect\n\nwith sync_playwright() as p:\n    browser = p.chromium.launch()\n    page = browser.new_page()\n    # неверный пароль → проверить отказ\n\n    browser.close()',
      },
    },
    title: 'Негативный логин-тест: неверный пароль',
    estimateMin: 40,
    priority: 'P1',
    portfolio: {
      artefact: 'Автотест негативного логина (Playwright): неверный пароль → видно отказ',
      shows: 'проверяешь не только happy path, но и что система правильно отказывает',
    },
    brief:
      '<p>В HW-2 ты проверил, что верный логин пускает. Не менее важно — что НЕверный не пускает. Негативные тесты ловят дыры в безопасности: junior часто проверяет только «всё хорошо» и пропускает «всё плохо». Это задание про второй случай.</p>',
    buddyAdvice:
      'Возьми за основу свой логин-тест из HW-2. Меняется немного: неверный пароль и проверка на «приветствия НЕ видно / видно отказ».',
    given: {
      label: 'Дано',
      html: '<p>Твой рабочий логин-тест из HW-2 и сайт demoblaze. При неверном логине demoblaze показывает браузерный alert «Wrong password.».</p>',
    },
    example: {
      head: 'Образец · перехват alert (ключевой шаг)',
      html:
        '<p><code>page.on("dialog", lambda d: d.accept())</code> — вешается ДО клика по кнопке входа.</p>' +
        '<p>После неверного пароля проверяешь текст alert («Wrong password.») ИЛИ что приветствия нет: <code>expect(page.get_by_text("Welcome")).not_to_be_visible()</code>. Проверка должна покраснеть, если подставить верный пароль.</p>',
    },
    steps: [
      'Скопируй рамку логин-теста из HW-2.',
      'Подставь верный логин и ЗАВЕДОМО неверный пароль.',
      'Перехвати alert (<code>page.on("dialog", ...)</code>) и проверь его текст, ИЛИ проверь, что приветствие НЕ появилось (<code>expect(...).not_to_be_visible()</code>).',
      'Запусти и убедись, что тест зелёный именно потому, что вход отклонён.',
    ],
    hints: [
      { label: 'Подсказка 1', sub: 'тест зелёный, но проверяет ли он что-то', body: 'Сломай нарочно: подставь ВЕРНЫЙ пароль. Негативный тест должен покраснеть (вход прошёл, отказа нет). Остался зелёным — проверка пустая.' },
      { label: 'Подсказка 2', sub: 'alert не ловится', body: 'Обработчик <code>page.on("dialog", lambda d: d.accept())</code> вешается ДО клика по кнопке входа. Текст берётся из <code>dialog.message</code>.' },
      { label: 'Подсказка 3', sub: 'какую проверку выбрать', body: 'Две рабочие стратегии: текст alert («Wrong password.») или отсутствие приветствия «Welcome». Достаточно одной, но она должна краснеть при успешном входе.' },
    ],
    selfCheck: [
      'Тест использует неверный пароль и зелёный именно из-за отказа.',
      'Проверка краснеет, если подставить верный пароль (проверено).',
      'expect осмысленный: текст alert или отсутствие приветствия.',
      'Нет time.sleep — ожидание через Playwright.',
    ],
    submitNote: 'Негативный тест в портфолио показывает, что ты думаешь про безопасность, а не только про happy path.',
    razbor: {
      etalon:
        '<p>Сильный негативный тест проверяемо краснеет при верном пароле — значит, реально стережёт отказ. Лучшая проверка завязана на видимое пользователю поведение: текст «Wrong password.» или отсутствие приветствия. Это пара к позитивному тесту: вместе они описывают «пускает верных, отклоняет неверных».</p>',
      mistakes: [
        'Проверка «страница загрузилась» — зелёная и при удачном, и при неудачном входе. Негативного смысла ноль.',
        'Не повесили обработчик dialog до клика — alert висит, тест таймаутит.',
        'Проверили только текст ошибки, но не убедились, что приветствия нет.',
      ],
      qa: [
        { q: 'Сколько негативных кейсов на один позитивный?', a: 'Обычно негативных больше: пустой пароль, неверный пароль, неверный логин, оба неверные, спецсимволы. Один позитивный путь — много способов его сломать.' },
        { q: 'Почему «сломай нарочно» — хорошая привычка?', a: 'Зелёный тест ничего не доказывает, если зелёный всегда. Временная поломка условия показывает, что тест реально реагирует. Базовая гигиена автоматизатора.' },
      ],
    },
  },
  {
    id: 'hw_s14_06',
    sprintId: 'S14',
    code: 'HW-S14-06',
    surface: {
      kind: 'code',
      config: {
        codeLang: 'python',
        minChars: 200,
        starter: 'from playwright.sync_api import sync_playwright, expect\n\ncases = [\n    # (login, password, ok)\n]\n\nwith sync_playwright() as p:\n    browser = p.chromium.launch()\n    for login, password, ok in cases:\n        page = browser.new_page()\n        # логин → проверка под ok\n    browser.close()',
      },
    },
    title: 'Data-driven: один тест, набор данных',
    estimateMin: 40,
    priority: 'P2',
    portfolio: {
      artefact: 'Data-driven логин-тест: один сценарий на наборе из 3+ пар логин/пароль',
      shows: 'не копипастишь тест на каждый набор данных — гоняешь один через цикл',
    },
    brief:
      '<p>В HW-1 ты прогнал данные через проверку циклом, в HW-2 написал логин-тест. Соедини: один сценарий, несколько наборов данных. Это data-driven — основа реальных наборов тестов. Без него на каждый набор пришлось бы копировать тест.</p>',
    buddyAdvice:
      'Сначала собери данные списком пар (логин, пароль, ожидание), потом оберни тело логин-теста в цикл. Тело не дублируется — меняются только данные.',
    given: {
      label: 'Дано · набор данных',
      html: '<p>Сайт demoblaze и 3 набора: (верный логин, верный пароль → ждём «пускает»), (верный логин, неверный пароль → «отказ»), (несуществующий логин, любой пароль → «отказ»).</p>',
    },
    example: {
      head: 'Образец · данные снаружи, тело одно',
      html:
        '<p><code>cases = [("admin", "admin", True), ("admin", "wrong", False), ("nouser", "x", False)]</code></p>' +
        '<p>Тело логина одно, наборы снаружи. Добавить 4-й случай = дописать строку данных, не копировать тест. Между наборами — новая страница (<code>browser.new_page()</code>), чтобы состояние не липло.</p>',
    },
    steps: [
      'Заведи список наборов: <code>cases = [(login, password, expect_ok), ...]</code>.',
      'Оберни тело логин-сценария в цикл <code>for login, password, ok in cases:</code> — заполнение полей берёт данные из набора.',
      'Внутри цикла — проверка под ожидание набора (пускает / отказ). Печатай результат по каждому набору.',
      'Запусти: прогоняются 3 набора, результат виден по каждому.',
    ],
    hints: [
      { label: 'Подсказка 1', sub: 'состояние между наборами липнет', body: 'После удачного входа ты залогинен — следующий набор стартует не с чистого листа. Между наборами возвращайся на исходную (повторный goto / разлогин) или открывай новую страницу.' },
      { label: 'Подсказка 2', sub: 'как проверять разное ожидание', body: 'Третий элемент набора (ok = True/False) подскажет, какую проверку звать: при True — приветствие видно, при False — нет. Ветка if внутри цикла.' },
      { label: 'Подсказка 3', sub: 'один набор упал — цикл встал', body: 'Чтобы остальные прогнались несмотря на падение, лови проверку в try/except и печатай PASS/FAIL. Для тастера достаточно и без этого, но знать полезно.' },
    ],
    selfCheck: [
      'Один сценарий логина прогоняется на 3+ наборах через цикл (без копипасты тела).',
      'Данные вынесены в список, а не вшиты в тест.',
      'Результат виден по каждому набору (PASS/FAIL или печать).',
      'Состояние между наборами не липнет (старт с чистой страницы).',
    ],
    submitNote: 'Data-driven тест — сильный артефакт: показывает, что понимаешь, как устроены реальные наборы. Идёт в портфолио.',
    razbor: {
      etalon:
        '<p>Сильное решение выносит данные из логики: тело теста одно, наборы снаружи списком. Добавить четвёртый случай = дописать строку данных, не копировать тест. Плюс честно решена проблема состояния (между наборами чистый старт). Ровно тот приём, на котором держатся боевые наборы автотестов.</p>',
      mistakes: [
        'Скопировали тело теста три раза вместо цикла — это не data-driven, а копипаста.',
        'Данные вшиты внутри цикла, а не вынесены наружу — добавить набор так же тяжело, как и раньше.',
        'Не сброшено состояние: после первого удачного входа остальные наборы стартуют залогиненными и врут.',
      ],
      qa: [
        { q: 'Чем это отличается от parametrize в pytest?', a: 'pytest.mark.parametrize делает то же аккуратнее: каждый набор — отдельный тест в отчёте. Ручной цикл — понятная основа; параметризацию фреймворком возьмёшь в S15.' },
        { q: 'Сколько наборов оптимально?', a: 'Покрой классы, а не количество: один happy, пара негативных, край. Три-четыре осмысленных набора лучше двадцати однотипных.' },
      ],
    },
  },
];

export const homeworkV2ById = (id: string): HomeworkV2 | undefined =>
  HOMEWORK_V2.find((h) => h.id === id);

export const homeworkV2BySprintId = (sprintId: string): ReadonlyArray<HomeworkV2> =>
  HOMEWORK_V2.filter((h) => h.sprintId === sprintId);
